// src/pages/ApproverPage.jsx

import React, { useState, useEffect } from 'react';
import {
    getApproverDashboard,
    listRequests,
    listClosedRequests,
    updateRequestStatus,
    getRequestDetails,
    getApproverAIAnalysis,
    getDashboardMetrics,
    getRequesterHistory,
    getL8Status,
    getComments,
    addComment,
    holdRequest,
    resumeRequest,
    listOnHoldRequests,
    triggerSync,
    getSyncStatus,
    VIEWER_URL
} from '../services/api';
import ConfigPage from './ConfigPage';
import T321ConfigPage from './T321ConfigPage';

// ════════════════════════════════════════════════════════════════
// TENANT REGISTRY (Phase 2B)
// ────────────────────────────────────────────────────────────────
// Declarative nav: each tenant lists the tabs it exposes. Adding a new
// tenant (we're scaling to ~7) is now a registry entry, not new JSX +
// new activeTab strings + new routing branches.
//
// Tab `key` values intentionally reuse the ORIGINAL activeTab strings
// ('dashboard' | 'requests' | 'bindle' | 'ruleset' | 'config') so the
// existing routing block, loaders, and useEffect keep working unchanged.
// Config tabs carry a `component` so content rendering stays generic.
//
// T178 is fully preserved (Dashboard / Access / Bindle / Ruleset / Config).
// T321 exposes Dashboard + Access + Config now; its request list is wired
// in Phase 2C (tenant-scoped filtering). Ruleset/Bindle are T178-only until
// a tenant declares them.
// ════════════════════════════════════════════════════════════════
const TENANT_REGISTRY = {
    T178: {
        label: 'T178',
        tabs: [
            { key: 'dashboard', label: '📊 Dashboard' },
            { key: 'requests',  label: '📋 Access Requests' },
            { key: 'bindle',    label: '📦 Bindle Requests' },
            { key: 'ruleset',   label: '📐 Ruleset Requests' },
            { key: 'config',    label: '⚙️ T178 Config', component: ConfigPage },
        ],
    },
    T321: {
        label: 'T321',
        tabs: [
            { key: 'dashboard', label: '📊 Dashboard' },
            { key: 'requests',  label: '📋 Access Requests' },
            { key: 'config',    label: '⚙️ T321 Config', component: T321ConfigPage },
        ],
    },
};

// Order the switcher shows tenants in.
const TENANT_ORDER = ['T178', 'T321'];

// ════════════════════════════════════════════════════════════════
// PROVISIONING TAB FLAG
// ────────────────────────────────────────────────────────────────
// The "Provisioning" sub-tab (bulk Approve & Add to Permission Page for LRP/MRP Full-Approval
// requests) is a FALLBACK path for manual provisioning. We are pursuing CARDS+Pascal for
// automated provisioning, so this sub-tab is HIDDEN for ALL tenants (both T178 and T321).
//
// The feature code (renderProvisioning + its handlers) is intentionally KEPT in this file,
// fully intact, so it can be re-enabled by a single flip of this flag if CARDS+Pascal does not
// land. It is also T178-centric (filters on LRP/MRP risk tiers, which don't exist for T321) —
// another reason it must stay hidden until reworked per-tenant if ever revived.
//   true  = show the Provisioning sub-tab (legacy manual bulk-add fallback)
//   false = hidden (default; CARDS+Pascal is the provisioning path)
const SHOW_PROVISIONING_TAB = false;

const ApproverPage = ({ userAlias }) => {
    // ════════════════════════════════════════════
    // STATE
    // ════════════════════════════════════════════
    const [activeTenant, setActiveTenant] = useState('T178'); // Phase 2B tenant scope
    const [activeTab, setActiveTab] = useState('requests');
    const [dashboard, setDashboard] = useState(null);
    const [pendingRequests, setPendingRequests] = useState([]);
    const [onHoldRequests, setOnHoldRequests] = useState([]);
    const [closedRequests, setClosedRequests] = useState([]);
    const [tatClosed, setTatClosed] = useState(null); // closed requests for team-TAT metrics (null=not loaded)
    const [tatFilters, setTatFilters] = useState({
        dateFrom: '2026-08-20', dateTo: '', useCase: 'ALL', team: '', requester: '',
        excludeWeekends: false, breachesOnly: false,
    });
    const [tatExpanded, setTatExpanded] = useState({}); // requestId -> bool (row breakdown expanded)
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [requestsSubTab, setRequestsSubTab] = useState('pending');

    // Bindle requests state
    const [bindleRequests, setBindleRequests] = useState([]);
    const [isLoadingBindle, setIsLoadingBindle] = useState(false);
    const [bindleSubTab, setBindleSubTab] = useState('pending');

    // Ruleset requests state
    const [rulesetRequests, setRulesetRequests] = useState([]);
    const [isLoadingRuleset, setIsLoadingRuleset] = useState(false);
    const [rulesetSubTab, setRulesetSubTab] = useState('pending');

    // Pending counts for the nav-tab badges (tenant-scoped; recomputed on tenant switch)
    const [bindlePendingCount, setBindlePendingCount] = useState(0);
    const [rulesetPendingCount, setRulesetPendingCount] = useState(0);
    const [accessPendingCount, setAccessPendingCount] = useState(0);
    // Tenant-scoped On Hold / Closed ACCESS counts for the sub-tab labels. The cross-tenant
    // `dashboard` totals would wrongly show T178's numbers under T321 (the "not seamless" bug),
    // so these are computed from tenant-filtered lists (same pattern as the pending counts).
    const [accessOnHoldCount, setAccessOnHoldCount] = useState(0);
    const [accessClosedCount, setAccessClosedCount] = useState(0);

    // Action modal state
    const [actionModal, setActionModal] = useState({
        show: false,
        requestId: '',
        action: '',
        notes: '',
        approver: '',
        isSubmitting: false
    });

    // Provisioning panel state (shown after approval)
    const [provisioningData, setProvisioningData] = useState(null);

    // 🆕 Expanded request detail state
    const [expandedRequest, setExpandedRequest] = useState(null);
    const [expandedData, setExpandedData] = useState(null);
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);
    const [aiAnalysis, setAiAnalysis] = useState(null);
    const [isLoadingAI, setIsLoadingAI] = useState(false);

    // Search, sort, bulk selection state
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('date_desc');
    // Queue filters (help approvers triage a large pending backlog)
    const [stageFilter, setStageFilter] = useState('all');      // all | mine | leadership
    const [typeFilter, setTypeFilter] = useState('all');        // all | approved | unlisted
    const [priorityFilter, setPriorityFilter] = useState('all');// all | Critical | High | Medium | Low
    const [dateFrom, setDateFrom] = useState('');               // YYYY-MM-DD (createdAt >=)
    const [dateTo, setDateTo] = useState('');                   // YYYY-MM-DD (createdAt <=)
    const [selectedRequests, setSelectedRequests] = useState(new Set());
    const [bulkApprover, setBulkApprover] = useState(userAlias || '');
    const [metrics, setMetrics] = useState(null);
    const [requesterHistory, setRequesterHistory] = useState(null);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [l8Status, setL8Status] = useState(null);
    const [isLoadingL8, setIsLoadingL8] = useState(false);
    const [comments, setComments] = useState([]);
    const [isLoadingComments, setIsLoadingComments] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [commentAlias, setCommentAlias] = useState(userAlias || '');

    // Provisioning tab state
    const [provSelected, setProvSelected] = useState(new Set());
    const [provStatus, setProvStatus] = useState({});
    const [isProvisioning, setIsProvisioning] = useState(false);

    // Validation table filter
    const [approverTableFilter, setApproverTableFilter] = useState('all');
    const [isPostingComment, setIsPostingComment] = useState(false);

    // Sync state
    const [isSyncing, setIsSyncing] = useState(false);
    const [lastSyncTime, setLastSyncTime] = useState(null);
    const [syncError, setSyncError] = useState('');

    // ════════════════════════════════════════════
    // LOAD DATA
    // ════════════════════════════════════════════
    useEffect(() => {
        loadDashboard();
        loadSyncStatus();
    }, []);

    // ════════════════════════════════════════════
    // DEEP LINK — open a specific request from a URL like
    //   https://pam-approver.tse.amazon.dev/?request=PAM-24640573
    // (used by the SLA Slack report links AND the Dashboard report CSV). We land
    // the approver on Access Requests with the search box pre-filled to the ID.
    // The correct sub-tab depends on the request's status: PENDING → Pending,
    // ON_HOLD → On Hold, APPROVED/REJECTED → Closed. We look up the request's
    // status first so a closed case doesn't land on the (empty) Pending tab.
    // Falls back to Pending if the lookup fails. Tolerates ?request= or
    // ?requestId=; strips the param from the URL afterwards.
    // ════════════════════════════════════════════
    useEffect(() => {
        let cancelled = false;
        const params = new URLSearchParams(window.location.search);
        const rid = (params.get('request') || params.get('requestId') || '').trim();
        if (!rid) return;
        // Pre-fill search + land on the requests tab immediately for responsiveness.
        setActiveTab('requests');
        setSearchQuery(rid);
        // Clean the URL so the deep-link param doesn't persist across reloads.
        if (window.history && window.history.replaceState) {
            window.history.replaceState({}, '', window.location.pathname);
        }
        // Resolve the correct sub-tab from the request's status.
        (async () => {
            try {
                const data = await getRequestDetails(rid);
                if (cancelled) return;
                const status = String(
                    (data && (data.status || data.approvalStatus || data.reportStatus)) || ''
                ).toUpperCase();
                if (status === 'APPROVED' || status === 'REJECTED') {
                    setRequestsSubTab('closed');
                } else if (status === 'ON_HOLD' || status === 'HOLD') {
                    setRequestsSubTab('onhold');
                } else {
                    setRequestsSubTab('pending');
                }
            } catch (e) {
                console.warn('Deep-link status lookup failed, defaulting to Pending:', e);
                if (!cancelled) setRequestsSubTab('pending');
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const loadSyncStatus = async () => {
        try {
            const data = await getSyncStatus();
            if (data.lastSyncTime && data.lastSyncTime !== 'Never') {
                setLastSyncTime(data.lastSyncTime);
            }
        } catch (e) {
            console.warn('Could not fetch sync status:', e);
        }
    };

    const handleSync = async () => {
        if (isSyncing) return;
        setIsSyncing(true);
        setSyncError('');
        try {
            const result = await triggerSync();
            if (result.inProgress) {
                // A sync (usually the scheduled run) is already refreshing the data — not an
                // error. Show the last sync time and reload so the user still sees fresh data.
                if (result.lastSyncTime && result.lastSyncTime !== 'Unknown') {
                    setLastSyncTime(result.lastSyncTime);
                }
                loadDashboard();
                if (requestsSubTab === 'pending') loadPendingRequests();
                else if (requestsSubTab === 'onhold') loadOnHoldRequests();
                else if (requestsSubTab === 'closed') loadClosedRequests();
            } else if (result.success) {
                setLastSyncTime(result.lastSyncTime);
                // Reload current tab data after sync
                loadDashboard();
                if (requestsSubTab === 'pending') loadPendingRequests();
                else if (requestsSubTab === 'onhold') loadOnHoldRequests();
                else if (requestsSubTab === 'closed') loadClosedRequests();
            }
        } catch (err) {
            setSyncError('Sync failed: ' + err.message);
        } finally {
            setIsSyncing(false);
        }
    };

    // ════════════════════════════════════════════
    // TENANT SCOPING (Phase 2C)
    // A request belongs to the active tenant when its `tenant` field matches.
    // Legacy/untagged records (pre-Phase-2A) are treated as 'T178' since every
    // historical request is T178 — this keeps the T178 view complete and never
    // leaks untagged rows into other tenants.
    // ════════════════════════════════════════════
    const matchesTenant = (r) => ((r && r.tenant) || 'T178') === activeTenant;

    const loadDashboard = async () => {
        setIsLoading(true);
        setError('');
        try {
            const data = await getApproverDashboard();
            setDashboard(data);
            // Compute pending counts for the Bindle/Ruleset nav-tab badges (tenant-scoped)
            try {
                const pending = await listRequests('PENDING');
                const reqs = (pending.requests || []).filter(matchesTenant);
                const bindleN = reqs.filter(r => r.requestType === 'bindle_access' || r.team === 'Bindle Access').length;
                const rulesetN = reqs.filter(r => r.requestType === 'ruleset_access' || r.team === 'Ruleset Access').length;
                setBindlePendingCount(bindleN);
                setRulesetPendingCount(rulesetN);
                // Access = tenant-scoped pending minus its bindle/ruleset sub-streams.
                setAccessPendingCount(Math.max(0, reqs.length - bindleN - rulesetN));
            } catch (e) {
                console.warn('Nav badge counts unavailable:', e);
            }
            // Load metrics in background
            try {
                const m = await getDashboardMetrics();
                setMetrics(m);
            } catch (e) {
                console.warn('Metrics unavailable:', e);
            }
            // Load closed requests for team-TAT metrics (access requests only).
            // Reuse the same tenant-filtered list to set the tenant-scoped Closed sub-tab count.
            try {
                const closed = await listClosedRequests();
                const closedAccess = (closed.requests || []).filter(r =>
                    matchesTenant(r) &&
                    r.requestType !== 'bindle_access' && r.team !== 'Bindle Access' &&
                    r.requestType !== 'ruleset_access' && r.team !== 'Ruleset Access'
                );
                setTatClosed(closedAccess);
                setAccessClosedCount(closedAccess.length);
            } catch (e) {
                console.warn('TAT closed-requests unavailable:', e);
                setTatClosed([]);
            }
            // Tenant-scoped On Hold access count for the sub-tab label.
            try {
                const onhold = await listOnHoldRequests();
                const onholdAccess = (onhold.requests || []).filter(r =>
                    matchesTenant(r) &&
                    r.requestType !== 'bindle_access' && r.team !== 'Bindle Access' &&
                    r.requestType !== 'ruleset_access' && r.team !== 'Ruleset Access'
                );
                setAccessOnHoldCount(onholdAccess.length);
            } catch (e) {
                console.warn('On-hold count unavailable:', e);
            }
        } catch (err) {
            setError('Failed to load dashboard: ' + err.message);
            console.error('Dashboard error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const loadPendingRequests = async () => {
        setIsLoading(true);
        setError('');
        try {
            const data = await listRequests('PENDING');
            setPendingRequests((data.requests || []).filter(r => matchesTenant(r) && r.requestType !== 'bindle_access' && r.team !== 'Bindle Access' && r.requestType !== 'ruleset_access' && r.team !== 'Ruleset Access'));
        } catch (err) {
            setError('Failed to load requests: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const loadClosedRequests = async () => {
        setIsLoading(true);
        setError('');
        try {
            const data = await listClosedRequests();
            setClosedRequests((data.requests || []).filter(r => matchesTenant(r) && r.requestType !== 'bindle_access' && r.team !== 'Bindle Access' && r.requestType !== 'ruleset_access' && r.team !== 'Ruleset Access'));
        } catch (err) {
            setError('Failed to load closed requests: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const loadOnHoldRequests = async () => {
        setIsLoading(true);
        setError('');
        try {
            const data = await listOnHoldRequests();
            setOnHoldRequests((data.requests || []).filter(r => matchesTenant(r) && r.requestType !== 'bindle_access' && r.team !== 'Bindle Access' && r.requestType !== 'ruleset_access' && r.team !== 'Ruleset Access'));
        } catch (err) {
            setError('Failed to load on-hold requests: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const loadBindleRequests = async () => {
        setIsLoadingBindle(true);
        try {
            // Load pending bindle requests — filter by requestType=bindle_access
            const pendingData = await listRequests('PENDING');
            const closedData = await listClosedRequests();
            const bindlePending = (pendingData.requests || []).filter(r => matchesTenant(r) && (r.requestType === 'bindle_access' || r.team === 'Bindle Access'));
            const bindleClosed = [...(closedData.requests || [])].filter(r => matchesTenant(r) && (r.requestType === 'bindle_access' || r.team === 'Bindle Access'));
            setBindleRequests(bindleSubTab === 'pending' ? bindlePending : bindleClosed);
        } catch (err) {
            console.error('Failed to load bindle requests:', err);
        } finally {
            setIsLoadingBindle(false);
        }
    };

    const loadRulesetRequests = async () => {
        setIsLoadingRuleset(true);
        try {
            const pendingData = await listRequests('PENDING');
            const closedData = await listClosedRequests();
            const rulesetPending = (pendingData.requests || []).filter(r => matchesTenant(r) && (r.requestType === 'ruleset_access' || r.team === 'Ruleset Access'));
            const rulesetClosed = [...(closedData.requests || [])].filter(r => matchesTenant(r) && (r.requestType === 'ruleset_access' || r.team === 'Ruleset Access'));
            setRulesetRequests(rulesetSubTab === 'pending' ? rulesetPending : rulesetClosed);
        } catch (err) {
            console.error('Failed to load ruleset requests:', err);
        } finally {
            setIsLoadingRuleset(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'requests') {
            if (requestsSubTab === 'pending') {
                loadPendingRequests();
            } else if (requestsSubTab === 'onhold') {
                loadOnHoldRequests();
            } else if (requestsSubTab === 'closed') {
                loadClosedRequests();
            }
        }
        if (activeTab === 'bindle') {
            loadBindleRequests();
        }
        if (activeTab === 'ruleset') {
            loadRulesetRequests();
        }
        if (activeTab === 'provisioning') {
            loadPendingRequests();
        }
        // activeTenant is a dep so switching tenant re-runs the active loader (Phase 2C).
    }, [activeTab, requestsSubTab, bindleSubTab, rulesetSubTab, activeTenant]);

    // Recompute the Bindle/Ruleset nav badges when the tenant changes so the counts
    // reflect only the active tenant (Phase 2C). Lightweight: one PENDING list fetch,
    // no full dashboard reload. Skips the initial mount (loadDashboard already set them).
    const didMountTenantCounts = React.useRef(false);
    useEffect(() => {
        if (!didMountTenantCounts.current) {
            didMountTenantCounts.current = true;
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const pending = await listRequests('PENDING');
                const reqs = (pending.requests || []).filter(matchesTenant);
                if (cancelled) return;
                const bindleN = reqs.filter(r => r.requestType === 'bindle_access' || r.team === 'Bindle Access').length;
                const rulesetN = reqs.filter(r => r.requestType === 'ruleset_access' || r.team === 'Ruleset Access').length;
                setBindlePendingCount(bindleN);
                setRulesetPendingCount(rulesetN);
                setAccessPendingCount(Math.max(0, reqs.length - bindleN - rulesetN));

                // Also recompute the tenant-scoped On Hold / Closed ACCESS sub-tab counts so they
                // reflect the active tenant on switch (not the cross-tenant dashboard totals).
                const isAccess = (r) => r.requestType !== 'bindle_access' && r.team !== 'Bindle Access'
                    && r.requestType !== 'ruleset_access' && r.team !== 'Ruleset Access';
                try {
                    const onhold = await listOnHoldRequests();
                    if (!cancelled) setAccessOnHoldCount((onhold.requests || []).filter(r => matchesTenant(r) && isAccess(r)).length);
                } catch (_) { /* leave previous count */ }
                try {
                    const closed = await listClosedRequests();
                    if (!cancelled) setAccessClosedCount((closed.requests || []).filter(r => matchesTenant(r) && isAccess(r)).length);
                } catch (_) { /* leave previous count */ }
            } catch (e) {
                console.warn('Tenant badge recount unavailable:', e);
            }
        })();
        return () => { cancelled = true; };
    }, [activeTenant]);

    // ════════════════════════════════════════════
    // 🆕 EXPAND REQUEST DETAILS
    // ════════════════════════════════════════════
    const handleExpandRequest = async (requestId) => {
        if (expandedRequest === requestId) {
            setExpandedRequest(null);
            setExpandedData(null);
            setAiAnalysis(null);
            setL8Status(null);
            setComments([]);
            return;
        }

        setExpandedRequest(requestId);
        setExpandedData(null);
        setAiAnalysis(null);
        setRequesterHistory(null);
        setL8Status(null);
        setComments([]);
        setIsLoadingDetails(true);

        try {
            const data = await getRequestDetails(requestId);
            setExpandedData(data);

            // Trigger AI analysis in background
            setIsLoadingAI(true);
            try {
                const ai = await getApproverAIAnalysis(data);
                setAiAnalysis(ai);
            } catch (aiErr) {
                console.warn('AI analysis unavailable:', aiErr);
                setAiAnalysis(null);
            } finally {
                setIsLoadingAI(false);
            }

            // Load L8 status in background
            setIsLoadingL8(true);
            try {
                const l8 = await getL8Status(requestId);
                setL8Status(l8);
            } catch (l8Err) {
                console.warn('L8 status unavailable:', l8Err);
            } finally {
                setIsLoadingL8(false);
            }

            // Load comments in background
            setIsLoadingComments(true);
            try {
                const commentsData = await getComments(requestId);
                setComments(commentsData.comments || []);
            } catch (commErr) {
                console.warn('Comments unavailable:', commErr);
            } finally {
                setIsLoadingComments(false);
            }

            // Load requester history in background
            if (data.requesterAlias) {
                setIsLoadingHistory(true);
                try {
                    const history = await getRequesterHistory(data.requesterAlias);
                    setRequesterHistory(history);
                } catch (histErr) {
                    console.warn('Requester history unavailable:', histErr);
                }
                setIsLoadingHistory(false);
            }
        } catch (err) {
            console.error('Error loading request details:', err);
            setExpandedData({ error: err.message });
        } finally {
            setIsLoadingDetails(false);
        }
    };

    // ════════════════════════════════════════════
    // ACTIONS
    // ════════════════════════════════════════════
    const openActionModal = (requestId, action) => {
        setActionModal({
            show: true,
            requestId,
            action,
            notes: '',
            approver: userAlias || '',
            isSubmitting: false
        });
        // Auto-fetch request details if not already expanded for this request
        if (expandedRequest !== requestId) {
            getRequestDetails(requestId).then(data => {
                setExpandedRequest(requestId);
                setExpandedData(data);
            }).catch(() => {});
        }
    };

    const closeActionModal = () => {
        setActionModal({
            show: false,
            requestId: '',
            action: '',
            notes: '',
            approver: '',
            isSubmitting: false
        });
    };

    const handleAction = async () => {
        if (!actionModal.approver.trim()) {
            alert('Please enter your login ID');
            return;
        }

        setActionModal(prev => ({ ...prev, isSubmitting: true }));

        try {
            const result = await updateRequestStatus(
                actionModal.requestId,
                actionModal.action === 'approve' ? 'APPROVED' : 'REJECTED',
                actionModal.approver,
                actionModal.notes
            );

            const wasApproval = actionModal.action === 'approve';
            const reqId = actionModal.requestId;
            const isStage1Only = result.stage1Only === true;
            const isStage3Final = result.stage3Final === true;
            const currentStage = result.currentStage || 0;

            closeActionModal();
            loadDashboard();
            if (requestsSubTab === 'pending') {
                loadPendingRequests();
            }

            if (wasApproval && isStage1Only) {
                // Multi-stage HRP/CRP: Stage 1 approved, request stays pending
                alert(`✅ Stage 1 (SPS Permissions Team) approved for ${reqId}.\n\nRequest remains in Pending — awaiting Leadership Approval (Stage 2) on approvals.amazon.com.\n\nOnce leadership approves, Stage 3 (Access Management) will become available here.`);
            } else if (wasApproval && isStage3Final) {
                // Stage 3 final: request closed — show provisioning panel
                if (expandedData) {
                    setProvisioningData({
                        requestId: reqId,
                        users: expandedData.userList || expandedData.eligibleUsers?.join(',') || '',
                        permissionLinks: expandedData.permissionLinks || [],
                        riskTypeGroup: expandedData.riskTypeGroup || '',
                        selectedPermissionNames: expandedData.selectedPermissionNames || []
                    });
                } else {
                    alert(`✅ Stage 3 (Access Management) approved for ${reqId}.\n\nRequest is now CLOSED. Approval synced to approvals.amazon.com.`);
                }
            } else if (wasApproval && expandedData && !result.isMultiStage) {
                // Non-multi-stage (LRP/MRP/listed): show provisioning panel
                setProvisioningData({
                    requestId: reqId,
                    users: expandedData.userList || expandedData.eligibleUsers?.join(',') || '',
                    permissionLinks: expandedData.permissionLinks || [],
                    riskTypeGroup: expandedData.riskTypeGroup || '',
                    selectedPermissionNames: expandedData.selectedPermissionNames || []
                });
            } else {
                alert(`Request ${reqId} has been ${wasApproval ? 'APPROVED' : 'REJECTED'}${currentStage ? ` (Stage ${currentStage})` : ''}`);
            }

        } catch (err) {
            alert('Error: ' + err.message);
            setActionModal(prev => ({ ...prev, isSubmitting: false }));
        }
    };

    // ════════════════════════════════════════════
    // HOLD & RESUME HANDLERS
    // ════════════════════════════════════════════
    const handleHoldRequest = async (requestId) => {
        if (!window.confirm(`Place request ${requestId} on hold? This will also place it on hold in approvals.amazon.com.`)) return;
        try {
            await holdRequest(requestId, userAlias, '');
            alert(`Request ${requestId} has been placed on hold.`);
            loadDashboard();
            loadPendingRequests();
        } catch (err) {
            alert('Error placing on hold: ' + err.message);
        }
    };

    const handleResumeRequest = async (requestId) => {
        if (!window.confirm(`Resume request ${requestId}? This will move it back to the pending queue.`)) return;
        try {
            await resumeRequest(requestId, userAlias, '');
            alert(`Request ${requestId} has been resumed.`);
            loadDashboard();
            loadOnHoldRequests();
        } catch (err) {
            alert('Error resuming request: ' + err.message);
        }
    };

    // ════════════════════════════════════════════
    // HELPERS
    // ════════════════════════════════════════════
    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getRecommendationBadge = (rec) => {
        if (rec === 'FULL_APPROVAL') return { class: 'badge-success', label: '✅ Full Approval' };
        if (rec === 'PARTIAL_APPROVAL') return { class: 'badge-warning', label: '⚠️ Partial' };
        if (rec === 'REJECT') return { class: 'badge-danger', label: '❌ Reject' };
        return { class: 'badge-default', label: rec || '—' };
    };

    const getPriorityBadge = (priority) => {
        const classes = {
            'Critical': 'badge-danger',
            'High': 'badge-warning',
            'Medium': 'badge-info',
            'Low': 'badge-default'
        };
        return classes[priority] || 'badge-default';
    };

    const getStatusBadge = (status) => {
        if (status === 'APPROVED') return { class: 'badge-success', label: '✅ Approved' };
        if (status === 'REJECTED' || status === 'DENIED') return { class: 'badge-danger', label: '❌ Rejected' };
        return { class: 'badge-warning', label: '⏳ Pending' };
    };

    // ════════════════════════════════════════════
    // STAGE PROGRESS DISPLAY
    // ════════════════════════════════════════════
    const getStageProgress = (req) => {
        const riskType = (req.riskTypeGroup || '').toUpperCase();
        const requestType = req.requestType || '';
        const isUnlisted = requestType.includes('unlisted');
        const isBindle = requestType === 'bindle_access' || req.team === 'Bindle Access';
        const isRuleset = requestType === 'ruleset_access' || req.team === 'Ruleset Access';
        // 3-stage: unlisted HRP/CRP  OR  bindle (Bindle POC → Leadership → SPS Team)
        const isHighRisk = (isUnlisted && (riskType === 'HRP' || riskType === 'CRP')) || isBindle;
        // 2-stage: unlisted LRP/MRP  OR  ruleset (Leadership → Permissions Team)
        const isLowRisk = ((isUnlisted && (riskType === 'LRP' || riskType === 'MRP')) || isRuleset) && !isHighRisk;

        if (isHighRisk) {
            // 3-stage. Stage 1/2 completion is read from cached stageNApprovedBy fields
            // (set by the SNS handler / sync Lambda). Stage 3 is the final Permissions/SPS
            // team stage — never inferred as done here; it closes when the whole approval
            // completes (overall APPROVED), at which point the request leaves the pending list.
            const stage1Done = !!(req.stage1ApprovedBy);
            const stage2Done = !!(req.stage2ApprovedBy);
            const currentStage = !stage1Done ? 1 : !stage2Done ? 2 : 3;
            // Bindle uses different stage labels than HRP/CRP permission requests
            const stage1Name = isBindle ? 'Bindle POC' : 'Security Review';
            const stage3Name = isBindle ? 'SPS Team' : 'Permissions Team';
            return {
                stages: [
                    { name: stage1Name, done: stage1Done, active: currentStage === 1, approver: req.stage1ApprovedBy },
                    { name: 'Leadership', done: stage2Done, active: currentStage === 2, approver: req.stage2ApprovedBy },
                    { name: stage3Name, done: false, active: currentStage === 3, approver: '' }
                ],
                currentStage,
                totalStages: 3,
                isYourTurn: currentStage === 1 || currentStage === 3
            };
        } else if (isLowRisk) {
            // 2-stage — Leadership → Permissions Team (unlisted LRP/MRP and ruleset)
            const leaderDone = !!(req.stage2ApprovedBy || req.l8ApprovalStatus === 'APPROVED' || req.l8ApproverAlias);
            const currentStage = !leaderDone ? 1 : 2;
            return {
                stages: [
                    { name: 'Leadership', done: leaderDone, active: currentStage === 1, approver: req.stage2ApprovedBy || req.l8ApproverAlias },
                    { name: 'Permissions Team', done: false, active: currentStage === 2, approver: '' }
                ],
                currentStage,
                totalStages: 2,
                isYourTurn: currentStage === 2
            };
        }
        // Pre-approved or unknown: 1-stage — Permissions Team
        return {
            stages: [{ name: 'Permissions Team', done: false, active: true, approver: '' }],
            currentStage: 1,
            totalStages: 1,
            isYourTurn: true
        };
    };

    const renderStageProgress = (req) => {
        const progress = getStageProgress(req);
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '10px' }}>
                {progress.stages.map((stage, idx) => {
                    // Shorter names for compact display
                    const shortName = stage.name === 'Security Review' ? 'Security' 
                        : stage.name === 'Permissions Team' ? 'Permissions' 
                        : stage.name;
                    return (
                        <React.Fragment key={idx}>
                            {idx > 0 && <span style={{ color: '#cbd5e1', fontSize: '9px' }}>›</span>}
                            <span
                                title={stage.done ? `${stage.name}: Approved by ${stage.approver}` : stage.active ? `${stage.name}: ${stage.name === 'Leadership' ? 'Awaiting leader' : 'Your action'}` : stage.name}
                                style={{
                                    padding: '2px 5px',
                                    borderRadius: '3px',
                                    fontWeight: '600',
                                    whiteSpace: 'nowrap',
                                    backgroundColor: stage.done ? '#dcfce7' : stage.active ? (stage.name === 'Leadership' ? '#fef3c7' : '#eef6ff') : '#f1f5f9',
                                    color: stage.done ? '#166534' : stage.active ? (stage.name === 'Leadership' ? '#92400e' : '#1e40af') : '#94a3b8',
                                    border: `1px solid ${stage.done ? '#bbf7d0' : stage.active ? (stage.name === 'Leadership' ? '#fde68a' : '#bfdbfe') : '#e2e8f0'}`
                                }}
                            >
                                {stage.done ? '✓' : stage.active ? '●' : '○'} {shortName}
                            </span>
                        </React.Fragment>
                    );
                })}
            </div>
        );
    };

    // ════════════════════════════════════════════
    // AUTO-CATEGORIZE REQUEST COMPLEXITY
    // ════════════════════════════════════════════
    const getComplexityTag = (req) => {
        const totalUsers = req.totalUsers || 0;
        const totalEligible = req.totalEligible || 0;
        const totalIneligible = req.totalIneligible || 0;
        const rec = req.recommendation || '';
        const requestType = req.requestType || '';

        // Exception: all ineligible or unlisted with ineligible users
        if (totalEligible === 0 && totalUsers > 0) {
            return { label: '🔴 Exception', bg: '#ffeef0', color: '#d13212', border: '#ffc1c8' };
        }
        if (requestType.includes('unlisted') && totalIneligible > 0) {
            return { label: '🟡 Needs Review', bg: '#fff3cd', color: '#856404', border: '#fde68a' };
        }
        // Needs review: mixed eligibility
        if (totalIneligible > 0 || rec === 'PARTIAL_APPROVAL') {
            return { label: '🟡 Needs Review', bg: '#fff3cd', color: '#856404', border: '#fde68a' };
        }
        // Routine: all eligible, approved use case
        if (rec === 'FULL_APPROVAL' && !requestType.includes('unlisted')) {
            return { label: '🟢 Routine', bg: '#dcfce7', color: '#166534', border: '#bbf7d0' };
        }
        // Unlisted but all eligible
        if (requestType.includes('unlisted')) {
            return { label: '🟡 Needs Review', bg: '#fff3cd', color: '#856404', border: '#fde68a' };
        }
        return { label: '🟢 Routine', bg: '#dcfce7', color: '#166534', border: '#bbf7d0' };
    };

    // ════════════════════════════════════════════
    // REQUEST AGE INDICATOR
    // ════════════════════════════════════════════
    const getAgeIndicator = (dateStr) => {
        if (!dateStr) return null;
        const now = new Date();
        // Ensure UTC parsing — append Z if no timezone indicator present
        const normalizedDate = dateStr.endsWith('Z') || dateStr.includes('+') || dateStr.includes('T') && dateStr.match(/[+-]\d{2}:\d{2}$/)
            ? dateStr
            : dateStr + 'Z';
        const created = new Date(normalizedDate);
        const diffMs = now - created;
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        let label, color, bg, border, tooltip;
        // Tooltip: full date/time in user's locale
        tooltip = created.toLocaleString();
        // Exact intake time shown under the age chip (compact: "22 Sep, 14:32").
        const exact = created.toLocaleString('en-GB', {
            day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false
        });

        if (diffMinutes < 60) {
            label = diffMinutes <= 1 ? 'Just now' : `${diffMinutes}m`;
            color = '#166534'; bg = '#dcfce7'; border = '#bbf7d0';
        } else if (diffHours < 24) {
            label = `${diffHours}h`;
            color = '#166534'; bg = '#dcfce7'; border = '#bbf7d0';
        } else if (diffDays <= 2) {
            label = `${diffDays}d`;
            color = '#166534'; bg = '#dcfce7'; border = '#bbf7d0';
        } else if (diffDays <= 5) {
            label = `${diffDays}d`;
            color = '#92400e'; bg = '#fef3c7'; border = '#fde68a';
        } else if (diffDays <= 7) {
            label = `${diffDays}d`;
            color = '#d13212'; bg = '#ffeef0'; border = '#ffc1c8';
        } else {
            // > 7 days: show date (e.g., "Aug 14")
            label = created.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            color = '#d13212'; bg = '#ffeef0'; border = '#ffc1c8';
        }
        return { label, color, bg, border, tooltip, exact };
    };

    // ════════════════════════════════════════════
    // SEARCH & SORT HELPERS
    // ════════════════════════════════════════════
    const filterAndSortRequests = (requests) => {
        return requests
            .filter(req => {
                if (!searchQuery.trim()) return true;
                const q = searchQuery.toLowerCase();
                return (
                    (req.requestId || '').toLowerCase().includes(q) ||
                    (req.team || '').toLowerCase().includes(q) ||
                    (req.functionDropdown || '').toLowerCase().includes(q) ||
                    (req.useCase || '').toLowerCase().includes(q) ||
                    (req.requesterAlias || '').toLowerCase().includes(q) ||
                    (req.orgName || '').toLowerCase().includes(q)
                );
            })
            // Stage / whose-turn filter — "mine" = awaiting Security/Permissions (our action),
            // "leadership" = currently waiting on a leader. Uses the same getStageProgress()
            // that drives the Stage column + action buttons, so it stays consistent with the UI.
            .filter(req => {
                if (stageFilter === 'all') return true;
                const progress = getStageProgress(req);
                const activeStage = (progress.stages.find(s => s.active)?.name || '').toLowerCase();
                const awaitingLeadership = activeStage === 'leadership';
                if (stageFilter === 'leadership') return awaitingLeadership;
                if (stageFilter === 'mine') return !awaitingLeadership; // Security / Permissions / SPS / Bindle POC
                return true;
            })
            // Type filter — MUST mirror the exact isApproved logic used by the Type column badge
            // (rt === 'approved' | 'approved_renewal' | permission_*_approved), otherwise rows shown
            // as "Approved Use Case" would be hidden by a narrower check.
            .filter(req => {
                if (typeFilter === 'all') return true;
                const rt = req.requestType || '';
                const isApproved = rt === 'approved' || rt === 'approved_renewal' || (rt.includes('permission_') && rt.includes('approved'));
                if (typeFilter === 'approved') return isApproved;
                if (typeFilter === 'unlisted') return !isApproved;
                return true;
            })
            // Priority filter
            .filter(req => {
                if (priorityFilter === 'all') return true;
                return (req.priority || 'Medium') === priorityFilter;
            })
            // Custom date range on createdAt (mirrors the Report section date filter).
            .filter(req => {
                if (!dateFrom && !dateTo) return true;
                const created = req.createdAt || '';
                if (!created) return false;
                if (dateFrom && created < dateFrom) return false;
                if (dateTo && created > dateTo + 'T23:59:59') return false;
                return true;
            })
            .sort((a, b) => {
                if (sortBy === 'date_desc') return new Date(b.createdAt) - new Date(a.createdAt);
                if (sortBy === 'date_asc') return new Date(a.createdAt) - new Date(b.createdAt);
                if (sortBy === 'priority') {
                    const order = { Critical: 0, High: 1, Medium: 2, Low: 3 };
                    return (order[a.priority] ?? 9) - (order[b.priority] ?? 9);
                }
                if (sortBy === 'users') return (b.totalUsers || 0) - (a.totalUsers || 0);
                return 0;
            });
    };

    // ════════════════════════════════════════════
    // BULK ACTIONS
    // ════════════════════════════════════════════
    const [bulkActionModal, setBulkActionModal] = useState({ show: false, action: '' });

    const toggleSelectRequest = (requestId) => {
        setSelectedRequests(prev => {
            const next = new Set(prev);
            if (next.has(requestId)) next.delete(requestId);
            else next.add(requestId);
            return next;
        });
    };

    const toggleSelectAll = (requests) => {
        if (selectedRequests.size === requests.length) {
            setSelectedRequests(new Set());
        } else {
            setSelectedRequests(new Set(requests.map(r => r.requestId)));
        }
    };

    const handleBulkAction = async (action) => {
        if (!bulkApprover.trim()) {
            return;
        }
        if (selectedRequests.size === 0) return;

        const status = action === 'approve' ? 'APPROVED' : 'REJECTED';
        const ids = Array.from(selectedRequests);
        let successCount = 0;
        let failCount = 0;

        for (const id of ids) {
            try {
                await updateRequestStatus(id, status, bulkApprover.trim(), bulkActionModal.notes || `Bulk ${action}`);
                successCount++;
            } catch {
                failCount++;
            }
        }

        setSelectedRequests(new Set());
        setBulkApprover(userAlias || '');
        loadDashboard();
        loadPendingRequests();
    };

    // ════════════════════════════════════════════
    // 🆕 RENDER — EXPANDED DETAIL PANEL
    // ════════════════════════════════════════════
            const renderExpandedPanel = (requestId) => {
        if (expandedRequest !== requestId) return null;

        // Helper: format array to readable string
        const formatArray = (arr) => {
            if (!arr || arr.length === 0) return null;
            return Array.isArray(arr) ? arr.join(', ') : arr;
        };

        const formatFrequency = (arr) => {
            if (!arr || arr.length === 0) return null;
            const labels = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' };
            return (Array.isArray(arr) ? arr : []).map(v => labels[v] || v).join(', ');
        };

        const formatSearchCriteria = (arr) => {
            if (!arr || arr.length === 0) return null;
            const labels = {
                seller_id: 'Seller ID', email: 'Email', phone: 'Phone',
                safet_claim_id: 'SAFE-T Claim ID', order_id: 'Order ID',
                investigation_id: 'Investigation ID', credit_card_id: 'Credit Card ID',
                customer_name: 'Customer Name', contact_id: 'Contact ID',
                atoz_claim_id: 'A-to-Z Claim ID'
            };
            return (Array.isArray(arr) ? arr : []).map(v => labels[v] || v).join(', ');
        };

        const getRequestTypeLabel = (type) => {
            if (type === 'approved' || type === 'approved_renewal' || type === 'permission_new_approved' || type === 'permission_renewal_approved') return { label: '✅ Approved Use Case', bg: '#dcfce7', color: '#166534', border: '#bbf7d0' };
            if (type === 'unlisted' || type === 'unlisted_renewal' || type === 'new_unlisted' || type === 'permission_new_unlisted' || type === 'permission_renewal_unlisted') return { label: '📋 Unlisted Use Case', bg: '#fff3cd', color: '#856404', border: '#fde68a' };
            if (type === 'renewal') return { label: '🔄 Renewal', bg: '#e0e7ff', color: '#4338ca', border: '#c7d2fe' };
            return { label: type || '—', bg: '#f3f4f6', color: '#6b7280', border: '#e5e7eb' };
        };

        // 🆕 Search count summary from stored data
        const searchSummary = expandedData?.searchCountSummary || {};
        const searchPass = searchSummary.passCount || 0;
        const searchBelow = searchSummary.belowThresholdCount || 0;
        const searchNoData = searchSummary.noDataCount || 0;
        const searchTotal = searchSummary.totalEvaluated || 0;

        return (
            <tr key={`${requestId}-detail`}>
                <td colSpan="100%" className="approver-expanded-cell">
                    <div className="approver-detail-panel">
                        {isLoadingDetails ? (
                            <div className="approver-detail-loading">
                                <div className="spinner-small"></div>
                                <span>Loading request details...</span>
                            </div>
                        ) : expandedData?.error ? (
                            <div className="approver-detail-error">
                                ❌ Error loading details: {expandedData.error}
                            </div>
                        ) : expandedData ? (
                            <>
                                <div className="approver-detail-grid">
                                    {/* ── Row 1: Request ID & Type ── */}
                                    <div className="approver-detail-item">
                                        <span className="approver-detail-label">Request ID</span>
                                        <span className="approver-detail-value">
                                            <a href={`${VIEWER_URL}?id=${requestId}`}
                                               target="_blank" rel="noopener noreferrer"
                                               className="approver-link">
                                                {requestId} ↗
                                            </a>
                                            {expandedData.approvalId && (
                                                <a href={`https://approvals.amazon.com/approval/${expandedData.approvalId}/details/memo-view/notes-and-summary`}
                                                   target="_blank" rel="noopener noreferrer"
                                                   style={{ marginLeft: '12px', fontSize: '12px', color: '#0073bb', textDecoration: 'none', fontWeight: '500' }}>
                                                    🔗 Approval #{expandedData.approvalId} ↗
                                                </a>
                                            )}
                                        </span>
                                    </div>
                                    <div className="approver-detail-item">
                                        <span className="approver-detail-label">Request Type</span>
                                        <span className="approver-detail-value">
                                            {(() => { const rt = getRequestTypeLabel(expandedData.requestType); return (
                                                <span style={{ display: 'inline-block', padding: '3px 12px', borderRadius: '14px', fontSize: '12px', fontWeight: '600', backgroundColor: rt.bg, color: rt.color, border: `1px solid ${rt.border}` }}>{rt.label}</span>
                                            ); })()}
                                            {(expandedData.requestType || '').includes('renewal') && <span style={{ marginLeft: '6px', fontSize: '12px', color: '#6366f1' }}>🔄 Renewal</span>}
                                            {(expandedData.requestType === 'approved' || expandedData.requestType === 'approved_renewal') && (() => {
                                                const leadershipApprovals = {
                                                    'PoE International': 'https://approvals.amazon.com/approval/47315200/details/memo-view/notes-and-summary'
                                                };
                                                const link = leadershipApprovals[expandedData.team];
                                                if (!link) return null;
                                                return (
                                                    <a href={link} target="_blank" rel="noopener noreferrer"
                                                        style={{ marginLeft: '10px', fontSize: '12px', color: '#0073bb', textDecoration: 'none', fontWeight: '600' }}>
                                                        🔗 Leadership Approval ↗
                                                    </a>
                                                );
                                            })()}
                                        </span>
                                    </div>

                                    {/* ── Requester Alias ── */}
                                    <div className="approver-detail-item">
                                        <span className="approver-detail-label">Requester</span>
                                        <span className="approver-detail-value" style={{ fontWeight: '600' }}>
                                            👤 {expandedData.requesterAlias || '—'}
                                        </span>
                                    </div>

                                    {/* ── Leadership Approval Status Banner ── */}
                                    {l8Status && l8Status.hasL8Stage && (
                                        <div className="approver-detail-item" style={{ gridColumn: '1 / -1' }}>
                                            {(() => {
                                                const rt = (expandedData?.riskTypeGroup || '').toUpperCase();
                                                const isBindle = expandedData?.requestType === 'bindle_access' || expandedData?.team === 'Bindle Access';
                                                const isHighRisk = rt === 'HRP' || rt === 'CRP' || isBindle;
                                                const level = rt === 'CRP' ? 'L8' : rt === 'HRP' ? 'L7' : 'L6';
                                                // Bindle is 3-stage but with different stages: Bindle POC → Leadership → SPS Team
                                                const flowLabel = isBindle ? 'Bindle' : rt;
                                                const stage1Label = isBindle ? 'Bindle POC' : 'SPS Permissions Team';
                                                const stage1Awaiting = isBindle ? 'Awaiting Bindle POC Approval' : 'Awaiting Security Team Approval';
                                                const stage2Label = isBindle ? 'Leadership Approval' : `${level} Approval`;
                                                const stage3Label = isBindle ? 'SPS Team' : 'Access Management Team';
                                                const stage3Awaiting = isBindle ? 'Awaiting SPS Team Approval' : 'Awaiting Permission Team Approval';

                                                if (isHighRisk) {
                                                    // 3-stage display — driven purely by the live per-stage data from
                                                    // getL8Status (stageNStatus can be APPROVED / REJECTED / NOT_REACHED /
                                                    // PENDING). We do NOT mix in l8Status.l8Status here — that caused
                                                    // stage bleed-through (e.g. a POC approval showing on Leadership).
                                                    const rawS1 = l8Status.stage1Status || (expandedData?.stage1ApprovedBy ? 'APPROVED' : 'PENDING');
                                                    const rawS2 = l8Status.stage2Status || 'PENDING';
                                                    const rawS3 = l8Status.stage3Status || 'PENDING';

                                                    const stage1Approver = l8Status.stage1Approver || expandedData?.stage1ApprovedBy || '';
                                                    const stage2Approver = l8Status.stage2Approver || '';
                                                    const stage3Approver = l8Status.stage3Approver || '';

                                                    const stage1Done = rawS1 === 'APPROVED';
                                                    const stage2Done = rawS2 === 'APPROVED';

                                                    // Map a raw stage status + position into a display status for the row.
                                                    const toDisplay = (raw, prevDone, prevRejected) => {
                                                        if (raw === 'APPROVED') return 'completed';
                                                        if (raw === 'REJECTED') return 'rejected';
                                                        if (raw === 'NOT_REACHED') return 'waiting';
                                                        // PENDING: it's the active stage only if all previous stages completed
                                                        return (prevDone && !prevRejected) ? 'active' : 'waiting';
                                                    };
                                                    const stage1Status = toDisplay(rawS1, true, false);
                                                    const s1Rejected = rawS1 === 'REJECTED';
                                                    const stage2Status = toDisplay(rawS2, stage1Done, s1Rejected);
                                                    const s2Rejected = rawS2 === 'REJECTED';
                                                    const stage3Status = toDisplay(rawS3, stage2Done, s1Rejected || s2Rejected);

                                                    const sublabelFor = (raw, approver, awaiting) =>
                                                        raw === 'APPROVED' ? `Approved by ${approver}`
                                                        : raw === 'REJECTED' ? `Rejected by ${approver}`
                                                        : raw === 'NOT_REACHED' ? 'Not reached (approval ended earlier)'
                                                        : awaiting;

                                                    return (
                                                        <div style={{ padding: '14px 16px', borderRadius: '10px', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                                            <div style={{ fontSize: '13px', fontWeight: '700', color: '#334155', marginBottom: '12px' }}>
                                                                📋 Approval Stages ({flowLabel} — 3-Stage)
                                                            </div>
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                                <ApproverStageRow stageNum={1} label={stage1Label} sublabel={sublabelFor(rawS1, stage1Approver, stage1Awaiting)} status={stage1Status} />
                                                                <ApproverStageRow stageNum={2} label={stage2Label} sublabel={sublabelFor(rawS2, stage2Approver, 'Awaiting Leadership Approval')} status={stage2Status} manager={l8Status.l8Manager} />
                                                                <ApproverStageRow stageNum={3} label={stage3Label} sublabel={sublabelFor(rawS3, stage3Approver, stage3Awaiting)} status={stage3Status} />
                                                            </div>
                                                        </div>
                                                    );
                                                } else {
                                                    // Original single banner for LRP/MRP
                                                    return (
                                                        <div style={{
                                                            padding: '12px 16px', borderRadius: '8px',
                                                            backgroundColor: l8Status.l8Status === 'APPROVED' ? '#dcfce7' : l8Status.l8Status === 'REJECTED' ? '#ffeef0' : l8Status.l8Status === 'PENDING' ? '#fff3cd' : '#f3f4f6',
                                                            border: `1px solid ${l8Status.l8Status === 'APPROVED' ? '#bbf7d0' : l8Status.l8Status === 'REJECTED' ? '#ffc1c8' : l8Status.l8Status === 'PENDING' ? '#fde68a' : '#e5e7eb'}`,
                                                            display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap'
                                                        }}>
                                                            <span style={{ fontSize: '18px' }}>
                                                                {l8Status.l8Status === 'APPROVED' ? '✅' : l8Status.l8Status === 'REJECTED' ? '❌' : l8Status.l8Status === 'PENDING' ? '⏳' : '❓'}
                                                            </span>
                                                            <div>
                                                                <div style={{ fontWeight: '700', fontSize: '14px', color: l8Status.l8Status === 'APPROVED' ? '#166534' : l8Status.l8Status === 'REJECTED' ? '#d13212' : '#92400e' }}>
                                                                    {level} Approval: {l8Status.l8Status}
                                                                </div>
                                                                <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                                                                    {l8Status.l8Manager && <span>{level} Manager: <strong>{l8Status.l8Manager}</strong></span>}
                                                                    {l8Status.l8ApproverAlias && <span> · Acted by: <strong>{l8Status.l8ApproverAlias}</strong></span>}
                                                                    {l8Status.l8ApprovedAt && <span> · {new Date(l8Status.l8ApprovedAt).toLocaleString()}</span>}
                                                                </div>
                                                            </div>
                                                            {l8Status.l8Status === 'PENDING' && (
                                                                <span style={{ marginLeft: 'auto', padding: '4px 12px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}>
                                                                    ⚠️ Awaiting {level} — proceed with caution
                                                                </span>
                                                            )}
                                                        </div>
                                                    );
                                                }
                                            })()}
                                        </div>
                                    )}
                                    {isLoadingL8 && (
                                        <div className="approver-detail-item" style={{ gridColumn: '1 / -1' }}>
                                            <span style={{ fontSize: '13px', color: '#94a3b8' }}>⏳ Checking leadership approval status...</span>
                                        </div>
                                    )}
                                    {l8Status && !l8Status.hasL8Stage && (
                                        <div className="approver-detail-item" style={{ gridColumn: '1 / -1' }}>
                                            <span style={{ fontSize: '13px', color: '#166534', fontWeight: '600' }}>✅ Pre-approved use case — no leadership approval required</span>
                                        </div>
                                    )}

                                    {/* ── Row 2: Function & Org ── */}
                                    <div className="approver-detail-item">
                                        <span className="approver-detail-label">Function</span>
                                        <span className="approver-detail-value">{expandedData.functionDropdown}</span>
                                    </div>
                                    <div className="approver-detail-item">
                                        <span className="approver-detail-label">Org Name</span>
                                        <span className="approver-detail-value">{expandedData.orgName || '—'}</span>
                                    </div>

                                    {/* ── Row 3: Team & Use Case ── */}
                                    <div className="approver-detail-item">
                                        <span className="approver-detail-label">Team</span>
                                        <span className="approver-detail-value">
                                            {expandedData.team}
                                            {expandedData.customTeamName && (
                                                <span style={{ color: '#0073bb', marginLeft: '4px' }}>
                                                    — {expandedData.customTeamName}
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    <div className="approver-detail-item">
                                        <span className="approver-detail-label">Use Case</span>
                                        <span className="approver-detail-value">{expandedData.useCaseDescription || expandedData.justification || expandedData.useCase || '—'}</span>
                                    </div>

                                    {/* ── T321 unlisted "User Details" fields — Process, Sub-Team, Legal Entity ──
                                         Only rendered when the request carries them (T321 unlisted path). */}
                                    {expandedData.t321Process && (
                                        <div className="approver-detail-item">
                                            <span className="approver-detail-label">Process</span>
                                            <span className="approver-detail-value">{expandedData.t321Process}</span>
                                        </div>
                                    )}
                                    {expandedData.subTeam && (
                                        <div className="approver-detail-item">
                                            <span className="approver-detail-label">Sub-Team</span>
                                            <span className="approver-detail-value">{expandedData.subTeam}</span>
                                        </div>
                                    )}
                                    {Array.isArray(expandedData.legalEntities) && expandedData.legalEntities.length > 0 && (
                                        <div className="approver-detail-item" style={{ gridColumn: '1 / -1' }}>
                                            <span className="approver-detail-label">Legal Entity</span>
                                            <span className="approver-detail-value">{expandedData.legalEntities.join(', ')}</span>
                                        </div>
                                    )}

                                    {/* ── T321 Base PI provisioning flag (Phase 3) ──
                                        Shown when this Regulated/NOIR request had users missing Base PI. A separate
                                        Base PI approval (L7→CTPS) was raised; access can't be granted until it's GRANTED. */}
                                    {expandedData.basePiStatus && (() => {
                                        const st = String(expandedData.basePiStatus).toUpperCase();
                                        const pending = Array.isArray(expandedData.basePiPending) ? expandedData.basePiPending : [];
                                        const isGranted = st === 'GRANTED';
                                        const isError = st === 'ERROR' || st === 'NEEDS_L7';
                                        const bg = isGranted ? '#f0fdf4' : isError ? '#fef2f2' : '#fffbeb';
                                        const bd = isGranted ? '#bbf7d0' : isError ? '#fecaca' : '#fde68a';
                                        const fg = isGranted ? '#166534' : isError ? '#991b1b' : '#92400e';
                                        return (
                                            <div className="approver-detail-item" style={{ gridColumn: '1 / -1' }}>
                                                <span className="approver-detail-label">🔑 Base PI Provisioning</span>
                                                <div style={{ marginTop: '4px', padding: '10px 14px', borderRadius: '8px', background: bg, border: `1px solid ${bd}`, fontSize: '13px', color: fg }}>
                                                    <div style={{ fontWeight: '700', marginBottom: '4px' }}>
                                                        {isGranted ? '✅ Base PI GRANTED — OK to grant Regulated/NOIR'
                                                            : st === 'NEEDS_L7' ? '⚠️ Base PI pending — NO L7 captured, cannot auto-raise'
                                                            : st === 'ERROR' ? '❌ Base PI approval failed to raise'
                                                            : '⏳ Base PI PENDING — do not grant until the Base PI approval completes'}
                                                    </div>
                                                    {pending.length > 0 && (
                                                        <div style={{ marginBottom: '4px' }}>
                                                            <strong>{pending.length} user(s)</strong> missing Base PI: {pending.join(', ')}
                                                        </div>
                                                    )}
                                                    {expandedData.basePiApprovalsUrl && (
                                                        <div>
                                                            <a href={expandedData.basePiApprovalsUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#0073bb', fontWeight: '700' }}>
                                                                View Base PI approval ↗
                                                            </a>
                                                            {expandedData.basePiApprovalRequestId && <span style={{ color: '#64748b' }}> ({expandedData.basePiApprovalRequestId})</span>}
                                                        </div>
                                                    )}
                                                    {isGranted && expandedData.basePiGrantedAt && (
                                                        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>Granted {expandedData.basePiGrantedAt}{expandedData.basePiGrantedBy ? ` by ${expandedData.basePiGrantedBy}` : ''}</div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {/* ── Permissions & Links ── */}
                                    {expandedData.selectedPermissionNames && expandedData.selectedPermissionNames.length > 0 && (
                                        <div className="approver-detail-item" style={{ gridColumn: '1 / -1' }}>
                                            <span className="approver-detail-label">Permissions ({expandedData.riskTypeGroup || 'All'})</span>
                                            <span className="approver-detail-value">
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                    {(expandedData.permissionLinks || expandedData.selectedPermissionNames.map(n => ({ name: n, link: '' }))).map((p, idx) => (
                                                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            {expandedData.riskTypeGroup && (
                                                                <span style={{ padding: '1px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: '700', backgroundColor: '#f3f4f6', color: '#555' }}>{expandedData.riskTypeGroup}</span>
                                                            )}
                                                            <span style={{ fontWeight: '600', fontSize: '13px' }}>{p.name}</span>
                                                            {p.link && (
                                                                <a href={p.link} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: '#0073bb', textDecoration: 'none' }}>
                                                                    Paragon ↗
                                                                </a>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </span>
                                        </div>
                                    )}

                                    {/* ── Row 4: User Levels & Frequency ── */}
                                    <div className="approver-detail-item">
                                        <span className="approver-detail-label">User Levels</span>
                                        <span className="approver-detail-value">
                                            {formatArray(expandedData.userLevels) || '—'}
                                        </span>
                                    </div>
                                    <div className="approver-detail-item">
                                        <span className="approver-detail-label">Access Frequency</span>
                                        <span className="approver-detail-value">
                                            {formatFrequency(expandedData.frequency) || '—'}
                                        </span>
                                    </div>

                                    {/* ── Row 5: Search Criteria ── */}
                                    <div className="approver-detail-item" style={{ gridColumn: '1 / -1' }}>
                                        <span className="approver-detail-label">UFS Search Criteria</span>
                                        <span className="approver-detail-value">
                                            {formatSearchCriteria(expandedData.searchCriteria) || '—'}
                                        </span>
                                    </div>

                                    {/* ── Row 6: Queues (if provided) ── */}
                                    {expandedData.queues && (
                                        <div className="approver-detail-item" style={{ gridColumn: '1 / -1' }}>
                                            <span className="approver-detail-label">UFS Queue List</span>
                                            <span className="approver-detail-value approver-detail-users">
                                                {expandedData.queues}
                                            </span>
                                        </div>
                                    )}

                                    {/* ── Row 7: Justification (unlisted only) ── */}
                                    {(expandedData.requestType === 'unlisted' || expandedData.requestType === 'unlisted_renewal' || expandedData.justification) && (
                                        <div className="approver-detail-item" style={{ gridColumn: '1 / -1' }}>
                                            <span className="approver-detail-label">Justification</span>
                                            <span className="approver-detail-value">{expandedData.justification || '—'}</span>
                                        </div>
                                    )}

                                    {/* ── Row 8: Created & Users ── */}
                                    <div className="approver-detail-item">
                                        <span className="approver-detail-label">Created</span>
                                        <span className="approver-detail-value">{formatDate(expandedData.createdAt)}</span>
                                    </div>
                                    <div className="approver-detail-item">
                                        <span className="approver-detail-label">Users</span>
                                        <span className="approver-detail-value">
                                            {expandedData.totalUsers} total · {expandedData.totalEligible} eligible · {expandedData.totalIneligible} ineligible
                                        </span>
                                    </div>

                                    {/* ── Row 8.1: Provisioning Details (Copy-friendly) ── */}
                                    <div className="approver-detail-item" style={{ gridColumn: '1 / -1' }}>
                                        <span className="approver-detail-label">📋 Provisioning Details</span>
                                        <span className="approver-detail-value">
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
                                                <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#f8f9fa', border: '1px solid #e2e6ea' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                                        <span style={{ fontSize: '11px', fontWeight: '600', color: '#666' }}>Users</span>
                                                        <button onClick={() => navigator.clipboard.writeText(expandedData.userList || expandedData.eligibleUsers?.join(',') || '')}
                                                            style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '4px', border: '1px solid #b8d4f0', background: '#f0f8ff', color: '#0073bb', cursor: 'pointer', fontWeight: '600' }}>📋 Copy</button>
                                                    </div>
                                                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#333', wordBreak: 'break-all' }}>{expandedData.userList || expandedData.eligibleUsers?.join(', ') || '—'}</div>
                                                </div>
                                                <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#f8f9fa', border: '1px solid #e2e6ea' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                                        <span style={{ fontSize: '11px', fontWeight: '600', color: '#666' }}>Start Date</span>
                                                        <button onClick={() => { const d = new Date(); navigator.clipboard.writeText(`${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')}/${d.getFullYear()}`); }}
                                                            style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '4px', border: '1px solid #b8d4f0', background: '#f0f8ff', color: '#0073bb', cursor: 'pointer', fontWeight: '600' }}>📋 Copy</button>
                                                    </div>
                                                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#333' }}>{(() => { const d = new Date(); return `${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')}/${d.getFullYear()}`; })()}</div>
                                                </div>
                                                <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#f8f9fa', border: '1px solid #e2e6ea' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                                        <span style={{ fontSize: '11px', fontWeight: '600', color: '#666' }}>End Date</span>
                                                        <button onClick={() => { const endStr = expandedData.accessEndDate ? (() => { const d = new Date(expandedData.accessEndDate); return `${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')}/${d.getFullYear()}`; })() : (() => { const days = (expandedData.riskTypeGroup === 'CRP' || expandedData.riskTypeGroup === 'HRP') ? 120 : 180; const end = new Date(); end.setDate(end.getDate() + days); return `${String(end.getMonth()+1).padStart(2,'0')}/${String(end.getDate()).padStart(2,'0')}/${end.getFullYear()}`; })(); navigator.clipboard.writeText(endStr); }}
                                                            style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '4px', border: '1px solid #b8d4f0', background: '#f0f8ff', color: '#0073bb', cursor: 'pointer', fontWeight: '600' }}>📋 Copy</button>
                                                    </div>
                                                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#333' }}>{expandedData.accessEndDate ? (() => { const d = new Date(expandedData.accessEndDate); return `${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')}/${d.getFullYear()}`; })() : (() => { const days = (expandedData.riskTypeGroup === 'CRP' || expandedData.riskTypeGroup === 'HRP') ? 120 : 180; const end = new Date(); end.setDate(end.getDate() + days); return `${String(end.getMonth()+1).padStart(2,'0')}/${String(end.getDate()).padStart(2,'0')}/${end.getFullYear()} (${days} days)`; })()}</div>
                                                </div>
                                                <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#f8f9fa', border: '1px solid #e2e6ea' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                                        <span style={{ fontSize: '11px', fontWeight: '600', color: '#666' }}>Reason</span>
                                                        <button onClick={() => navigator.clipboard.writeText(requestId)}
                                                            style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '4px', border: '1px solid #b8d4f0', background: '#f0f8ff', color: '#0073bb', cursor: 'pointer', fontWeight: '600' }}>📋 Copy</button>
                                                    </div>
                                                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#333' }}>{requestId}</div>
                                                </div>
                                            </div>
                                        </span>
                                    </div>

                                    {/* 🆕 Row 8.5: Search Activity Summary — Approver Insight */}
                                                                        {/* 🆕 Row 8.5: Search Activity Summary — Approver Insight */}
                                    {searchTotal > 0 && (
                                        <div className="approver-detail-item" style={{ gridColumn: '1 / -1' }}>
                                            <span className="approver-detail-label">
                                                📊 Search Activity (Approver Insight)
                                            </span>
                                            <span className="approver-detail-value">
                                                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
                                                    {searchPass > 0 && (
                                                        <span style={{
                                                            display: 'inline-flex', alignItems: 'center',
                                                            padding: '4px 12px', borderRadius: '16px', fontSize: '13px', fontWeight: '600',
                                                            backgroundColor: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0'
                                                        }}>
                                                            ✅ {searchPass} Sufficient
                                                        </span>
                                                    )}
                                                    {searchBelow > 0 && (
                                                        <span style={{
                                                            display: 'inline-flex', alignItems: 'center',
                                                            padding: '4px 12px', borderRadius: '16px', fontSize: '13px', fontWeight: '600',
                                                            backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #fde68a'
                                                        }}>
                                                            ⚠️ {searchBelow} Low Activity
                                                        </span>
                                                    )}
                                                    {searchNoData > 0 && (
                                                        <span style={{
                                                            display: 'inline-flex', alignItems: 'center',
                                                            padding: '4px 12px', borderRadius: '16px', fontSize: '13px', fontWeight: '600',
                                                            backgroundColor: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb'
                                                        }}>
                                                            — {searchNoData} No Data
                                                        </span>
                                                    )}
                                                    <span style={{ fontSize: '12px', color: '#888' }}>
                                                        ({searchTotal} users evaluated)
                                                    </span>
                                                </div>

                                                {/* 🆕 Detailed Per-User Table — Approver Only */}
                                                {expandedData.tableData && expandedData.tableData.length > 0 && (
                                                    <div style={{
                                                        border: '1px solid #e2e6ea',
                                                        borderRadius: '8px',
                                                        overflow: 'auto',
                                                        marginTop: '4px'
                                                    }}>
                                                        <table style={{
                                                            width: '100%',
                                                            borderCollapse: 'collapse',
                                                            fontSize: '13px',
                                                            minWidth: '1000px'
                                                        }}>
                                                            <thead>
                                                                <tr style={{ backgroundColor: '#f1f3f5' }}>
                                                                    <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', color: '#495057', borderBottom: '2px solid #dee2e6', whiteSpace: 'nowrap' }}>
                                                                        Login
                                                                    </th>
                                                                    <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: '600', color: '#495057', borderBottom: '2px solid #dee2e6', whiteSpace: 'nowrap' }}>
                                                                        Activity
                                                                    </th>
                                                                    <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: '600', color: '#495057', borderBottom: '2px solid #dee2e6', whiteSpace: 'nowrap' }}>
                                                                        Inactive Days
                                                                    </th>
                                                                    <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: '600', color: '#495057', borderBottom: '2px solid #dee2e6', whiteSpace: 'nowrap' }}>
                                                                        Base PI
                                                                    </th>
                                                                    <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: '600', color: '#495057', borderBottom: '2px solid #dee2e6', whiteSpace: 'nowrap' }}>
                                                                        Job Family
                                                                    </th>
                                                                    <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: '600', color: '#495057', borderBottom: '2px solid #dee2e6', whiteSpace: 'nowrap' }}>
                                                                        Job Level
                                                                    </th>
                                                                    <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: '600', color: '#495057', borderBottom: '2px solid #dee2e6', whiteSpace: 'nowrap', borderLeft: '2px solid #0073bb33' }}>
                                                                        Searches (30d)
                                                                    </th>
                                                                    <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: '600', color: '#495057', borderBottom: '2px solid #dee2e6', whiteSpace: 'nowrap' }}>
                                                                        Searches (60d)
                                                                    </th>
                                                                    <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: '600', color: '#495057', borderBottom: '2px solid #dee2e6', whiteSpace: 'nowrap' }}>
                                                                        Searches (90d)
                                                                    </th>
                                                                    <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: '600', color: '#495057', borderBottom: '2px solid #dee2e6', whiteSpace: 'nowrap', borderLeft: '2px solid #0073bb33' }}>
                                                                        Search Status
                                                                    </th>
                                                                    <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: '600', color: '#495057', borderBottom: '2px solid #dee2e6', whiteSpace: 'nowrap' }}>
                                                                        UFS Status
                                                                    </th>
                                                                    <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: '600', color: '#495057', borderBottom: '2px solid #dee2e6', whiteSpace: 'nowrap' }}>
                                                                        Eligible
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {expandedData.tableData.map((user, idx) => {
                                                                    const counts = user.searchCounts || {};
                                                                    const days30 = counts['30'];
                                                                    const days60 = counts['60'];
                                                                    const days90 = counts['90'];
                                                                    
                                                                    const getCountStyle = (count, threshold) => {
                                                                        if (count === undefined || count === null) return { color: '#6b7280' };
                                                                        return count > threshold 
                                                                            ? { color: '#166534', fontWeight: '600' } 
                                                                            : { color: '#d13212', fontWeight: '600' };
                                                                    };

                                                                    const getSearchBadge = (status) => {
                                                                        if (status === 'PASS') return { bg: '#dcfce7', color: '#166534', border: '#bbf7d0', label: '✅ Sufficient' };
                                                                        if (status === 'BELOW_THRESHOLD') return { bg: '#fef3c7', color: '#92400e', border: '#fde68a', label: '⚠️ Low' };
                                                                        return { bg: '#f3f4f6', color: '#6b7280', border: '#e5e7eb', label: '— No Data' };
                                                                    };

                                                                    const getEligibleBadge = (eligible) => {
                                                                        if (eligible && eligible.includes('Eligible') && !eligible.includes('Not')) 
                                                                            return { bg: '#dcfce7', color: '#166534', border: '#bbf7d0', label: '✅ Eligible' };
                                                                        if (eligible && eligible.includes('Not Eligible')) 
                                                                            return { bg: '#ffeef0', color: '#d13212', border: '#ffc1c8', label: '❌ Not Eligible' };
                                                                        return { bg: '#fff8dc', color: '#92400e', border: '#fde68a', label: '⚠️ Not Found' };
                                                                    };

                                                                    const searchBadge = getSearchBadge(user.searchActivity);
                                                                    const eligibleBadge = getEligibleBadge(user.eligible);

                                                                    return (
                                                                        <tr key={idx} style={{ 
                                                                            borderBottom: '1px solid #eee',
                                                                            backgroundColor: idx % 2 === 0 ? '#fff' : '#fafbfc'
                                                                        }}>
                                                                            {/* Login */}
                                                                            <td style={{ padding: '7px 10px', fontWeight: '600' }}>
                                                                                {user.login}
                                                                            </td>

                                                                            {/* Activity */}
                                                                            <td style={{ 
                                                                                padding: '7px 10px', textAlign: 'center',
                                                                                color: user.inactivity === 'Active' ? '#166534' : user.inactivity === 'Inactive' ? '#d13212' : '#666',
                                                                                fontWeight: '600'
                                                                            }}>
                                                                                {user.inactivity || 'N/A'}
                                                                            </td>

                                                                            {/* Inactive Days */}
                                                                            <td style={{ 
                                                                                padding: '7px 10px', textAlign: 'center',
                                                                                color: user.inactiveDays !== 'N/A' && parseInt(user.inactiveDays) > 30 ? '#d13212' : '#166534',
                                                                                fontWeight: '600'
                                                                            }}>
                                                                                {user.inactiveDays !== 'N/A' ? user.inactiveDays : '—'}
                                                                            </td>

                                                                            {/* Base PI */}
                                                                            <td style={{ 
                                                                                padding: '7px 10px', textAlign: 'center',
                                                                                color: user.basePIAccess === 'Yes' ? '#166534' : user.basePIAccess === 'No' ? '#d13212' : '#666',
                                                                                fontWeight: '600'
                                                                            }}>
                                                                                {user.basePIAccess || 'N/A'}
                                                                            </td>

                                                                            {/* Job Family */}
                                                                            <td style={{ padding: '7px 10px', textAlign: 'center' }}>
                                                                                {user.jobFamily || 'N/A'}
                                                                            </td>

                                                                            {/* Job Level */}
                                                                            <td style={{ 
                                                                                padding: '7px 10px', textAlign: 'center',
                                                                                color: parseInt(user.jobLevel) >= 3 ? '#166534' : parseInt(user.jobLevel) < 3 ? '#d13212' : '#666',
                                                                                fontWeight: '600'
                                                                            }}>
                                                                                {user.jobLevel !== undefined ? user.jobLevel : 'N/A'}
                                                                            </td>

                                                                            {/* Search Counts — 30d (approver-only section starts) */}
                                                                            <td style={{ padding: '7px 10px', textAlign: 'center', borderLeft: '2px solid #0073bb22', ...getCountStyle(days30, 200) }}>
                                                                                {days30 !== undefined ? days30 : '—'}
                                                                            </td>

                                                                            {/* Search Counts — 60d */}
                                                                            <td style={{ padding: '7px 10px', textAlign: 'center', ...getCountStyle(days60, 400) }}>
                                                                                {days60 !== undefined ? days60 : '—'}
                                                                            </td>

                                                                            {/* Search Counts — 90d */}
                                                                            <td style={{ padding: '7px 10px', textAlign: 'center', ...getCountStyle(days90, 600) }}>
                                                                                {days90 !== undefined ? days90 : '—'}
                                                                            </td>

                                                                            {/* Search Status */}
                                                                            <td style={{ padding: '7px 10px', textAlign: 'center', borderLeft: '2px solid #0073bb22' }}>
                                                                                <span style={{
                                                                                    display: 'inline-block', padding: '2px 10px',
                                                                                    borderRadius: '12px', fontSize: '11px', fontWeight: '600',
                                                                                    backgroundColor: searchBadge.bg, color: searchBadge.color,
                                                                                    border: `1px solid ${searchBadge.border}`
                                                                                }}>
                                                                                    {searchBadge.label}
                                                                                </span>
                                                                            </td>

                                                                            {/* UFS Status */}
                                                                            <td style={{ padding: '7px 10px', textAlign: 'center', fontSize: '12px' }}>
                                                                                {user.jobLevelStatus || 'N/A'}
                                                                            </td>

                                                                            {/* Eligible */}
                                                                            <td style={{ padding: '7px 10px', textAlign: 'center' }}>
                                                                                <span style={{
                                                                                    display: 'inline-block', padding: '2px 10px',
                                                                                    borderRadius: '12px', fontSize: '11px', fontWeight: '600',
                                                                                    backgroundColor: eligibleBadge.bg, color: eligibleBadge.color,
                                                                                    border: `1px solid ${eligibleBadge.border}`
                                                                                }}>
                                                                                    {eligibleBadge.label}
                                                                                </span>
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                        
                                                        {/* Threshold legend */}
                                                        <div style={{
                                                            padding: '8px 12px',
                                                            backgroundColor: '#f8f9fa',
                                                            borderTop: '1px solid #e2e6ea',
                                                            fontSize: '11px',
                                                            color: '#888',
                                                            display: 'flex',
                                                            gap: '16px',
                                                            flexWrap: 'wrap'
                                                        }}>
                                                            <span>📏 Thresholds: <strong>30d</strong> &gt;200 | <strong>60d</strong> &gt;400 | <strong>90d</strong> &gt;600</span>
                                                            <span style={{ color: '#166534' }}>● Green = above threshold</span>
                                                            <span style={{ color: '#d13212' }}>● Red = below threshold</span>
                                                            <span style={{ color: '#0073bb', opacity: 0.6 }}>│ Blue border = approver-only data</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </span>
                                        </div>
                                    )}

                                    {/* ── Row 9: User Logins ── */}
                                    <div className="approver-detail-item" style={{ gridColumn: '1 / -1' }}>
                                        <span className="approver-detail-label">User Logins</span>
                                        <span className="approver-detail-value approver-detail-users" style={{ fontWeight: '600' }}>
                                            {(() => {
                                                const fromList = expandedData.userList ? expandedData.userList.split(',').map(u => u.trim()).filter(Boolean) : [];
                                                const fromEligible = expandedData.eligibleUsers || [];
                                                const allUsers = fromList.length > 0 ? fromList : fromEligible;
                                                return allUsers.length > 0 ? allUsers.join(', ') : 'None';
                                            })()}
                                        </span>
                                    </div>

                                    {/* ── Row 10: Ineligible Users ── */}
                                    {expandedData.ineligibleUsers && expandedData.ineligibleUsers.length > 0 && (
                                        <div className="approver-detail-item" style={{ gridColumn: '1 / -1' }}>
                                            <span className="approver-detail-label">Ineligible Users</span>
                                            <span className="approver-detail-value approver-detail-users" style={{ color: '#d13212' }}>
                                                {expandedData.ineligibleUsers.join(', ')}
                                            </span>
                                        </div>
                                    )}

                                    {/* ── Row 11: Exception Details ── */}
                                    {expandedData.ineligibleNotes && Object.keys(expandedData.ineligibleNotes).length > 0 && (
                                        <div className="approver-detail-item" style={{ gridColumn: '1 / -1' }}>
                                            <span className="approver-detail-label">Exception Details</span>
                                            <div style={{ marginTop: '4px' }}>
                                                {Object.entries(expandedData.ineligibleNotes).map(([key, note]) => {
                                                    const login = key.includes(':') ? key.split(':')[1] : key;
                                                    return (
                                                        <div key={key} style={{ padding: '6px 10px', marginBottom: '4px', borderRadius: '6px', background: '#fffbeb', border: '1px solid #fde68a', fontSize: '12px' }}>
                                                            <span style={{ fontWeight: '600', color: '#92400e' }}>{login}:</span> {note}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* ── Validation Results Table (risk-type specific) ── */}
                                {expandedData.tableData && expandedData.tableData.length > 0 && (() => {
                                    const rtGroup = expandedData.riskTypeGroup || (expandedData.riskTypes && expandedData.riskTypes[0]) || '';
                                    
                                    // LRP with only base_pi or old data with N/A values: show simple message
                                    const isLrpNoChecks = rtGroup === 'LRP' && expandedData.tableData.every(r => r.basePIAccess === 'N/A');
                                    if (isLrpNoChecks) {
                                        return (
                                            <div style={{ margin: '16px 0', padding: '12px 16px', borderRadius: '8px', background: '#f0fdf4', border: '1px solid #bbf7d0', fontSize: '13px', color: '#166534' }}>
                                                ✅ LRP (Base PI) — No validation checks required. All users are eligible.
                                            </div>
                                        );
                                    }

                                    // Re-evaluate eligibility per risk type
                                    const evaluateForRT = (row) => {
                                        if (rtGroup === 'LRP') return row.basePIAccess === 'Yes';
                                        if (rtGroup === 'MRP') return row.basePIAccess === 'Yes';
                                        if (rtGroup === 'HRP') return row.basePIAccess === 'Yes' && row.inactivity !== 'Inactive';
                                        // CRP or unknown — use stored eligible
                                        return row.eligible && row.eligible.includes('Eligible') && !row.eligible.includes('Not');
                                    };

                                    return (
                                    <div style={{ margin: '16px 0', padding: '14px 16px', borderRadius: '10px', border: '1px solid #e2e8f0', background: '#fff' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                            <div style={{ fontWeight: '600', fontSize: '13px', color: '#475569' }}>
                                                📊 Validation Results ({rtGroup})
                                            </div>
                                            <div style={{ display: 'flex', gap: '4px' }}>
                                                {['all', 'eligible', 'not_eligible'].map(f => {
                                                    const count = f === 'all' ? expandedData.tableData.length
                                                        : f === 'eligible' ? expandedData.tableData.filter(r => evaluateForRT(r)).length
                                                        : expandedData.tableData.filter(r => !evaluateForRT(r)).length;
                                                    const labels = { all: `All (${count})`, eligible: `✅ (${count})`, not_eligible: `❌ (${count})` };
                                                    return (
                                                        <button key={f} type="button" onClick={() => setApproverTableFilter(f)}
                                                            style={{ padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', cursor: 'pointer',
                                                                border: `1px solid ${approverTableFilter === f ? '#0073bb' : '#ddd'}`,
                                                                background: approverTableFilter === f ? '#f0f8ff' : '#fff',
                                                                color: approverTableFilter === f ? '#0073bb' : '#666' }}>
                                                            {labels[f]}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        <div style={{ overflowX: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                                <thead>
                                                    <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e2e6ea' }}>
                                                        <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', color: '#555' }}>Login</th>
                                                        <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', color: '#555' }}>Department</th>
                                                        {(rtGroup === 'HRP' || rtGroup === 'CRP' || !rtGroup) && (
                                                            <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', color: '#555' }}>Activity</th>
                                                        )}
                                                        {(rtGroup === 'MRP' || rtGroup === 'HRP' || rtGroup === 'CRP' || !rtGroup) && (
                                                            <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', color: '#555' }}>Base PI</th>
                                                        )}
                                                        {(rtGroup === 'CRP' || !rtGroup) && (
                                                            <>
                                                                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', color: '#555' }}>Inactive Days</th>
                                                                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', color: '#555' }}>Job Family</th>
                                                                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', color: '#555' }}>Job Level</th>
                                                                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', color: '#555' }}>Searches (30D)</th>
                                                                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', color: '#555' }}>Searches (60D)</th>
                                                                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', color: '#555' }}>Searches (90D)</th>
                                                                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', color: '#555' }}>Search Status</th>
                                                            </>
                                                        )}
                                                        {(expandedData.useCaseValue || '').split(',').some(p => ['unfettered_search', 'bulk_operations', 'action_widget'].includes(p.trim().toLowerCase())) && (
                                                            <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', color: '#555' }}>Region</th>
                                                        )}
                                                        <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', color: '#555' }}>Eligible</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {expandedData.tableData
                                                        .filter(row => {
                                                            if (approverTableFilter === 'eligible') return evaluateForRT(row);
                                                            if (approverTableFilter === 'not_eligible') return !evaluateForRT(row);
                                                            return true;
                                                        })
                                                        .map((row, idx) => {
                                                        const isEligible = evaluateForRT(row);
                                                        const showRegion = (expandedData.useCaseValue || '').split(',').some(p => ['unfettered_search', 'bulk_operations', 'action_widget'].includes(p.trim().toLowerCase()));
                                                        return (
                                                        <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                                            <td style={{ padding: '8px 10px', fontWeight: '600' }}>{row.login}</td>
                                                            <td style={{ padding: '8px 10px', fontSize: '11px' }}>
                                                                {row.department || '—'}
                                                                {row.department && row.department !== 'N/A' && expandedData.team && (() => {
                                                                    const dept = row.department.toLowerCase();
                                                                    const teamName = (expandedData.team || '').toLowerCase();
                                                                    const orgNameParts = (expandedData.orgName || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
                                                                    const matches = (teamName && dept.includes(teamName)) || orgNameParts.some(o => dept.includes(o) || o.includes(dept)) || dept.includes('poe') || dept.includes('tse');
                                                                    if (!matches) return <span style={{ display: 'block', fontSize: '10px', color: '#d4880f', fontWeight: '600' }}>⚠️ Mismatch</span>;
                                                                    return null;
                                                                })()}
                                                            </td>
                                                            {(rtGroup === 'HRP' || rtGroup === 'CRP' || !rtGroup) && (
                                                                <td style={{ padding: '8px 10px', color: row.inactivity === 'Active' ? '#166534' : row.inactivity === 'Inactive' ? '#d13212' : '#666' }}>{row.inactivity || '—'}</td>
                                                            )}
                                                            {(rtGroup === 'MRP' || rtGroup === 'HRP' || rtGroup === 'CRP' || !rtGroup) && (
                                                                <td style={{ padding: '8px 10px', color: row.basePIAccess === 'Yes' ? '#166534' : row.basePIAccess === 'No' ? '#d13212' : '#666' }}>{row.basePIAccess || '—'}</td>
                                                            )}
                                                            {(rtGroup === 'CRP' || !rtGroup) && (
                                                                <>
                                                                    <td style={{ padding: '8px 10px' }}>{row.inactiveDays || '—'}</td>
                                                                    <td style={{ padding: '8px 10px' }}>{row.jobFamily || '—'}</td>
                                                                    <td style={{ padding: '8px 10px', fontWeight: '600', color: parseInt(row.jobLevel) >= 3 ? '#166534' : parseInt(row.jobLevel) < 3 ? '#d13212' : '#666' }}>{row.jobLevel || '—'}</td>
                                                                    <td style={{ padding: '8px 10px', color: '#0073bb', fontWeight: '600' }}>{row.searchCounts?.['30'] ?? '—'}</td>
                                                                    <td style={{ padding: '8px 10px', color: '#0073bb', fontWeight: '600' }}>{row.searchCounts?.['60'] ?? '—'}</td>
                                                                    <td style={{ padding: '8px 10px', color: '#0073bb', fontWeight: '600' }}>{row.searchCounts?.['90'] ?? '—'}</td>
                                                                    <td style={{ padding: '8px 10px' }}>
                                                                        {row.searchActivity === 'PASS' ? <span style={{ color: '#166534', fontWeight: '600' }}>✅ Sufficient</span> : row.searchActivity === 'BELOW_THRESHOLD' ? <span style={{ color: '#92400e', fontWeight: '600' }}>⚠️ Low</span> : <span style={{ color: '#666' }}>— No Data</span>}
                                                                    </td>
                                                                </>
                                                            )}
                                                            {showRegion && (
                                                                <td style={{ padding: '8px 10px' }}>
                                                                    {row.isCHN 
                                                                        ? <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '700', backgroundColor: '#ffeef0', color: '#d13212', border: '1px solid #ffc1c8' }}>🇨🇳 CHN</span>
                                                                        : <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', backgroundColor: '#dcfce7', color: '#166534' }}>{row.region || 'N/A'}</span>
                                                                    }
                                                                </td>
                                                            )}
                                                            <td style={{ padding: '8px 10px' }}>
                                                                {isEligible ? <span style={{ color: '#166534', fontWeight: '600' }}>✅ Eligible</span> : <span style={{ color: '#d13212', fontWeight: '600' }}>❌ Not Eligible</span>}
                                                            </td>
                                                        </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                    );
                                })()}

                                {/* ── Requester History ── */}
                                {(requesterHistory || isLoadingHistory) && (
                                    <div style={{
                                        margin: '16px 0', padding: '14px 16px', borderRadius: '10px',
                                        border: '1px solid #e2e8f0', background: '#f8fafc'
                                    }}>
                                        <div style={{ fontWeight: '600', fontSize: '13px', color: '#475569', marginBottom: '8px' }}>
                                            👤 Requester History: {expandedData?.requesterAlias || '—'}
                                        </div>
                                        {isLoadingHistory ? (
                                            <div style={{ fontSize: '13px', color: '#94a3b8' }}>Loading history...</div>
                                        ) : requesterHistory ? (
                                            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                                                <span style={{ fontSize: '13px' }}>
                                                    📋 <strong>{requesterHistory.totalRequests}</strong> total requests
                                                </span>
                                                <span style={{ fontSize: '13px', color: '#166534' }}>
                                                    ✅ {requesterHistory.approved} approved
                                                </span>
                                                <span style={{ fontSize: '13px', color: '#d13212' }}>
                                                    ❌ {requesterHistory.rejected} rejected
                                                </span>
                                                {requesterHistory.pending > 0 && (
                                                    <span style={{ fontSize: '13px', color: '#92400e' }}>
                                                        ⏳ {requesterHistory.pending} pending
                                                    </span>
                                                )}
                                                <span style={{
                                                    padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
                                                    backgroundColor: requesterHistory.approvalRate >= 80 ? '#dcfce7' : requesterHistory.approvalRate >= 50 ? '#fef3c7' : '#ffeef0',
                                                    color: requesterHistory.approvalRate >= 80 ? '#166534' : requesterHistory.approvalRate >= 50 ? '#92400e' : '#d13212'
                                                }}>
                                                    {requesterHistory.approvalRate}% approval rate
                                                </span>
                                            </div>
                                        ) : null}
                                    </div>
                                )}

                                {/* ── AI Approver Analysis ── */}
                                <div style={{
                                    margin: '16px 0',
                                    padding: '16px',
                                    borderRadius: '10px',
                                    border: '1px solid #e0e7ff',
                                    background: 'linear-gradient(135deg, #f0f4ff 0%, #faf5ff 100%)'
                                }}>
                                    <div style={{ fontWeight: '700', fontSize: '14px', color: '#4338ca', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        AI Approver Assistant
                                    </div>

                                    {isLoadingAI ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#6366f1' }}>
                                            <span className="spinner-small"></span> Analyzing request...
                                        </div>
                                    ) : aiAnalysis ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                            {/* Risk + Decision row */}
                                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                                                {/* Risk badge */}
                                                <span style={{
                                                    padding: '4px 14px', borderRadius: '16px', fontSize: '13px', fontWeight: '700',
                                                    backgroundColor: aiAnalysis.riskLevel === 'LOW' ? '#dcfce7' : aiAnalysis.riskLevel === 'MEDIUM' ? '#fef3c7' : '#ffeef0',
                                                    color: aiAnalysis.riskLevel === 'LOW' ? '#166534' : aiAnalysis.riskLevel === 'MEDIUM' ? '#92400e' : '#d13212',
                                                    border: `1px solid ${aiAnalysis.riskLevel === 'LOW' ? '#bbf7d0' : aiAnalysis.riskLevel === 'MEDIUM' ? '#fde68a' : '#ffc1c8'}`
                                                }}>
                                                    {aiAnalysis.riskLevel === 'LOW' ? '🟢' : aiAnalysis.riskLevel === 'MEDIUM' ? '🟡' : '🔴'} {aiAnalysis.riskLevel} RISK
                                                </span>
                                                {/* Decision badge */}
                                                <span style={{
                                                    padding: '4px 14px', borderRadius: '16px', fontSize: '13px', fontWeight: '700',
                                                    backgroundColor: aiAnalysis.suggestedDecision === 'APPROVE' ? '#dcfce7' : aiAnalysis.suggestedDecision === 'LIKELY_REJECT' ? '#ffeef0' : '#fff3cd',
                                                    color: aiAnalysis.suggestedDecision === 'APPROVE' ? '#166534' : aiAnalysis.suggestedDecision === 'LIKELY_REJECT' ? '#d13212' : '#856404',
                                                    border: `1px solid ${aiAnalysis.suggestedDecision === 'APPROVE' ? '#bbf7d0' : aiAnalysis.suggestedDecision === 'LIKELY_REJECT' ? '#ffc1c8' : '#fde68a'}`
                                                }}>
                                                    {aiAnalysis.suggestedDecision === 'APPROVE' ? '✅ Recommend: Approve' : aiAnalysis.suggestedDecision === 'LIKELY_REJECT' ? '❌ Recommend: Reject' : '⚠️ Review Carefully'}
                                                </span>
                                                {aiAnalysis.historicalMatchCount > 0 && (
                                                    <span style={{ fontSize: '12px', color: '#6366f1' }}>
                                                        📊 {aiAnalysis.historicalMatchCount} similar past requests
                                                    </span>
                                                )}
                                            </div>

                                            {/* Risk summary */}
                                            <div style={{ fontSize: '13px', color: '#333', lineHeight: '1.8', whiteSpace: 'pre-line' }}>
                                                {aiAnalysis.riskSummary}
                                            </div>

                                            {/* Decision reasoning */}
                                            <div style={{ fontSize: '13px', color: '#4338ca', fontStyle: 'italic', lineHeight: '1.5' }}>
                                                💡 {aiAnalysis.decisionReasoning}
                                            </div>

                                            {/* Key factors */}
                                            {aiAnalysis.keyFactors && aiAnalysis.keyFactors.length > 0 && (
                                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                                    {aiAnalysis.keyFactors.map((factor, i) => (
                                                        <span key={i} style={{
                                                            padding: '2px 8px', borderRadius: '4px', fontSize: '11px',
                                                            backgroundColor: '#e0e7ff', color: '#4338ca', fontWeight: '500'
                                                        }}>
                                                            {factor}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Exception summary */}
                                            {aiAnalysis.exceptionSummary && (
                                                <div style={{
                                                    padding: '10px 14px', borderRadius: '6px', fontSize: '13px',
                                                    backgroundColor: '#fffbeb', border: '1px solid #fde68a', color: '#92400e'
                                                }}>
                                                    ⚠️ <strong>Exception Summary:</strong> {aiAnalysis.exceptionSummary}
                                                </div>
                                            )}

                                            {/* Similar decisions */}
                                            {aiAnalysis.similarDecisions && (
                                                <div style={{
                                                    padding: '10px 14px', borderRadius: '6px', fontSize: '13px',
                                                    backgroundColor: '#f0f4ff', border: '1px solid #c7d2fe', color: '#4338ca'
                                                }}>
                                                    📋 <strong>Past Decisions:</strong> {aiAnalysis.similarDecisions}
                                                </div>
                                            )}

                                            {/* Action Items */}
                                            {aiAnalysis.actionItems && aiAnalysis.actionItems.length > 0 && (
                                                <div style={{
                                                    padding: '10px 14px', borderRadius: '8px', fontSize: '13px',
                                                    backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534'
                                                }}>
                                                    <strong>📋 Action Items:</strong>
                                                    <ul style={{ margin: '6px 0 0 0', paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                        {aiAnalysis.actionItems.map((item, i) => (
                                                            <li key={i} style={{ fontSize: '13px' }}>{item}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}

                                            <div style={{ fontSize: '11px', color: '#999', fontStyle: 'italic' }}>
                                                AI-generated analysis — approver makes the final decision
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ fontSize: '13px', color: '#999' }}>
                                            AI analysis unavailable for this request.
                                        </div>
                                    )}
                                </div>

                                {/* ── Comments / Correspondence Thread ── */}
                                <div style={{
                                    margin: '16px 0', padding: '16px', borderRadius: '10px',
                                    border: '1px solid #e2e8f0', background: '#fff'
                                }}>
                                    <div style={{ fontWeight: '700', fontSize: '14px', color: '#334155', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        💬 Comments & Correspondence
                                        {comments.length > 0 && <span style={{ fontSize: '12px', fontWeight: '400', color: '#94a3b8' }}>({comments.length})</span>}
                                    </div>

                                    {isLoadingComments ? (
                                        <div style={{ fontSize: '13px', color: '#94a3b8' }}>Loading comments...</div>
                                    ) : (
                                        <>
                                            {/* Comment thread */}
                                            {comments.length > 0 ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px', maxHeight: '300px', overflowY: 'auto' }}>
                                                    {comments.map((c, idx) => (
                                                        <div key={idx} style={{
                                                            padding: '10px 14px', borderRadius: '8px',
                                                            backgroundColor: c.source === 'amazon_approvals' ? '#f0f4ff' : '#f8fafc',
                                                            border: `1px solid ${c.source === 'amazon_approvals' ? '#c7d2fe' : '#e2e8f0'}`,
                                                            fontSize: '13px'
                                                        }}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                                                <span style={{ fontWeight: '600', color: '#334155' }}>
                                                                    {c.source === 'amazon_approvals' ? '🔗' : '🛡️'} {c.alias}
                                                                </span>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                    <span style={{
                                                                        padding: '1px 8px', borderRadius: '8px', fontSize: '10px', fontWeight: '600',
                                                                        backgroundColor: c.source === 'amazon_approvals' ? '#e0e7ff' : '#f0fdf4',
                                                                        color: c.source === 'amazon_approvals' ? '#4338ca' : '#166534'
                                                                    }}>
                                                                        {c.source === 'amazon_approvals' ? 'Approvals.com' : 'PAM Portal'}
                                                                    </span>
                                                                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                                                                        {c.timestamp ? new Date(c.timestamp).toLocaleString() : ''}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <div style={{ color: '#475569', lineHeight: '1.5' }}>{c.message}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '14px' }}>No comments yet.</div>
                                            )}

                                            {/* Add comment form */}
                                            <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '12px' }}>
                                                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                                    <input
                                                        type="text"
                                                        value={commentAlias}
                                                        onChange={(e) => setCommentAlias(e.target.value)}
                                                        placeholder="Your alias"
                                                        style={{ width: '140px', padding: '6px 10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' }}
                                                    />
                                                </div>
                                                <div style={{ display: 'flex', gap: '8px' }}>
                                                    <textarea
                                                        value={newComment}
                                                        onChange={(e) => setNewComment(e.target.value)}
                                                        placeholder="Add a comment (syncs to approvals.amazon.com)..."
                                                        rows={2}
                                                        style={{ flex: 1, padding: '8px 10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px', resize: 'vertical' }}
                                                    />
                                                    <button
                                                        onClick={async () => {
                                                            if (!commentAlias.trim() || !newComment.trim()) return;
                                                            setIsPostingComment(true);
                                                            try {
                                                                const result = await addComment(requestId, commentAlias.trim(), newComment.trim());
                                                                setComments(prev => [...prev, result]);
                                                                setNewComment('');
                                                            } catch (err) {
                                                                alert('Failed to post comment: ' + err.message);
                                                            } finally {
                                                                setIsPostingComment(false);
                                                            }
                                                        }}
                                                        disabled={isPostingComment || !commentAlias.trim() || !newComment.trim()}
                                                        style={{
                                                            padding: '8px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: '600',
                                                            cursor: 'pointer', border: 'none', alignSelf: 'flex-end',
                                                            backgroundColor: (!commentAlias.trim() || !newComment.trim()) ? '#e2e8f0' : '#0073bb',
                                                            color: (!commentAlias.trim() || !newComment.trim()) ? '#94a3b8' : '#fff'
                                                        }}
                                                    >
                                                        {isPostingComment ? '⏳' : '📤 Send'}
                                                    </button>
                                                </div>
                                                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                                                    Comments are synced to approvals.amazon.com as correspondence
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* Provisioning Details — shown for all requests */}
                                {expandedData && (
                                <div style={{ margin: '16px 0', padding: '16px', borderRadius: '10px', border: `1px solid ${(expandedData.status === 'APPROVED' || expandedData.approvalStatus === 'APPROVED') ? '#bbf7d0' : '#e2e8f0'}`, background: (expandedData.status === 'APPROVED' || expandedData.approvalStatus === 'APPROVED') ? '#f0fdf4' : '#f8fafc' }}>
                                    <div style={{ fontWeight: '700', fontSize: '14px', color: (expandedData.status === 'APPROVED' || expandedData.approvalStatus === 'APPROVED') ? '#166534' : '#334155', marginBottom: '12px' }}>
                                        📋 Provisioning Details
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#555', marginBottom: '12px' }}>
                                        {(expandedData.status === 'APPROVED' || expandedData.approvalStatus === 'APPROVED')
                                            ? 'Copy the details below and add users to the permission group on permissions.amazon.com'
                                            : 'Review the provisioning details below before approving'}
                                    </div>
                                    
                                    {/* Users */}
                                    <div style={{ marginBottom: '10px', padding: '10px 14px', borderRadius: '8px', background: '#fff', border: '1px solid #e2e6ea' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                            <span style={{ fontSize: '12px', fontWeight: '600', color: '#666' }}>Users</span>
                                            <button onClick={() => { 
                                                const users = expandedData.userList || expandedData.eligibleUsers?.join(',') || '';
                                                navigator.clipboard.writeText(users.replace(/,/g, '\n'));
                                            }} style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '4px', border: '1px solid #b8d4f0', background: '#f0f8ff', color: '#0073bb', cursor: 'pointer', fontWeight: '600' }}>
                                                📋 Copy
                                            </button>
                                        </div>
                                        <div style={{ fontSize: '13px', color: '#333', wordBreak: 'break-all' }}>
                                            {(expandedData.userList || expandedData.eligibleUsers?.join(', ') || 'N/A')}
                                        </div>
                                    </div>

                                    {/* Dates */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                                        <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#fff', border: '1px solid #e2e6ea' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                                <span style={{ fontSize: '12px', fontWeight: '600', color: '#666' }}>Start Date</span>
                                                <button onClick={() => {
                                                    const today = new Date();
                                                    navigator.clipboard.writeText(`${String(today.getMonth()+1).padStart(2,'0')}/${String(today.getDate()).padStart(2,'0')}/${today.getFullYear()}`);
                                                }} style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '4px', border: '1px solid #b8d4f0', background: '#f0f8ff', color: '#0073bb', cursor: 'pointer', fontWeight: '600' }}>
                                                    📋 Copy
                                                </button>
                                            </div>
                                            <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                                                {(() => { const d = new Date(); return `${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')}/${d.getFullYear()}`; })()}
                                            </div>
                                        </div>
                                        <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#fff', border: '1px solid #e2e6ea' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                                <span style={{ fontSize: '12px', fontWeight: '600', color: '#666' }}>End Date</span>
                                                <button onClick={() => {
                                                    // Approved/listed requests carry a fixed cycle end date (accessEndDate).
                                                    // Only unlisted requests (no stored cycle date) use the rolling window.
                                                    if (expandedData.accessEndDate) {
                                                        navigator.clipboard.writeText(expandedData.accessEndDate);
                                                        return;
                                                    }
                                                    const days = expandedData.riskTypeGroup === 'CRP' || expandedData.riskTypeGroup === 'HRP' ? 120 : 180;
                                                    const end = new Date(); end.setDate(end.getDate() + days);
                                                    navigator.clipboard.writeText(`${String(end.getMonth()+1).padStart(2,'0')}/${String(end.getDate()).padStart(2,'0')}/${end.getFullYear()}`);
                                                }} style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '4px', border: '1px solid #b8d4f0', background: '#f0f8ff', color: '#0073bb', cursor: 'pointer', fontWeight: '600' }}>
                                                    📋 Copy
                                                </button>
                                            </div>
                                            <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                                                {(() => { 
                                                    // Use the stored cycle end date for approved/listed requests.
                                                    if (expandedData.accessEndDate) return expandedData.accessEndDate;
                                                    // Fallback (unlisted): rolling window from today by risk type.
                                                    const days = expandedData.riskTypeGroup === 'CRP' || expandedData.riskTypeGroup === 'HRP' ? 120 : 180;
                                                    const end = new Date(); end.setDate(end.getDate() + days);
                                                    return `${String(end.getMonth()+1).padStart(2,'0')}/${String(end.getDate()).padStart(2,'0')}/${end.getFullYear()} (${days} days)`;
                                                })()}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Reason */}
                                    <div style={{ marginBottom: '10px', padding: '10px 14px', borderRadius: '8px', background: '#fff', border: '1px solid #e2e6ea' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                            <span style={{ fontSize: '12px', fontWeight: '600', color: '#666' }}>Reason</span>
                                            <button onClick={() => navigator.clipboard.writeText(requestId)} style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '4px', border: '1px solid #b8d4f0', background: '#f0f8ff', color: '#0073bb', cursor: 'pointer', fontWeight: '600' }}>
                                                📋 Copy
                                            </button>
                                        </div>
                                        <div style={{ fontSize: '13px', color: '#333' }}>{requestId}</div>
                                    </div>

                                    {/* Permission Links */}
                                    {expandedData.permissionLinks && expandedData.permissionLinks.length > 0 && (
                                        <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#fff', border: '1px solid #e2e6ea' }}>
                                            <span style={{ fontSize: '12px', fontWeight: '600', color: '#666', display: 'block', marginBottom: '6px' }}>Permission Group(s)</span>
                                            {expandedData.permissionLinks.map((pl, i) => (
                                                <div key={i} style={{ marginBottom: '4px' }}>
                                                    <a href={pl.link} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', color: '#0073bb', fontWeight: '600', textDecoration: 'underline' }}>
                                                        {pl.name} ↗
                                                    </a>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                )}
                            </>
                        ) : null}
                    </div>
                </td>
            </tr>
        );
    };

    // ════════════════════════════════════════════
    // RENDER — DASHBOARD TAB
    // ════════════════════════════════════════════
    const [reportFilters, setReportFilters] = useState({
        statuses: ['PENDING', 'APPROVED', 'REJECTED', 'ON_HOLD'],
        dateFrom: '',
        dateTo: '',
        team: '',
        requester: '',
        riskType: '',
        useCase: '',        // '' = all, 'LISTED' (Pre-Approved), 'UNLISTED'
        breachesOnly: false,
        excludeWeekends: false
    });
    const [reportData, setReportData] = useState([]);
    const [isLoadingReport, setIsLoadingReport] = useState(false);

    const loadReportData = async () => {
        setIsLoadingReport(true);
        try {
            const { getAccessToken } = await import('../services/auth');
            const ENDPOINT = 'https://b70682k0f3.execute-api.eu-north-1.amazonaws.com/prod/approver-api';
            const getHeaders = () => {
                const h = { 'Content-Type': 'application/json' };
                const token = getAccessToken();
                if (token) h['Authorization'] = `Bearer ${token}`;
                return h;
            };

            const allRequests = [];
            for (const status of reportFilters.statuses) {
                let lastKey = null;
                let hasMore = true;
                while (hasMore) {
                    const response = await fetch(ENDPOINT, {
                        method: 'POST',
                        headers: getHeaders(),
                        body: JSON.stringify({ operation: 'listRequests', status, lastKey })
                    });
                    const raw = await response.json();
                    const data = typeof raw.body === 'string' ? JSON.parse(raw.body) : raw;
                    const requests = data.requests || [];
                    allRequests.push(...requests.map(r => ({ ...r, reportStatus: status })));
                    lastKey = data.lastKey || null;
                    hasMore = !!lastKey;
                }
            }
            // Scope to ACCESS requests only — exclude bindle/ruleset — to match the top
            // dashboard tiles (which count access requests only). Without this the Report
            // Summary counted more than the tiles (e.g. 44 vs 38 pending).
            // Also exclude test-requester aliases so this report matches the Team TAT
            // panel exactly (both drop the same test requests).
            let filtered = allRequests.filter(r =>
                r.requestType !== 'bindle_access' && r.team !== 'Bindle Access' &&
                r.requestType !== 'ruleset_access' && r.team !== 'Ruleset Access' &&
                !isTestRequest(r)
            );
            // Date filter uses the SAME basis as the Team TAT panel so the two widgets
            // agree: CLOSED requests are filtered on their close date (approvalDate),
            // OPEN requests on their created date. Using createdAt for everything caused
            // a closed-on-9/29 request created on 9/28 to appear here but not in the panel.
            const reportDateBasis = (r) => {
                const st = (r.reportStatus || r.status || '').toUpperCase();
                if (st === 'APPROVED' || st === 'REJECTED') {
                    return r.approvalDate || r.approvalCompletedAt || r.createdAt || '';
                }
                return r.createdAt || '';
            };
            if (reportFilters.dateFrom) {
                filtered = filtered.filter(r => reportDateBasis(r) >= reportFilters.dateFrom);
            }
            if (reportFilters.dateTo) {
                filtered = filtered.filter(r => reportDateBasis(r) <= reportFilters.dateTo + 'T23:59:59');
            }
            if (reportFilters.team) {
                filtered = filtered.filter(r => (r.team || '').toLowerCase().includes(reportFilters.team.toLowerCase()));
            }
            if (reportFilters.requester) {
                filtered = filtered.filter(r => (r.requesterAlias || '').toLowerCase().includes(reportFilters.requester.toLowerCase()));
            }
            if (reportFilters.riskType) {
                filtered = filtered.filter(r => (r.riskTypeGroup || '').toUpperCase() === reportFilters.riskType.toUpperCase());
            }
            if (reportFilters.useCase) {
                filtered = filtered.filter(r => {
                    const uc = classifyUseCase(r); // PREAPPROVED | UNLISTED | UNKNOWN
                    return reportFilters.useCase === 'LISTED' ? uc === 'PREAPPROVED' : uc === 'UNLISTED';
                });
            }
            if (reportFilters.breachesOnly) {
                filtered = filtered.filter(r => {
                    // Judge breach on business-hour team time (weekends excluded).
                    if (r.teamTatWithinSlaBiz != null) {
                        return String(r.teamTatWithinSlaBiz).toUpperCase() === 'NO';
                    }
                    const tat = computeTeamTat(r);
                    return tat && !tat.withinSla;
                });
            }
            setReportData(filtered);
        } catch (err) {
            setError('Failed to load report data: ' + err.message);
        } finally {
            setIsLoadingReport(false);
        }
    };

    // ---------------------------------------------------------------------
    // Deep-dive report helpers — parse the stored per-stage breakdown into
    // fixed, Excel-pivot-friendly columns for SLA miss analysis.
    // ---------------------------------------------------------------------
    // Deep-link bases (confirmed working — same links the SLA Slack report uses).
    //   PAM request  -> approver app /?request=<requestId>  (ApproverPage opens the request)
    //   Approval     -> approvals.amazon.com/approval/<approvalId>
    const PAM_REQUEST_BASE = 'https://pam-approver.tse.amazon.dev/?request=';
    const APPROVAL_BASE = 'https://approvals.amazon.com/approval/';

    // Route-name → workflow-stage mapping (matches pam-approval-sync TEAM_TAT_ROUTES
    // and the stage order defined by approvals.amazon.com).
    //   HRP/CRP (3 stages): Stage 1 "SPS Permissions Team" = Security Review →
    //     Stage 2 Leadership → Stage 3 "Access Management Team" = Provisioning.
    //   LRP/MRP (2 stages): Stage 1 Leadership → Stage 2 combined
    //     "CTPS-Permissions-Security Review" = Provisioning (review + grant folded).
    //   Listed (1 stage): "Access Management Team" = the whole team step (Provisioning).
    const SECURITY_REVIEW_ROUTES = new Set(['sps permissions team']);
    const PROVISIONING_ROUTES = new Set(['access management team', 'sps team', 'ctps-permissions-security review']);

    // Parse teamTatStageBreakdown ("route|TEAM|1.50 ; route|OTHER|12.10 ; ...")
    // into workflow-stage buckets BY ROUTE NAME. Returns hours (null when absent):
    //   securityReview — Stage 1 security review (HRP/CRP only)
    //   provisioning   — provisioning / grant stage (the CARDS+Pascal candidate)
    //   leadership     — sum of all OTHER (leadership) stages
    //   teamActive     — securityReview + provisioning (active team decision time)
    //   slowestStage   — label of the longest single stage
    const parseStageBreakdown = (r) => {
        const empty = { securityReview: null, provisioning: null, leadership: null, teamActive: null, slowestStage: '' };
        // Prefer the business-hour breakdown (weekends excluded); fall back to raw
        // for any record not yet backfilled.
        const raw = r.teamTatStageBreakdownBiz || r.teamTatStageBreakdown;
        if (!raw || typeof raw !== 'string') return empty;
        const parts = raw.split(';').map(p => p.trim()).filter(Boolean);
        let securityReview = null, provisioning = null, leadership = 0, sawLeadership = false, teamActive = 0, sawTeam = false;
        const all = []; // { label, hours } for slowest-stage detection
        parts.forEach(p => {
            const seg = p.split('|').map(x => x.trim());
            if (seg.length < 3) return;
            const [name, kind, hStr] = seg;
            const hours = hStr === '?' ? null : Number(hStr);
            const valid = hours != null && !Number.isNaN(hours);
            const rt = name.toLowerCase();
            if (kind === 'TEAM') {
                if (valid) { teamActive += hours; sawTeam = true; }
                if (SECURITY_REVIEW_ROUTES.has(rt)) {
                    securityReview = (securityReview || 0) + (valid ? hours : 0);
                    if (valid) all.push({ label: 'Stage 1 - Security Review', hours });
                } else {
                    // Provisioning routes + any unknown team route.
                    provisioning = (provisioning || 0) + (valid ? hours : 0);
                    if (valid) all.push({ label: 'Provisioning', hours });
                }
            } else {
                if (valid) { leadership += hours; sawLeadership = true; all.push({ label: 'Leadership Review', hours }); }
            }
        });
        const round2 = (v) => (v == null ? null : Number(v.toFixed(2)));
        let slowestStage = '';
        if (all.length) {
            const max = all.reduce((a, b) => (b.hours > a.hours ? b : a));
            slowestStage = `${max.label} (${max.hours.toFixed(2)}h)`;
        }
        return {
            securityReview: round2(securityReview),
            provisioning: round2(provisioning),
            leadership: sawLeadership ? round2(leadership) : null,
            teamActive: sawTeam ? round2(teamActive) : null,
            slowestStage
        };
    };

    const downloadCSV = () => {
        if (reportData.length === 0) return;
        const headers = [
            'Request ID', 'PAM Link', 'Approval Link', 'Approval ID', 'Tenant',
            'Requester', 'Actioned By', 'Listed/Unlisted', 'Risk Type', 'Priority',
            'Team', 'Function', 'Org', 'Permissions', 'Users', 'Status',
            'Created Date', 'Created Time', 'Closed Date', 'Closed Time',
            'Total TAT (business h)', 'Team TAT (business h)', 'Leadership Review (business h)',
            'Stage 1 - Security Review (business h)', 'Stage 3 - Provisioning (business h)',
            'Slowest Stage', 'SLA (h)', 'SLA Breached', 'TAT Source',
            'Stage Breakdown (raw)'
        ];
        const rows = reportData.map(r => {
            const useCase = classifyUseCase(r); // PREAPPROVED | UNLISTED | UNKNOWN
            const listedLabel = useCase === 'PREAPPROVED' ? 'Listed' : useCase === 'UNLISTED' ? 'Unlisted' : '';
            const tat = computeTeamTat(r); // used only for SLA hours + source flag
            const stages = parseStageBreakdown(r); // reads business-hour breakdown (weekends excluded)
            const createdIso = r.createdAt || '';
            const closedIso = r.approvalDate || r.approvalCompletedAt || '';
            const [createdDate, createdTime] = createdIso ? createdIso.split('T') : ['', ''];
            const [closedDate, closedTimeRaw] = closedIso ? closedIso.split('T') : ['', ''];
            const closedTime = (closedTimeRaw || '').replace('Z', '').split('.')[0];
            // ── All TAT values are business hours (Mon-Fri, weekends excluded) ──
            // Stage figures come from the business-hour breakdown.
            const securityReview = stages.securityReview; // Stage 1 (HRP/CRP)
            const provisioning = stages.provisioning;     // Provisioning stage
            const leadReview = stages.leadership;          // Leadership Review (business hours)
            // Team TAT (business) = securityReview + provisioning.
            const teamTat = stages.teamActive;
            // Total TAT (business) = backend-stored created→closed business hours;
            // fall back to Team + Leadership when the stored value is absent.
            let totalTat = r.teamTatTotalBusinessHours != null ? Number(r.teamTatTotalBusinessHours) : null;
            if (totalTat == null && (teamTat != null || leadReview != null)) {
                totalTat = (teamTat || 0) + (leadReview || 0);
            }
            const slaHours = tat ? tat.sla : (useCase === 'PREAPPROVED' ? 24 : useCase === 'UNLISTED' ? 48 : '');
            // SLA breach judged on business-hour team time.
            const withinBiz = r.teamTatWithinSlaBiz != null
                ? String(r.teamTatWithinSlaBiz).toUpperCase() === 'YES'
                : (teamTat != null && slaHours !== '' ? teamTat <= slaHours : null);
            const breached = withinBiz == null ? '' : (withinBiz ? 'No' : 'Yes');
            const source = tat ? (tat.exactSource ? 'Exact' : tat.approx ? 'Approximate' : 'CreatedAt-based') : '';
            const num = (v) => (v == null || Number.isNaN(v) ? '' : Number(v).toFixed(2));
            return [
                r.requestId || '',
                r.requestId ? PAM_REQUEST_BASE + r.requestId : '',
                r.approvalId ? APPROVAL_BASE + r.approvalId : '',
                r.approvalId || '',
                r.tenant || 'T178',
                r.requesterAlias || '',
                r.approvedBy || '',
                listedLabel,
                r.riskTypeGroup || '',
                r.priority || '',
                r.team || '',
                r.functionDropdown || '',
                r.orgName || '',
                (r.selectedPermissionNames || []).join('; '),
                r.totalUsers || '',
                r.reportStatus || r.status || '',
                createdDate,
                (createdTime || '').replace('Z', '').split('.')[0],
                closedDate,
                closedTime,
                num(totalTat),
                num(teamTat),
                num(leadReview),
                num(securityReview),
                num(provisioning),
                stages.slowestStage,
                slaHours,
                breached,
                source,
                r.teamTatStageBreakdownBiz || r.teamTatStageBreakdown || ''
            ];
        });
        const csvContent = [headers, ...rows].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `PAM-Report-${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    // ---------------------------------------------------------------------
    // Team TAT (Turn-Around Time) helpers — T178 launch metrics
    // ---------------------------------------------------------------------
    // We measure only the time OUR TEAM (Security + Permissions buckets) took to
    // act, NOT the leadership approval wait, because leaders can take any amount
    // of time on Unlisted requests.
    //
    //  - Pre-Approved use cases have NO leadership stage, so team TAT is exact:
    //        approvalDate - createdAt
    //  - Unlisted use cases go through a leadership (L8) stage. We can only cleanly
    //    isolate the POST-leadership team time: approvalDate - l8ApprovedAt.
    //    The pre-leadership Security-bucket portion isn't separable from the
    //    leader wait with today's stored data, so Unlisted TAT is flagged approx.

    // TSE launch date — only count requests closed on/after this.
    const T178_LAUNCH_DATE = '2026-08-20';
    // Test requesters to exclude from metrics.
    const TAT_TEST_ALIASES = ['abhimige', 'shasurab'];
    // SLA thresholds (hours).
    const TAT_SLA_HOURS = { PREAPPROVED: 24, UNLISTED: 48 };

    // Template IDs, validated 100% on 84 closed records.
    const PREAPPROVED_TEMPLATE_IDS = new Set(['221400', '1013154', '1013153']);
    const UNLISTED_TEMPLATE_IDS = new Set(['225064', '225065', '225066', '225068', '212425', '1013155', '642154']);

    // Classify a request as 'PREAPPROVED' | 'UNLISTED' | 'UNKNOWN'.
    // Primary signal: approvalTemplateId. Fallback: presence of leadership (L8) stage.
    const classifyUseCase = (req) => {
        const tpl = req.approvalTemplateId != null ? String(req.approvalTemplateId).trim() : '';
        if (PREAPPROVED_TEMPLATE_IDS.has(tpl)) return 'PREAPPROVED';
        if (UNLISTED_TEMPLATE_IDS.has(tpl)) return 'UNLISTED';
        // Fallback — an L8 leadership approval means this went the Unlisted path.
        if (req.l8ApprovedAt || req.l8ApprovalStatus) return 'UNLISTED';
        return 'UNKNOWN';
    };

    // Is this a test request we should exclude from metrics?
    const isTestRequest = (req) => {
        const alias = (req.requesterAlias || req.requester || '').toLowerCase().trim();
        return TAT_TEST_ALIASES.includes(alias);
    };

    // Parse an ISO timestamp to millis; returns null when missing/invalid.
    // IMPORTANT: our records store two timestamp shapes — createdAt has NO
    // timezone marker (e.g. "2026-09-28T09:37:02.542631", actually UTC) while
    // approvalDate/l8ApprovedAt end in "Z". Date.parse() treats a marker-less
    // string as LOCAL time, which on an IST machine mis-shifts createdAt by
    // 5:30 and inflates every Total TAT. We normalise: if the string has no
    // "Z" and no ±hh:mm offset, treat it as UTC (append "Z"). Matches the
    // backend _parse_iso() which also assumes UTC for naive timestamps.
    const parseTs = (v) => {
        if (!v) return null;
        let s = String(v).trim();
        const hasTz = /[zZ]$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s);
        if (!hasTz && s.includes('T')) s += 'Z';
        const t = Date.parse(s);
        return Number.isNaN(t) ? null : t;
    };

    // Compute team-only TAT (in hours) for a closed request.
    // Returns { useCase, hours, approx, sla, withinSla, basis, leaderWaitHours?, breakdown? }
    // or null if not computable.
    //
    // Preference order:
    //   1. teamTatExactHours (backfilled from approvals.amazon.com per-stage timestamps) —
    //      EXACT team-only time summing only our team's stages, leader wait excluded. No ≈.
    //   2. Fallback (older/unbackfilled records): Pre-Approved = approvalDate−createdAt (exact);
    //      Unlisted = approvalDate−l8ApprovedAt (post-leadership only, approximate ≈).
    const computeTeamTat = (req) => {
        const useCase = classifyUseCase(req);
        if (useCase === 'UNKNOWN') return null;
        const slaKey = useCase; // PREAPPROVED | UNLISTED
        const sla = TAT_SLA_HOURS[slaKey];

        // 1) Exact backfilled value wins. Prefer the BUSINESS-HOUR value (weekends
        //    excluded) so the panel matches the report CSV exactly — same source,
        //    same weekend definition, no separate approximation.
        const biz = req.teamTatBusinessHours;
        if (biz !== undefined && biz !== null && biz !== '') {
            const hours = Number(biz);
            if (!Number.isNaN(hours)) {
                const withinSla = req.teamTatWithinSlaBiz != null
                    ? String(req.teamTatWithinSlaBiz).toUpperCase() === 'YES'
                    : hours <= sla;
                const leaderWaitHours = req.teamTatLeaderBusinessHours != null
                    ? Number(req.teamTatLeaderBusinessHours)
                    : (req.teamTatLeaderWaitHours != null ? Number(req.teamTatLeaderWaitHours) : null);
                return {
                    useCase, hours, approx: false, sla, withinSla,
                    basis: 'Exact team-only business hours (weekends excluded) from approvals.amazon.com stage timestamps',
                    leaderWaitHours,
                    breakdown: req.teamTatStageBreakdownBiz || req.teamTatStageBreakdown || null,
                    exactSource: true,
                    businessHours: true,
                };
            }
        }

        // 1b) Fall back to the raw exact value if business-hours not yet backfilled.
        const exact = req.teamTatExactHours;
        if (exact !== undefined && exact !== null && exact !== '') {
            const hours = Number(exact);
            if (!Number.isNaN(hours)) {
                const leaderWaitHours = req.teamTatLeaderWaitHours != null ? Number(req.teamTatLeaderWaitHours) : null;
                return {
                    useCase, hours, approx: false, sla, withinSla: hours <= sla,
                    basis: 'Exact team-only time from approvals.amazon.com stage timestamps (leader wait excluded)',
                    leaderWaitHours,
                    breakdown: req.teamTatStageBreakdown || null,
                    exactSource: true,
                    businessHours: false,
                };
            }
        }

        // 2) Fallback for records without a backfilled value.
        const closedMs = parseTs(req.approvalDate) || parseTs(req.approvalCompletedAt);
        if (!closedMs) return null;

        let startMs, approx, basis;
        if (useCase === 'PREAPPROVED') {
            startMs = parseTs(req.createdAt);
            approx = false;
            basis = 'approvalDate − createdAt (exact, no leadership stage)';
        } else {
            startMs = parseTs(req.l8ApprovedAt);
            approx = true; // pre-leadership Security-bucket portion not separable without backfill
            basis = 'approvalDate − l8ApprovedAt (post-leadership team time; pre-leadership portion not included)';
        }
        if (!startMs || closedMs < startMs) return null;

        const hours = (closedMs - startMs) / 3600000;
        return { useCase, hours, approx, sla, withinSla: hours <= sla, basis, exactSource: false };
    };

    // Build the full team-TAT dataset from report/closed data: filter to closed,
    // non-test, launched-since requests and attach a computed TAT to each.
    const buildTatDataset = (requests) => {
        const launchMs = Date.parse(T178_LAUNCH_DATE + 'T00:00:00Z');
        const rows = [];
        (requests || []).forEach((req) => {
            if (isTestRequest(req)) return;
            const status = (req.reportStatus || req.status || req.approvalStatus || '').toUpperCase();
            const isClosed = status === 'APPROVED' || status === 'REJECTED';
            if (!isClosed) return;
            const closedMs = parseTs(req.approvalDate) || parseTs(req.approvalCompletedAt);
            if (!closedMs || closedMs < launchMs) return;
            const tat = computeTeamTat(req);
            if (!tat) return;
            rows.push({ req, ...tat });
        });
        return rows;
    };

    // Summarise SLA compliance per use case.
    const summariseTatSla = (rows) => {
        const summary = {
            PREAPPROVED: { total: 0, within: 0, breached: 0, sla: TAT_SLA_HOURS.PREAPPROVED },
            UNLISTED: { total: 0, within: 0, breached: 0, sla: TAT_SLA_HOURS.UNLISTED },
        };
        rows.forEach((r) => {
            const s = summary[r.useCase];
            if (!s) return;
            s.total++;
            if (r.withinSla) s.within++; else s.breached++;
        });
        return summary;
    };

    // Apply the Team TAT filter bar to a computed dataset. Rejected are excluded from
    // SLA entirely (per requirement). Hours are already business hours (weekends excluded).
    const applyTatFilters = (rows, filters) => {
        const fromMs = filters.dateFrom ? Date.parse(filters.dateFrom + 'T00:00:00Z') : null;
        const toMs = filters.dateTo ? Date.parse(filters.dateTo + 'T23:59:59Z') : null;
        const team = (filters.team || '').toLowerCase().trim();
        const requester = (filters.requester || '').toLowerCase().trim();
        return rows.map((r) => {
            // Hours are already business hours (weekends excluded) from the backend,
            // matching the report CSV exactly — no separate approximation here.
            return { ...r, weekendAdjusted: !!r.businessHours };
        }).filter((r) => {
            const status = (r.req.reportStatus || r.req.status || r.req.approvalStatus || '').toUpperCase();
            if (status !== 'APPROVED') return false; // Rejected excluded from SLA
            const closedMs = parseTs(r.req.approvalDate) || parseTs(r.req.approvalCompletedAt);
            if (fromMs && (!closedMs || closedMs < fromMs)) return false;
            if (toMs && (!closedMs || closedMs > toMs)) return false;
            if (filters.useCase !== 'ALL' && r.useCase !== filters.useCase) return false;
            if (team) {
                const t = ((r.req.team || r.req.teamValue || '') + '').toLowerCase();
                if (!t.includes(team)) return false;
            }
            if (requester) {
                const rq = ((r.req.requesterAlias || r.req.requester || '') + '').toLowerCase();
                if (!rq.includes(requester)) return false;
            }
            if (filters.breachesOnly && r.withinSla) return false;
            return true;
        });
    };

    // Median of an array of numbers.
    const median = (arr) => {
        if (!arr.length) return 0;
        const s = arr.slice().sort((a, b) => a - b);
        const m = Math.floor(s.length / 2);
        return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
    };

    // Distribution buckets for a set of rows.
    const tatDistribution = (rows) => {
        const buckets = [
            { label: '0–8h', min: 0, max: 8, count: 0 },
            { label: '8–24h', min: 8, max: 24, count: 0 },
            { label: '24–48h', min: 24, max: 48, count: 0 },
            { label: '>48h', min: 48, max: Infinity, count: 0 },
        ];
        rows.forEach((r) => {
            const b = buckets.find((x) => r.hours >= x.min && r.hours < x.max);
            if (b) b.count++;
        });
        return buckets;
    };

    // Download the Team TAT dataset as CSV.
    const downloadTatCsv = (rows) => {
        const headers = [
            'Request ID', 'Requester', 'Use Case', 'Team TAT (hours)', 'Team TAT (days)',
            'Leader Wait (hours)', 'SLA (hours)', 'Within SLA', 'Exact (source)', 'Closed Date',
            'Stage Breakdown', 'TAT Basis'
        ];
        const esc = (v) => {
            const s = (v == null ? '' : String(v));
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const lines = [headers.join(',')];
        rows.slice().sort((a, b) => b.hours - a.hours).forEach((r) => {
            lines.push([
                r.req.requestId || '',
                r.req.requesterAlias || r.req.requester || '',
                r.useCase === 'PREAPPROVED' ? 'Pre-Approved' : 'Unlisted',
                r.hours.toFixed(2),
                (r.hours / 24).toFixed(2),
                r.leaderWaitHours != null ? r.leaderWaitHours.toFixed(2) : '',
                r.sla,
                r.withinSla ? 'YES' : 'NO',
                r.exactSource ? 'YES (from approvals stage timestamps)' : (r.approx ? 'NO (approximate)' : 'NO (createdAt-based)'),
                (r.req.approvalDate || r.req.approvalCompletedAt || '').split('T')[0],
                r.breakdown || '',
                r.basis
            ].map(esc).join(','));
        });
        const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Team-TAT-${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const renderDashboard = () => {
        if (!dashboard) return null;

        // Access-request pending count excludes Bindle/Ruleset (they have their own
        // tabs/badges). The server dashboard.pending counts all request types, so we
        // subtract the bindle/ruleset pending totals to match the Access Requests list.
        const accessPending = Math.max(0, (dashboard.pending || 0) - bindlePendingCount - rulesetPendingCount);

        return (
            <div className="approver-dashboard">
                {/* Compact Stats Row */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', marginBottom: '24px' }}>
                    {[
                        { label: 'Pending', value: accessPending, icon: '⏳', color: '#f59e0b', bg: '#fffbeb', border: '#fde68a' },
                        { label: 'On Hold', value: dashboard.onHold || 0, icon: '⏸', color: '#6b7280', bg: '#f3f4f6', border: '#e5e7eb' },
                        { label: 'Approved', value: dashboard.approved, icon: '✅', color: '#166534', bg: '#dcfce7', border: '#bbf7d0' },
                        { label: 'Rejected', value: dashboard.rejected, icon: '❌', color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
                        { label: 'Total', value: dashboard.total, icon: '📊', color: '#0073bb', bg: '#f0f8ff', border: '#b8d4f0' }
                    ].map(stat => (
                        <div key={stat.label} style={{ padding: '14px 16px', borderRadius: '10px', background: stat.bg, border: `1px solid ${stat.border}`, textAlign: 'center' }}>
                            <div style={{ fontSize: '24px', fontWeight: '700', color: stat.color }}>{stat.value}</div>
                            <div style={{ fontSize: '11px', fontWeight: '600', color: stat.color, marginTop: '2px' }}>{stat.icon} {stat.label}</div>
                        </div>
                    ))}
                </div>

                {/* ═══ Team TAT (Turn-Around Time) — T178 launch metrics ═══ */}
                {(() => {
                    const allRows = buildTatDataset(tatClosed || []);
                    const rows = applyTatFilters(allRows, tatFilters);
                    const fmtHrs = (h) => h >= 24 ? `${(h / 24).toFixed(1)}d (${h.toFixed(1)}h)` : `${h.toFixed(1)}h`;
                    const pct = (n, d) => d ? Math.round((n / d) * 100) : 0;

                    // KPI computations (filtered, APPROVED only)
                    const pre = rows.filter(r => r.useCase === 'PREAPPROVED');
                    const unl = rows.filter(r => r.useCase === 'UNLISTED');
                    const withinCount = (arr) => arr.filter(r => r.withinSla).length;
                    const overallWithin = withinCount(rows);
                    const totalTeamH = rows.reduce((a, r) => a + r.hours, 0);
                    const totalLeaderH = rows.reduce((a, r) => a + (r.leaderWaitHours || 0), 0);
                    const teamSharePct = pct(totalTeamH, totalTeamH + totalLeaderH);
                    const dist = tatDistribution(rows);
                    const distMax = Math.max(...dist.map(b => b.count), 1);

                    const kpi = [
                        { label: 'Pre-Approved', sub: '≤ 24h', within: withinCount(pre), total: pre.length, med: median(pre.map(r => r.hours)), color: '#0073bb', bg: '#f0f8ff', border: '#b8d4f0' },
                        { label: 'Unlisted', sub: '≤ 48h', within: withinCount(unl), total: unl.length, med: median(unl.map(r => r.hours)), color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe' },
                        { label: 'Overall SLA', sub: 'all closed', within: overallWithin, total: rows.length, med: median(rows.map(r => r.hours)), color: '#166534', bg: '#dcfce7', border: '#bbf7d0' },
                    ];

                    const inputStyle = { width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '12px' };
                    const labelStyle = { fontSize: '10px', fontWeight: 600, color: '#64748b', display: 'block', marginBottom: '3px' };
                    const setF = (patch) => setTatFilters(prev => ({ ...prev, ...patch }));
                    const quickRange = (days) => {
                        const to = new Date();
                        const from = days === null ? new Date(T178_LAUNCH_DATE + 'T00:00:00Z') : new Date(Date.now() - days * 86400000);
                        setF({ dateFrom: from.toISOString().split('T')[0], dateTo: to.toISOString().split('T')[0] });
                    };

                    return (
                        <div style={{ padding: '20px', borderRadius: '10px', background: '#fff', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                <div style={{ fontSize: '15px', fontWeight: '700', color: '#111' }}>⏱ Team TAT — Time Our Team Took to Act</div>
                                {rows.length > 0 && (
                                    <button onClick={() => downloadTatCsv(rows)}
                                        style={{ fontSize: '12px', fontWeight: 600, color: '#166534', background: '#dcfce7', border: '1px solid #bbf7d0', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer' }}>
                                        ⬇ Download CSV ({rows.length} rows)
                                    </button>
                                )}
                            </div>
                            <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '14px' }}>
                                Exact team-only business hours from approvals.amazon.com stage timestamps — weekends excluded (Mon-Fri),
                                leadership wait excluded. Approved requests only (rejected are not counted toward SLA). T178 launch {T178_LAUNCH_DATE}.
                                Matches the Report Builder CSV.
                            </div>

                            {/* FILTER BAR */}
                            <div style={{ padding: '12px 14px', borderRadius: '8px', background: '#f8fafc', border: '1px solid #e2e8f0', marginBottom: '14px' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '10px' }}>
                                    <div><label style={labelStyle}>From</label><input type="date" value={tatFilters.dateFrom} onChange={e => setF({ dateFrom: e.target.value })} style={inputStyle} /></div>
                                    <div><label style={labelStyle}>To</label><input type="date" value={tatFilters.dateTo} onChange={e => setF({ dateTo: e.target.value })} style={inputStyle} /></div>
                                    <div><label style={labelStyle}>Use Case</label>
                                        <select value={tatFilters.useCase} onChange={e => setF({ useCase: e.target.value })} style={inputStyle}>
                                            <option value="ALL">All</option><option value="PREAPPROVED">Pre-Approved</option><option value="UNLISTED">Unlisted</option>
                                        </select>
                                    </div>
                                    <div><label style={labelStyle}>Team</label><input type="text" value={tatFilters.team} onChange={e => setF({ team: e.target.value })} placeholder="All teams" style={inputStyle} /></div>
                                    <div><label style={labelStyle}>Requester</label><input type="text" value={tatFilters.requester} onChange={e => setF({ requester: e.target.value })} placeholder="All" style={inputStyle} /></div>
                                </div>
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                    <span style={{ fontSize: '10px', color: '#94a3b8' }}>Quick:</span>
                                    {[['Last 7d', 7], ['Last 30d', 30], ['Since launch', null]].map(([lbl, d]) => (
                                        <button key={lbl} onClick={() => quickRange(d)} style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', background: '#fff', border: '1px solid #cbd5e1', cursor: 'pointer' }}>{lbl}</button>
                                    ))}
                                    <label style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', marginLeft: '8px' }}>
                                        <input type="checkbox" checked={tatFilters.breachesOnly} onChange={e => setF({ breachesOnly: e.target.checked })} /> ⚠ Breaches only
                                    </label>
                                    <button onClick={() => setTatFilters({ dateFrom: '2026-08-20', dateTo: '', useCase: 'ALL', team: '', requester: '', excludeWeekends: false, breachesOnly: false })}
                                        style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', background: '#fff', border: '1px solid #cbd5e1', cursor: 'pointer', marginLeft: 'auto' }}>Reset</button>
                                </div>
                            </div>

                            {tatClosed === null ? (
                                <div style={{ fontSize: '13px', color: '#666' }}>Loading TAT metrics…</div>
                            ) : rows.length === 0 ? (
                                <div style={{ fontSize: '13px', color: '#666' }}>No approved requests match these filters.</div>
                            ) : (
                                <>
                                    {/* KPI STRIP */}
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '12px', marginBottom: '14px' }}>
                                        {kpi.map(c => (
                                            <div key={c.label} style={{ padding: '12px 14px', borderRadius: '10px', background: c.bg, border: `1px solid ${c.border}` }}>
                                                <div style={{ fontSize: '12px', fontWeight: 700, color: c.color }}>{c.label} <span style={{ fontSize: '10px', color: '#94a3b8' }}>{c.sub}</span></div>
                                                <div style={{ fontSize: '22px', fontWeight: 700, color: c.color, marginTop: '4px' }}>
                                                    {c.within}<span style={{ fontSize: '13px', color: '#94a3b8' }}>/{c.total}</span>
                                                    <span style={{ fontSize: '12px', color: '#22c55e', marginLeft: '6px' }}>{pct(c.within, c.total)}%</span>
                                                </div>
                                                <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px' }}>median {c.med.toFixed(1)}h · {c.total - c.within} breached</div>
                                            </div>
                                        ))}
                                        <div style={{ padding: '12px 14px', borderRadius: '10px', background: '#fffbeb', border: '1px solid #fde68a' }}>
                                            <div style={{ fontSize: '12px', fontWeight: 700, color: '#b45309' }}>Team vs Leader</div>
                                            <div style={{ fontSize: '22px', fontWeight: 700, color: '#b45309', marginTop: '4px' }}>{teamSharePct}%<span style={{ fontSize: '12px', color: '#94a3b8' }}> team</span></div>
                                            <div style={{ marginTop: '4px', height: '8px', borderRadius: '4px', background: '#fde68a', overflow: 'hidden' }}>
                                                <div style={{ width: `${teamSharePct}%`, height: '100%', background: '#f59e0b' }}></div>
                                            </div>
                                            <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px' }}>leader {100 - teamSharePct}% of total time</div>
                                        </div>
                                    </div>

                                    {/* DISTRIBUTION */}
                                    <div style={{ padding: '12px 14px', borderRadius: '8px', background: '#fff', border: '1px solid #e2e8f0', marginBottom: '14px' }}>
                                        <div style={{ fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '8px' }}>Team TAT distribution</div>
                                        {dist.map(b => (
                                            <div key={b.label} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                                <span style={{ fontSize: '11px', color: '#64748b', width: '54px' }}>{b.label}</span>
                                                <div style={{ flex: 1, background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden', height: '16px', cursor: b.label === '>48h' ? 'pointer' : 'default' }}
                                                    onClick={() => { if (b.label === '>48h') setF({ breachesOnly: true }); }}>
                                                    <div style={{ width: `${(b.count / distMax) * 100}%`, height: '100%', background: b.label === '>48h' ? '#ef4444' : '#0073bb', minWidth: b.count ? '2px' : '0' }}></div>
                                                </div>
                                                <span style={{ fontSize: '11px', fontWeight: 600, color: '#334155', width: '28px', textAlign: 'right' }}>{b.count}</span>
                                            </div>
                                        ))}
                                    </div>

                                    {/* REQUESTS TABLE (expandable rows) */}
                                    <div style={{ overflowX: 'auto', maxHeight: '400px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                            <thead style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
                                                <tr style={{ textAlign: 'left', color: '#475569' }}>
                                                    <th style={{ padding: '8px 10px', borderBottom: '1px solid #e2e8f0', width: '20px' }}></th>
                                                    <th style={{ padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>Request</th>
                                                    <th style={{ padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>Requester</th>
                                                    <th style={{ padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>Use Case</th>
                                                    <th style={{ padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>Team TAT</th>
                                                    <th style={{ padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>SLA</th>
                                                    <th style={{ padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>Leader</th>
                                                    <th style={{ padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>Closed</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {rows.slice().sort((a, b) => b.hours - a.hours).map((r, i) => {
                                                    const rid = r.req.requestId || String(i);
                                                    const open = !!tatExpanded[rid];
                                                    return (
                                                        <React.Fragment key={rid}>
                                                            <tr style={{ background: r.withinSla ? '#fff' : '#fef2f2' }}>
                                                                <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9', cursor: r.breakdown ? 'pointer' : 'default', color: '#94a3b8' }}
                                                                    onClick={() => r.breakdown && setTatExpanded(prev => ({ ...prev, [rid]: !prev[rid] }))}>
                                                                    {r.breakdown ? (open ? '▾' : '▸') : ''}
                                                                </td>
                                                                <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9', fontFamily: 'monospace' }}>{r.req.requestId || '—'}</td>
                                                                <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9' }}>{r.req.requesterAlias || r.req.requester || '—'}</td>
                                                                <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9' }}>
                                                                    {r.useCase === 'PREAPPROVED' ? 'Pre-Approved' : 'Unlisted'}
                                                                    {r.approx && <span title={r.basis} style={{ color: '#f59e0b', marginLeft: '4px' }}>≈</span>}
                                                                    {r.weekendAdjusted && <span title="weekend hours excluded" style={{ color: '#0ea5e9', marginLeft: '4px' }}>⌂</span>}
                                                                </td>
                                                                <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9', fontWeight: 600 }}>{fmtHrs(r.hours)}</td>
                                                                <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9', color: r.withinSla ? '#22c55e' : '#ef4444', fontWeight: 600 }}>
                                                                    {r.withinSla ? `✅ ≤${r.sla}h` : `❌ >${r.sla}h`}
                                                                </td>
                                                                <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9', color: '#64748b' }}>{r.leaderWaitHours != null ? `${r.leaderWaitHours.toFixed(1)}h` : '—'}</td>
                                                                <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9', color: '#64748b' }}>{(r.req.approvalDate || r.req.approvalCompletedAt || '').split('T')[0]}</td>
                                                            </tr>
                                                            {open && r.breakdown && (
                                                                <tr>
                                                                    <td></td>
                                                                    <td colSpan={7} style={{ padding: '6px 10px 10px', borderBottom: '1px solid #f1f5f9', background: '#fafafa', fontSize: '11px', color: '#475569' }}>
                                                                        <span style={{ fontWeight: 600 }}>Stage breakdown: </span>
                                                                        {r.breakdown.split(' ; ').map((seg, si) => {
                                                                            const [route, owner, hrs] = seg.split('|');
                                                                            return <span key={si} style={{ marginRight: '10px', color: owner === 'TEAM' ? '#166534' : '#94a3b8' }}>{route} <b>({owner} {hrs}h)</b></span>;
                                                                        })}
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '8px' }}>
                                        Rows highlighted red breached SLA. ▸ expand a row for the per-stage breakdown (TEAM stages counted, others excluded).
                                        ≈ = approximate (un-backfilled). ⌂ = weekend hours excluded.
                                    </div>
                                </>
                            )}
                        </div>
                    );
                })()}

                {/* Report Builder */}
                <div style={{ padding: '20px', borderRadius: '10px', background: '#fff', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                    <div style={{ fontSize: '15px', fontWeight: '700', color: '#111', marginBottom: '16px' }}>📋 Report Builder</div>
                    
                    {/* Status filters */}
                    <div style={{ marginBottom: '14px' }}>
                        <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '6px' }}>Status</label>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {['PENDING', 'ON_HOLD', 'APPROVED', 'REJECTED'].map(s => (
                                <label key={s} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', cursor: 'pointer', padding: '4px 10px', borderRadius: '6px', background: reportFilters.statuses.includes(s) ? '#e0f2fe' : '#f8f9fa', border: `1px solid ${reportFilters.statuses.includes(s) ? '#0073bb' : '#e2e6ea'}` }}>
                                    <input type="checkbox" checked={reportFilters.statuses.includes(s)}
                                        onChange={(e) => {
                                            if (e.target.checked) setReportFilters(prev => ({ ...prev, statuses: [...prev.statuses, s] }));
                                            else setReportFilters(prev => ({ ...prev, statuses: prev.statuses.filter(x => x !== s) }));
                                        }} style={{ marginRight: '2px' }} />
                                    {s.replace('_', ' ')}
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Date range + other filters */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '14px' }}>
                        <div>
                            <label style={{ fontSize: '11px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '4px' }}>From Date</label>
                            <input type="date" value={reportFilters.dateFrom} onChange={(e) => setReportFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                                style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' }} />
                        </div>
                        <div>
                            <label style={{ fontSize: '11px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '4px' }}>To Date</label>
                            <input type="date" value={reportFilters.dateTo} onChange={(e) => setReportFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                                style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' }} />
                        </div>
                        <div>
                            <label style={{ fontSize: '11px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '4px' }}>Team</label>
                            <input type="text" value={reportFilters.team} onChange={(e) => setReportFilters(prev => ({ ...prev, team: e.target.value }))} placeholder="All teams"
                                style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' }} />
                        </div>
                        <div>
                            <label style={{ fontSize: '11px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '4px' }}>Requester</label>
                            <input type="text" value={reportFilters.requester} onChange={(e) => setReportFilters(prev => ({ ...prev, requester: e.target.value }))} placeholder="All"
                                style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' }} />
                        </div>
                        <div>
                            <label style={{ fontSize: '11px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '4px' }}>Risk Type</label>
                            <select value={reportFilters.riskType} onChange={(e) => setReportFilters(prev => ({ ...prev, riskType: e.target.value }))}
                                style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' }}>
                                <option value="">All</option>
                                <option value="LRP">LRP</option>
                                <option value="MRP">MRP</option>
                                <option value="HRP">HRP</option>
                                <option value="CRP">CRP</option>
                            </select>
                        </div>
                        <div>
                            <label style={{ fontSize: '11px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '4px' }}>Use Case</label>
                            <select value={reportFilters.useCase} onChange={(e) => setReportFilters(prev => ({ ...prev, useCase: e.target.value }))}
                                style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' }}>
                                <option value="">All</option>
                                <option value="LISTED">Listed (Pre-Approved)</option>
                                <option value="UNLISTED">Unlisted</option>
                            </select>
                        </div>
                    </div>

                    {/* Deep-dive toggles */}
                    <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', marginBottom: '14px', alignItems: 'center' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', color: '#555' }}>
                            <input type="checkbox" checked={reportFilters.breachesOnly}
                                onChange={(e) => setReportFilters(prev => ({ ...prev, breachesOnly: e.target.checked }))} />
                            SLA breaches only
                        </label>
                        <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                            All TAT values exclude weekends (business hours, Mon-Fri). CSV includes per-stage hours, TAT splits, SLA flag and PAM/approval links.
                        </span>
                    </div>

                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <button onClick={loadReportData} disabled={isLoadingReport || reportFilters.statuses.length === 0}
                            style={{ padding: '10px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', border: 'none', background: '#0073bb', color: '#fff' }}>
                            {isLoadingReport ? '⏳ Loading...' : '🔍 Generate Report'}
                        </button>
                        {reportData.length > 0 && (
                            <button onClick={downloadCSV}
                                style={{ padding: '10px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', border: '1px solid #22c55e', background: '#dcfce7', color: '#166534' }}>
                                📥 Download Deep-Dive CSV ({reportData.length} rows)
                            </button>
                        )}
                        {reportData.length > 0 && (
                            <span style={{ fontSize: '12px', color: '#666' }}>{reportData.length} records found</span>
                        )}
                    </div>
                </div>

                {/* Status Bar Chart */}
                {reportData.length > 0 && (
                    <div style={{ padding: '16px 20px', borderRadius: '10px', background: '#fff', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '2px' }}>📊 Report Summary</div>
                        <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '12px' }}>
                            Counts for the current filters{reportFilters.dateFrom || reportFilters.dateTo ? ` (${reportFilters.dateFrom || '…'} → ${reportFilters.dateTo || '…'})` : ' (all dates)'} — access requests only.
                            This can be lower than the tiles at the top of the page: those tiles are all-time totals across every request type (including Bindle and Ruleset requests, which this report excludes), while the report counts access requests matching your filters. A gap between the two is expected.
                        </div>
                        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', height: '80px' }}>
                            {(() => {
                                const counts = { PENDING: 0, ON_HOLD: 0, APPROVED: 0, REJECTED: 0 };
                                reportData.forEach(r => { const s = r.reportStatus || r.status || ''; if (counts[s] !== undefined) counts[s]++; });
                                const max = Math.max(...Object.values(counts), 1);
                                const colors = { PENDING: '#f59e0b', ON_HOLD: '#6b7280', APPROVED: '#22c55e', REJECTED: '#ef4444' };
                                return Object.entries(counts).map(([status, count]) => (
                                    <div key={status} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                                        <div style={{ width: '100%', maxWidth: '60px', height: `${Math.max((count / max) * 60, 4)}px`, borderRadius: '4px 4px 0 0', background: colors[status] || '#ccc' }}></div>
                                        <span style={{ fontSize: '12px', fontWeight: '700', color: colors[status] }}>{count}</span>
                                        <span style={{ fontSize: '10px', color: '#666' }}>{status.replace('_', ' ')}</span>
                                    </div>
                                ));
                            })()}
                        </div>
                    </div>
                )}

                {/* Preview Table */}
                {reportData.length > 0 && (
                    <div style={{ borderRadius: '10px', background: '#fff', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Preview (first 25 rows)</span>
                        </div>
                        <div style={{ overflowX: 'auto' }}>
                            <table className="approver-table">
                                <thead>
                                    <tr>
                                        <th>Request ID</th>
                                        <th>Requester</th>
                                        <th>Team</th>
                                        <th>Permissions</th>
                                        <th>Risk Type</th>
                                        <th>Priority</th>
                                        <th>Users</th>
                                        <th>Status</th>
                                        <th>Created</th>
                                        <th>Approval ID</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reportData.slice(0, 25).map(r => (
                                        <tr key={r.requestId}>
                                            <td><span style={{ fontWeight: '600', color: '#0073bb' }}>{r.requestId}</span></td>
                                            <td>{r.requesterAlias || '—'}</td>
                                            <td>{r.team || '—'}</td>
                                            <td style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(r.selectedPermissionNames || []).join(', ') || '—'}</td>
                                            <td>{r.riskTypeGroup || '—'}</td>
                                            <td>{r.priority || '—'}</td>
                                            <td>{r.totalUsers || '—'}</td>
                                            <td>
                                                <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600',
                                                    backgroundColor: (r.reportStatus || r.status) === 'APPROVED' ? '#dcfce7' : (r.reportStatus || r.status) === 'REJECTED' ? '#fef2f2' : (r.reportStatus || r.status) === 'ON_HOLD' ? '#f3f4f6' : '#fffbeb',
                                                    color: (r.reportStatus || r.status) === 'APPROVED' ? '#166534' : (r.reportStatus || r.status) === 'REJECTED' ? '#dc2626' : (r.reportStatus || r.status) === 'ON_HOLD' ? '#6b7280' : '#92400e'
                                                }}>{(r.reportStatus || r.status || '').replace('_', ' ')}</span>
                                            </td>
                                            <td style={{ fontSize: '12px' }}>{r.createdAt ? r.createdAt.split('T')[0] : '—'}</td>
                                            <td style={{ fontSize: '12px' }}>{r.approvalId || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // ════════════════════════════════════════════
    // RENDER — REQUESTS TAB
    // ════════════════════════════════════════════
    const renderRequests = () => {
        // Defensive: if the Provisioning tab is disabled but stale state still points at it,
        // treat it as 'pending' so the view never renders blank.
        const effectiveSubTab = (!SHOW_PROVISIONING_TAB && requestsSubTab === 'provisioning')
            ? 'pending' : requestsSubTab;
        const requests = effectiveSubTab === 'pending' ? pendingRequests : effectiveSubTab === 'onhold' ? onHoldRequests : closedRequests;

        return (
            <div className="approver-requests">
                <div className="approver-subtabs">
                    <button
                        className={`approver-subtab ${effectiveSubTab === 'pending' ? 'approver-subtab-active' : ''}`}
                        onClick={() => setRequestsSubTab('pending')}
                    >
                        ⏳ Pending ({pendingRequests.length})
                    </button>
                    <button
                        className={`approver-subtab ${effectiveSubTab === 'onhold' ? 'approver-subtab-active' : ''}`}
                        onClick={() => setRequestsSubTab('onhold')}
                    >
                        ⏸ On Hold ({accessOnHoldCount})
                    </button>
                    <button
                        className={`approver-subtab ${effectiveSubTab === 'closed' ? 'approver-subtab-active' : ''}`}
                        onClick={() => setRequestsSubTab('closed')}
                    >
                        📁 Closed ({accessClosedCount})
                    </button>
                    {SHOW_PROVISIONING_TAB && (
                        <button
                            className={`approver-subtab ${requestsSubTab === 'provisioning' ? 'approver-subtab-active' : ''}`}
                            onClick={() => setRequestsSubTab('provisioning')}
                        >
                            🚀 Provisioning
                        </button>
                    )}
                </div>

                {SHOW_PROVISIONING_TAB && effectiveSubTab === 'provisioning' ? renderProvisioning() : null}
                {effectiveSubTab !== 'provisioning' && requests.length > 0 ? (
                    <>
                        {/* Search + Sort bar */}
                        <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search by ID, team, requester, use case..."
                                style={{ flex: 1, minWidth: '200px', padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' }} />
                            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' }}>
                                <option value="date_desc">Newest First</option>
                                <option value="date_asc">Oldest First</option>
                                <option value="priority">By Priority</option>
                                <option value="users">By User Count</option>
                            </select>
                        </div>

                        {/* Queue filters — help approvers narrow a large backlog to what needs their action */}
                        <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}
                                title="Filter by whose action a request is waiting on"
                                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' }}>
                                <option value="all">All Stages</option>
                                <option value="mine">Awaiting Us (Security/Permissions)</option>
                                <option value="leadership">Awaiting Leadership</option>
                            </select>
                            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
                                title="Filter by request type"
                                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' }}>
                                <option value="all">All Types</option>
                                <option value="approved">Approved Use Case</option>
                                <option value="unlisted">Unlisted Use Case</option>
                            </select>
                            <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}
                                title="Filter by priority"
                                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' }}>
                                <option value="all">All Priorities</option>
                                <option value="High">High</option>
                                <option value="Medium">Medium</option>
                                <option value="Low">Low</option>
                            </select>
                            <label style={{ fontSize: '12px', color: '#555', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                From
                                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                                    style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' }} />
                            </label>
                            <label style={{ fontSize: '12px', color: '#555', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                To
                                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                                    style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' }} />
                            </label>
                            {(stageFilter !== 'all' || typeFilter !== 'all' || priorityFilter !== 'all' || dateFrom || dateTo || searchQuery) && (
                                <button
                                    onClick={() => { setStageFilter('all'); setTypeFilter('all'); setPriorityFilter('all'); setDateFrom(''); setDateTo(''); setSearchQuery(''); }}
                                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px', background: '#f8f9fa', color: '#444', cursor: 'pointer' }}>
                                    ✕ Clear Filters
                                </button>
                            )}
                            <span style={{ fontSize: '12px', color: '#666', marginLeft: 'auto' }}>
                                Showing <strong>{filterAndSortRequests(requests).length}</strong> of {requests.length}
                            </span>
                        </div>

                        {/* Bulk action bar (pending only) */}
                        {requestsSubTab === 'pending' && selectedRequests.size > 0 && (
                            <div style={{
                                position: 'sticky', bottom: 0, zIndex: 100,
                                padding: '12px 16px', marginBottom: '12px',
                                background: 'linear-gradient(135deg, #1a365d, #2a4a7f)',
                                borderRadius: '10px', display: 'flex', alignItems: 'center',
                                gap: '12px', flexWrap: 'wrap', boxShadow: '0 -2px 10px rgba(0,0,0,0.15)'
                            }}>
                                <span style={{ color: '#fff', fontWeight: '600', fontSize: '13px' }}>
                                    {selectedRequests.size} selected
                                </span>
                                <button onClick={() => setBulkActionModal({ show: true, action: 'approve' })}
                                    style={{ padding: '6px 14px', borderRadius: '5px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', border: 'none', backgroundColor: '#22c55e', color: '#fff' }}>
                                    ✅ Bulk Approve
                                </button>
                                <button onClick={() => setBulkActionModal({ show: true, action: 'reject' })}
                                    style={{ padding: '6px 14px', borderRadius: '5px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', border: 'none', backgroundColor: '#ef4444', color: '#fff' }}>
                                    ❌ Bulk Reject
                                </button>
                                <button onClick={() => setSelectedRequests(new Set())}
                                    style={{ padding: '6px 14px', borderRadius: '5px', fontSize: '13px', cursor: 'pointer', border: '1px solid #4a6fa5', backgroundColor: 'transparent', color: '#fff' }}>
                                    Clear
                                </button>
                            </div>
                        )}

                        <div className="approver-table-wrapper">
                        <table className="approver-table">
                            <thead>
                                <tr>
                                    {requestsSubTab === 'pending' && (
                                        <th style={{ width: '40px' }}>
                                            <input type="checkbox"
                                                checked={selectedRequests.size === filterAndSortRequests(requests).length && requests.length > 0}
                                                onChange={() => toggleSelectAll(filterAndSortRequests(requests))} />
                                        </th>
                                    )}
                                    <th style={{ width: '120px' }}>Request ID</th>
                                    <th style={{ width: '100px' }}>Type</th>
                                    <th style={{ width: '75px' }}>Requester</th>
                                    <th style={{ width: '120px' }}>Team</th>
                                    <th style={{ width: '130px' }}>Permissions</th>
                                    <th style={{ width: '55px' }}>Priority</th>
                                    <th style={{ width: '45px' }}>Users</th>
                                    <th style={{ width: '100px' }}>Recommendation</th>
                                    <th style={{ width: '50px' }}>Age</th>
                                    {(requestsSubTab === 'pending' || requestsSubTab === 'onhold') && (
                                        <th style={{ width: '190px' }}>Stage</th>
                                    )}
                                    {requestsSubTab === 'pending' || requestsSubTab === 'onhold' ? (
                                        <th>Actions</th>
                                    ) : (
                                        <>
                                            <th>Status</th>
                                            <th>Actioned By</th>
                                        </>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {filterAndSortRequests(requests).map((req) => {
                                    const recBadge = getRecommendationBadge(req.recommendation);
                                    const statusBadge = getStatusBadge(req.status);
                                    const age = getAgeIndicator(req.createdAt);
                                    return (
                                        <React.Fragment key={req.requestId}>
                                            <tr className={expandedRequest === req.requestId ? 'approver-row-expanded' : ''}>
                                                {requestsSubTab === 'pending' && (
                                                    <td>
                                                        <input type="checkbox"
                                                            checked={selectedRequests.has(req.requestId)}
                                                            onChange={() => toggleSelectRequest(req.requestId)} />
                                                    </td>
                                                )}
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    <button className="approver-expand-btn" onClick={() => handleExpandRequest(req.requestId)} title="View details">
                                                        {expandedRequest === req.requestId ? '▼' : '▶'}
                                                    </button>
                                                    <a href={`${VIEWER_URL}?id=${req.requestId}`} className="approver-link"
                                                       onClick={(e) => { e.preventDefault(); handleExpandRequest(req.requestId); }}>
                                                        {req.requestId}
                                                    </a>
                                                    {req.basePiStatus && (() => {
                                                        const st = String(req.basePiStatus).toUpperCase();
                                                        const granted = st === 'GRANTED';
                                                        const err = st === 'ERROR' || st === 'NEEDS_L7';
                                                        return (
                                                            <span title={`Base PI: ${st}`} style={{ display: 'inline-block', marginLeft: '6px', padding: '1px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: '700',
                                                                background: granted ? '#dcfce7' : err ? '#ffeef0' : '#fff3cd',
                                                                color: granted ? '#166534' : err ? '#d13212' : '#856404' }}>
                                                                {granted ? '🔑 Base PI ✓' : err ? '🔑 Base PI !' : '🔑 Base PI ⏳'}
                                                            </span>
                                                        );
                                                    })()}
                                                </td>
                                                <td>
                                                    {(() => {
                                                        const rt = req.requestType || '';
                                                        const isApproved = rt === 'approved' || rt === 'approved_renewal' || rt.includes('permission_') && rt.includes('approved');
                                                        return <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '600', backgroundColor: isApproved ? '#dcfce7' : '#fff3cd', color: isApproved ? '#166534' : '#856404', border: `1px solid ${isApproved ? '#bbf7d0' : '#fde68a'}`, whiteSpace: 'nowrap' }}>{isApproved ? '✅ Approved Use Case' : '📋 Unlisted Use Case'}</span>;
                                                    })()}
                                                </td>
                                                <td style={{ fontWeight: '500' }}>{req.requesterAlias || '—'}</td>
                                                <td>{req.team}</td>
                                                <td className="approver-permissions-cell" style={{ fontSize: '12px' }}>
                                                    {(() => {
                                                        const permNames = req.selectedPermissionNames || req.useCase || '';
                                                        const riskGroup = req.riskTypeGroup || req.riskType || '';
                                                        if (Array.isArray(permNames)) {
                                                            return permNames.map((p, i) => <div key={i}><span style={{ fontWeight: '600', color: '#0073bb', fontSize: '10px' }}>{riskGroup}</span> {p}</div>);
                                                        }
                                                        return riskGroup ? <span><span style={{ fontWeight: '600', color: '#0073bb', fontSize: '10px' }}>{riskGroup}:</span> {permNames}</span> : (permNames || '—');
                                                    })()}
                                                </td>
                                                <td>
                                                    {(() => {
                                                        const p = req.priority || 'Medium';
                                                        const colors = { High: { bg: '#ffeef0', color: '#d13212', border: '#ffc1c8' }, Medium: { bg: '#fff8e1', color: '#92400e', border: '#fde68a' }, Low: { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' } };
                                                        const c = colors[p] || colors.Medium;
                                                        return <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', backgroundColor: c.bg, color: c.color, border: `1px solid ${c.border}` }}>{p}</span>;
                                                    })()}
                                                </td>
                                                <td>{req.totalUsers}</td>
                                                <td><span className={`approver-badge ${recBadge.class}`}>{recBadge.label}</span></td>
                                                <td>{age && (
                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '3px' }}>
                                                        <span title={age.tooltip} style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', backgroundColor: age.bg, color: age.color, border: `1px solid ${age.border}`, cursor: 'default' }}>{age.label}</span>
                                                        <span title={age.tooltip} style={{ fontSize: '10px', color: '#94a3b8', whiteSpace: 'nowrap' }}>{age.exact}</span>
                                                    </div>
                                                )}</td>
                                                {(requestsSubTab === 'pending' || requestsSubTab === 'onhold') && (
                                                    <td>{renderStageProgress(req)}</td>
                                                )}
                                                {requestsSubTab === 'pending' ? (
                                                    <td>
                                                        <div className="approver-action-btns">
                                                            {(() => {
                                                                const progress = getStageProgress(req);
                                                                
                                                                // If it's not your turn (Leadership stage), only show Hold
                                                                if (!progress.isYourTurn) {
                                                                    return (
                                                                        <button className="approver-btn" style={{ background: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb' }} onClick={() => handleHoldRequest(req.requestId)}>⏸ Hold</button>
                                                                    );
                                                                }
                                                                // Your turn — show Approve/Reject/Hold
                                                                return (
                                                                    <>
                                                                        <button className="approver-btn approver-btn-approve" onClick={() => openActionModal(req.requestId, 'approve')}>✅ Approve</button>
                                                                        <button className="approver-btn approver-btn-reject" onClick={() => openActionModal(req.requestId, 'reject')}>❌ Reject</button>
                                                                        <button className="approver-btn" style={{ background: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb' }} onClick={() => handleHoldRequest(req.requestId)}>⏸ Hold</button>
                                                                    </>
                                                                );
                                                            })()}
                                                        </div>
                                                    </td>
                                                ) : requestsSubTab === 'onhold' ? (
                                                    <td>
                                                        <div className="approver-action-btns">
                                                            <button className="approver-btn" style={{ background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0', fontWeight: '600' }} onClick={() => handleResumeRequest(req.requestId)}>▶ Resume</button>
                                                        </div>
                                                    </td>
                                                ) : (
                                                    <>
                                                        <td><span className={`approver-badge ${statusBadge.class}`}>{statusBadge.label}</span></td>
                                                        <td>{req.approvedBy || '—'}</td>
                                                    </>
                                                )}
                                            </tr>
                                            {renderExpandedPanel(req.requestId)}
                                        </React.Fragment>
                                    );
                                })}
                                {filterAndSortRequests(requests).length === 0 && (
                                    <tr>
                                        <td colSpan={12} style={{ textAlign: 'center', padding: '24px', color: '#888', fontSize: '13px' }}>
                                            No requests match the current filters.{' '}
                                            <button
                                                onClick={() => { setStageFilter('all'); setTypeFilter('all'); setPriorityFilter('all'); setDateFrom(''); setDateTo(''); setSearchQuery(''); }}
                                                style={{ background: 'none', border: 'none', color: '#0073bb', cursor: 'pointer', textDecoration: 'underline', fontSize: '13px', padding: 0 }}>
                                                Clear filters
                                            </button>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    </>
                ) : effectiveSubTab !== 'provisioning' ? (
                    <div className="approver-empty">
                        <span>{effectiveSubTab === 'pending' ? '🎉' : '📭'}</span>
                        <p>{effectiveSubTab === 'pending' ? 'No pending requests!' : 'No closed requests yet'}</p>
                    </div>
                ) : null}
            </div>
        );
    };

    // ════════════════════════════════════════════
    // RENDER — ACTION MODAL
    // ════════════════════════════════════════════
    const renderActionModal = () => {
        if (!actionModal.show) return null;

        const isApprove = actionModal.action === 'approve';

        return (
            <div className="approver-modal-overlay" onClick={closeActionModal}>
                <div className="approver-modal" onClick={(e) => e.stopPropagation()}>
                    <div className={`approver-modal-header ${isApprove ? 'modal-approve' : 'modal-reject'}`}>
                        <h3>{isApprove ? '✅ Approve Request' : '❌ Reject Request'}</h3>
                        <button className="approver-modal-close" onClick={closeActionModal}>✕</button>
                    </div>

                    <div className="approver-modal-body">
                        <p className="approver-modal-request-id">
                            Request: <strong>{actionModal.requestId}</strong>
                        </p>

                        <div className="approver-modal-field">
                            <label>Your Login ID <span className="required">*</span></label>
                            <input
                                type="text"
                                value={actionModal.approver}
                                onChange={(e) => setActionModal(prev => ({ ...prev, approver: e.target.value }))}
                                placeholder="Enter your login (e.g., johndoe)"
                            />
                        </div>

                        <div className="approver-modal-field">
                            <label>Notes (Optional)</label>
                            <textarea
                                value={actionModal.notes}
                                onChange={(e) => setActionModal(prev => ({ ...prev, notes: e.target.value }))}
                                placeholder={isApprove ? 'Approval notes...' : 'Reason for rejection...'}
                                rows={2}
                            />
                        </div>

                        {/* Provisioning Details — shown before approval for review */}
                        {isApprove && (() => {
                            const req = pendingRequests.find(r => r.requestId === actionModal.requestId) || {};
                            const detailData = expandedData && expandedRequest === actionModal.requestId ? expandedData : {};
                            const merged = { ...req, ...detailData };
                            const users = merged.userList || merged.eligibleUsers?.join(', ') || '';
                            const permLinks = merged.permissionLinks || [];
                            const riskType = (merged.riskTypeGroup || '').toUpperCase();
                            const requestType = merged.requestType || '';
                            const isUnlisted = requestType.includes('unlisted');
                            const days = (riskType === 'CRP' || riskType === 'HRP') ? 120 : 180;
                            const startDate = new Date();
                            const endDateObj = new Date();
                            // Prefer the authoritative stored cycle end date (accessEndDate) — this is
                            // what approved/listed requests commit to and what the request-detail view
                            // shows. Only fall back to computed dates when it's absent.
                            const storedEnd = merged.accessEndDate ? new Date(merged.accessEndDate) : null;
                            if (storedEnd && !isNaN(storedEnd.getTime())) {
                                endDateObj.setTime(storedEnd.getTime());
                            } else if (isUnlisted) {
                                endDateObj.setDate(endDateObj.getDate() + days);
                            } else {
                                const year = startDate.getFullYear();
                                let cycleDates = [];
                                if (riskType === 'HRP' || riskType === 'CRP') {
                                    cycleDates = [new Date(year, 3, 30), new Date(year, 7, 31), new Date(year, 11, 31), new Date(year + 1, 3, 30)];
                                } else {
                                    cycleDates = [new Date(year, 5, 30), new Date(year, 11, 31), new Date(year + 1, 5, 30)];
                                }
                                const next = cycleDates.find(d => d > startDate);
                                if (next) { endDateObj.setTime(next.getTime()); }
                            }
                            
                            const fmtDate = (d) => `${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')}/${d.getFullYear()}`;
                            const endDays = Math.round((endDateObj - startDate) / (1000*60*60*24));
                            
                            return (
                                <div style={{ marginTop: '12px', padding: '14px', borderRadius: '8px', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#334155', marginBottom: '10px', borderBottom: '1px solid #e2e8f0', paddingBottom: '6px' }}>
                                        📋 Provisioning Details — Review before approving
                                    </div>
                                    
                                    {/* Users */}
                                    <div style={{ marginBottom: '8px', padding: '8px 12px', borderRadius: '6px', background: '#fff', border: '1px solid #e2e6ea' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                                            <span style={{ fontSize: '11px', fontWeight: '600', color: '#666' }}>Users</span>
                                            <button onClick={() => navigator.clipboard.writeText(users.replace(/,/g, '\n'))}
                                                style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', border: '1px solid #b8d4f0', background: '#f0f8ff', color: '#0073bb', cursor: 'pointer', fontWeight: '600' }}>
                                                📋 Copy
                                            </button>
                                        </div>
                                        <div style={{ fontSize: '13px', color: '#333', wordBreak: 'break-all' }}>{users || '—'}</div>
                                    </div>
                                    
                                    {/* Dates + Reason in 3 columns */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px', marginBottom: '8px' }}>
                                        <div style={{ padding: '8px 10px', borderRadius: '6px', background: '#fff', border: '1px solid #e2e6ea' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                                                <span style={{ fontSize: '10px', fontWeight: '600', color: '#666' }}>Start Date</span>
                                                <button onClick={() => navigator.clipboard.writeText(fmtDate(startDate))}
                                                    style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '3px', border: '1px solid #b8d4f0', background: '#f0f8ff', color: '#0073bb', cursor: 'pointer', fontWeight: '600' }}>Copy</button>
                                            </div>
                                            <div style={{ fontSize: '13px', fontWeight: '700', color: '#333' }}>{fmtDate(startDate)}</div>
                                        </div>
                                        <div style={{ padding: '8px 10px', borderRadius: '6px', background: '#fff', border: '1px solid #e2e6ea' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                                                <span style={{ fontSize: '10px', fontWeight: '600', color: '#666' }}>End Date</span>
                                                <button onClick={() => navigator.clipboard.writeText(fmtDate(endDateObj))}
                                                    style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '3px', border: '1px solid #b8d4f0', background: '#f0f8ff', color: '#0073bb', cursor: 'pointer', fontWeight: '600' }}>Copy</button>
                                            </div>
                                            <div style={{ fontSize: '13px', fontWeight: '700', color: '#333' }}>{fmtDate(endDateObj)}</div>
                                            <div style={{ fontSize: '10px', color: '#64748b' }}>({endDays} days)</div>
                                        </div>
                                        <div style={{ padding: '8px 10px', borderRadius: '6px', background: '#fff', border: '1px solid #e2e6ea' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                                                <span style={{ fontSize: '10px', fontWeight: '600', color: '#666' }}>Reason</span>
                                                <button onClick={() => navigator.clipboard.writeText(actionModal.requestId)}
                                                    style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '3px', border: '1px solid #b8d4f0', background: '#f0f8ff', color: '#0073bb', cursor: 'pointer', fontWeight: '600' }}>Copy</button>
                                            </div>
                                            <div style={{ fontSize: '12px', fontWeight: '700', color: '#333', wordBreak: 'break-all' }}>{actionModal.requestId}</div>
                                        </div>
                                    </div>
                                    
                                    {/* Permission Links */}
                                    {permLinks.length > 0 && (
                                        <div style={{ padding: '8px 12px', borderRadius: '6px', background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                                            <span style={{ fontSize: '11px', fontWeight: '600', color: '#166534', display: 'block', marginBottom: '4px' }}>Permission Group(s) — click to open</span>
                                            {permLinks.map((pl, i) => (
                                                <div key={i} style={{ marginBottom: '2px' }}>
                                                    <a href={pl.link} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: '#0073bb', fontWeight: '600', textDecoration: 'underline' }}>
                                                        {pl.name} ↗
                                                    </a>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })()}
                    </div>

                    <div className="approver-modal-footer">
                        <button className="approver-btn approver-btn-secondary"
                            onClick={closeActionModal} disabled={actionModal.isSubmitting}>
                            Cancel
                        </button>
                        <button
                            className={`approver-btn ${isApprove ? 'approver-btn-approve-lg' : 'approver-btn-reject-lg'}`}
                            onClick={handleAction} disabled={actionModal.isSubmitting}>
                            {actionModal.isSubmitting
                                ? '⏳ Processing...'
                                : isApprove ? '✅ Confirm Approve' : '❌ Confirm Reject'}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // ════════════════════════════════════════════
    // BULK ACTION MODAL
    // ════════════════════════════════════════════
    const renderBulkActionModal = () => {
        if (!bulkActionModal.show) return null;
        const isApprove = bulkActionModal.action === 'approve';
        return (
            <div className="approver-modal-overlay" onClick={() => setBulkActionModal({ show: false, action: '' })}>
                <div className="approver-modal" onClick={(e) => e.stopPropagation()}>
                    <div className={`approver-modal-header ${isApprove ? 'modal-approve' : 'modal-reject'}`}>
                        <h3>{isApprove ? '✅ Bulk Approve' : '❌ Bulk Reject'} ({selectedRequests.size} requests)</h3>
                        <button className="approver-modal-close" onClick={() => setBulkActionModal({ show: false, action: '' })}>✕</button>
                    </div>
                    <div className="approver-modal-body">
                        <p style={{ fontSize: '14px', color: '#333', marginBottom: '16px' }}>
                            You are about to <strong>{isApprove ? 'approve' : 'reject'}</strong> {selectedRequests.size} request(s):
                        </p>
                        <div style={{ maxHeight: '120px', overflowY: 'auto', padding: '8px 12px', background: '#f8f9fa', borderRadius: '6px', marginBottom: '16px', fontSize: '12px' }}>
                            {Array.from(selectedRequests).map(id => <div key={id} style={{ padding: '2px 0' }}>• {id}</div>)}
                        </div>
                        <div className="approver-modal-field">
                            <label>Your Login ID <span className="required">*</span></label>
                            <input type="text" value={bulkApprover}
                                onChange={(e) => setBulkApprover(e.target.value)}
                                placeholder="Enter your login (e.g., johndoe)" />
                        </div>
                        <div className="approver-modal-field">
                            <label>Notes (Optional)</label>
                            <textarea value={bulkActionModal.notes || ''}
                                onChange={(e) => setBulkActionModal(prev => ({ ...prev, notes: e.target.value }))}
                                placeholder={isApprove ? 'Approval notes...' : 'Reason for rejection...'}
                                rows={3} />
                        </div>
                    </div>
                    <div className="approver-modal-footer">
                        <button className="approver-btn approver-btn-secondary"
                            onClick={() => setBulkActionModal({ show: false, action: '' })}>
                            Cancel
                        </button>
                        <button className={`approver-btn ${isApprove ? 'approver-btn-approve-lg' : 'approver-btn-reject-lg'}`}
                            onClick={() => { handleBulkAction(bulkActionModal.action); setBulkActionModal({ show: false, action: '' }); }}>
                            {isApprove ? '✅ Confirm Bulk Approve' : '❌ Confirm Bulk Reject'}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // ════════════════════════════════════════════
    // RENDER — PROVISIONING TAB
    // ════════════════════════════════════════════
    const renderProvisioning = () => {
        // Filter: PENDING + FULL_APPROVAL + LRP/MRP only
        const provisionableRequests = pendingRequests.filter(req => {
            const rec = req.recommendation || '';
            const rt = (req.riskTypeGroup || '').toUpperCase();
            return rec === 'FULL_APPROVAL' && (rt === 'LRP' || rt === 'MRP');
        });

        const toggleProvSelect = (id) => {
            setProvSelected(prev => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
            });
        };

        const toggleProvSelectAll = () => {
            if (provSelected.size === provisionableRequests.length) {
                setProvSelected(new Set());
            } else {
                setProvSelected(new Set(provisionableRequests.map(r => r.requestId)));
            }
        };

        const handleApproveAndProvision = async () => {
            if (provSelected.size === 0) return;
            setIsProvisioning(true);

            const selectedReqs = provisionableRequests.filter(r => provSelected.has(r.requestId));

            // Step 1: Approve each request via the API
            for (const req of selectedReqs) {
                setProvStatus(prev => ({ ...prev, [req.requestId]: 'processing' }));
                try {
                    await updateRequestStatus(req.requestId, 'APPROVED', userAlias, 'Approved & provisioned via Provisioning tab');
                    setProvStatus(prev => ({ ...prev, [req.requestId]: 'approved' }));
                } catch (err) {
                    console.error('Approve failed:', req.requestId, err);
                    setProvStatus(prev => ({ ...prev, [req.requestId]: 'failed' }));
                }
            }

            // Step 2: Trigger extension to add users (if available)
            const approvedReqs = selectedReqs.filter(r => provStatus[r.requestId] !== 'failed');
            if (approvedReqs.length > 0) {
                try {
                    // Check if extension is available
                    const extensionAvailable = document.documentElement.getAttribute('data-pam-extension');
                    if (extensionAvailable) {
                        for (const req of approvedReqs) {
                            const permIds = req.selectedPermissions || req.useCaseValue?.split(',') || [];
                            window.postMessage({
                                type: 'PAM_EXTENSION_REQUEST',
                                messageId: `prov_${req.requestId}_${Date.now()}`,
                                action: 'provisionUsers',
                                payload: {
                                    permissionId: permIds[0] || '',
                                    permissionIds: permIds,
                                    users: (req.userList || '').split(',').map(u => u.trim()).filter(Boolean),
                                    reason: `PAM Portal - Request ${req.requestId} approved by ${userAlias}`
                                }
                            }, '*');
                        }
                    }
                } catch (e) {
                    console.log('Extension communication error:', e);
                }
            }

            setIsProvisioning(false);
            loadPendingRequests();
        };

        return (
            <div>
                <div style={{ marginBottom: '20px' }}>
                    <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#111', margin: '0 0 6px 0' }}>🚀 Approve & Add to Permission Page</h2>
                    <p style={{ fontSize: '13px', color: '#666', margin: 0 }}>
                        LRP/MRP requests with Full Approval — select and approve in bulk, then add users to permissions.amazon.com
                    </p>
                </div>

                {provisionableRequests.length === 0 ? (
                    <div className="approver-empty">
                        <span>✅</span>
                        <p>No LRP/MRP requests with Full Approval pending</p>
                    </div>
                ) : (
                    <>
                        {/* Action bar */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', padding: '12px 16px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e2e6ea' }}>
                            <div style={{ fontSize: '13px', color: '#555' }}>
                                <strong>{provisionableRequests.length}</strong> request{provisionableRequests.length !== 1 ? 's' : ''} ready
                                {provSelected.size > 0 && <span style={{ marginLeft: '12px', color: '#0073bb', fontWeight: '600' }}>({provSelected.size} selected)</span>}
                            </div>
                            <button
                                onClick={handleApproveAndProvision}
                                disabled={provSelected.size === 0 || isProvisioning}
                                style={{
                                    padding: '10px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: '700',
                                    cursor: provSelected.size === 0 || isProvisioning ? 'not-allowed' : 'pointer',
                                    border: 'none',
                                    background: provSelected.size > 0 && !isProvisioning ? '#22c55e' : '#e2e6ea',
                                    color: provSelected.size > 0 && !isProvisioning ? '#fff' : '#888'
                                }}
                            >
                                {isProvisioning ? '⏳ Processing...' : `✅ Approve & Add to Permission Page (${provSelected.size})`}
                            </button>
                        </div>

                        {/* Table */}
                        <div className="approver-section">
                            <div className="approver-table-wrapper">
                                <table className="approver-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '40px' }}>
                                                <input type="checkbox"
                                                    checked={provSelected.size === provisionableRequests.length && provisionableRequests.length > 0}
                                                    onChange={toggleProvSelectAll} />
                                            </th>
                                            <th>Request ID</th>
                                            <th>Requester</th>
                                            <th>Team</th>
                                            <th>Permissions</th>
                                            <th>Risk</th>
                                            <th>Users</th>
                                            <th>Age</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {provisionableRequests.map(req => {
                                            const age = getAgeIndicator(req.createdAt);
                                            const status = provStatus[req.requestId];
                                            return (
                                                <tr key={req.requestId}>
                                                    <td>
                                                        <input type="checkbox"
                                                            checked={provSelected.has(req.requestId)}
                                                            onChange={() => toggleProvSelect(req.requestId)}
                                                            disabled={status === 'processing' || status === 'approved'} />
                                                    </td>
                                                    <td style={{ whiteSpace: 'nowrap', fontWeight: '500' }}>
                                                        <a href={`${VIEWER_URL}?id=${req.requestId}`} target="_blank" rel="noopener noreferrer" className="approver-link">
                                                            {req.requestId}
                                                        </a>
                                                    </td>
                                                    <td>{req.requesterAlias || '—'}</td>
                                                    <td>{req.team}</td>
                                                    <td className="approver-permissions-cell" style={{ fontSize: '12px' }}>
                                                        {Array.isArray(req.selectedPermissionNames)
                                                            ? req.selectedPermissionNames.join(', ')
                                                            : (req.useCase || '—')}
                                                    </td>
                                                    <td>
                                                        <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', backgroundColor: req.riskTypeGroup === 'LRP' ? '#dcfce7' : '#e0f2fe', color: req.riskTypeGroup === 'LRP' ? '#166534' : '#0073bb' }}>
                                                            {req.riskTypeGroup}
                                                        </span>
                                                    </td>
                                                    <td>{req.totalUsers}</td>
                                                    <td>{age && <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', backgroundColor: age.bg, color: age.color, border: `1px solid ${age.border}` }}>{age.label}</span>}</td>
                                                    <td>
                                                        {status === 'processing' && <span style={{ color: '#ff9900', fontWeight: '600', fontSize: '12px' }}>⏳ Processing</span>}
                                                        {status === 'approved' && <span style={{ color: '#22c55e', fontWeight: '600', fontSize: '12px' }}>✅ Approved</span>}
                                                        {status === 'failed' && <span style={{ color: '#d13212', fontWeight: '600', fontSize: '12px' }}>❌ Failed</span>}
                                                        {!status && <span style={{ color: '#888', fontSize: '12px' }}>Ready</span>}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Info note */}
                        <div style={{ marginTop: '16px', padding: '12px 16px', borderRadius: '8px', background: '#f0f8ff', border: '1px solid #b8d4f0', fontSize: '13px', color: '#0073bb' }}>
                            💡 <strong>How it works:</strong> Selecting "Approve & Add to Permission Page" will approve the request in the system and sync to approvals.amazon.com. 
                            With the Chrome extension installed, users will also be automatically added to the permission group on permissions.amazon.com.
                        </div>
                    </>
                )}
            </div>
        );
    };

    // ════════════════════════════════════════════
    // RENDER — BINDLE REQUESTS TAB
    // ════════════════════════════════════════════
    const renderBindleRequests = () => {
        return (
            <div className="approver-requests">
                <div className="approver-subtabs">
                    <button className={`approver-subtab ${bindleSubTab === 'pending' ? 'approver-subtab-active' : ''}`} onClick={() => { setBindleSubTab('pending'); }}>
                        📋 Pending
                    </button>
                    <button className={`approver-subtab ${bindleSubTab === 'closed' ? 'approver-subtab-active' : ''}`} onClick={() => { setBindleSubTab('closed'); }}>
                        🔒 Closed
                    </button>
                </div>

                {isLoadingBindle ? (
                    <div className="approver-loading"><div className="spinner"></div><p>Loading bindle requests...</p></div>
                ) : bindleRequests.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                        <div style={{ fontSize: '32px', marginBottom: '12px' }}>📦</div>
                        <p>No bindle requests found.</p>
                    </div>
                ) : (
                    <div className="approver-table-wrapper">
                        <table className="approver-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '130px' }}>Request ID</th>
                                    <th style={{ width: '80px' }}>Requester</th>
                                    <th style={{ width: '150px' }}>Permission</th>
                                    <th style={{ width: '55px' }}>Age</th>
                                    <th style={{ width: '200px' }}>Stage</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bindleRequests.map(req => {
                                    const isClosed = req.status === 'APPROVED' || req.status === 'REJECTED' || req.status === 'DENIED';
                                    const age = getAgeIndicator(req.createdAt);
                                    // Bindle 3-stage: POC → Leadership → Permissions Team
                                    const stage1Done = !!(req.stage1ApprovedBy);
                                    const stage2Done = !!(req.stage2ApprovedBy);
                                    const currentStage = !stage1Done ? 1 : !stage2Done ? 2 : 3;
                                    const isYourTurn = currentStage === 3;

                                    const bindleStages = [
                                        { name: 'Bindle POC', done: stage1Done, active: currentStage === 1, approver: req.stage1ApprovedBy },
                                        { name: 'Leadership', done: stage2Done, active: currentStage === 2, approver: req.stage2ApprovedBy },
                                        { name: 'Permissions', done: false, active: currentStage === 3, approver: '' }
                                    ];

                                    return (
                                        <React.Fragment key={req.requestId}>
                                        <tr key={req.requestId}>
                                            <td style={{ fontWeight: '600', color: '#0073bb', whiteSpace: 'nowrap' }}>
                                                <button className="approver-expand-btn" onClick={() => handleExpandRequest(req.requestId)} title="View details">
                                                    {expandedRequest === req.requestId ? '▼' : '▶'}
                                                </button>
                                                <a href="#" className="approver-link" onClick={(e) => { e.preventDefault(); handleExpandRequest(req.requestId); }}>{req.requestId}</a>
                                            </td>
                                            <td>{req.requesterAlias || '—'}</td>
                                            <td style={{ fontSize: '12px' }}>{req.selectedPermissionNames?.[0] || req.functionDropdown || 'Bindle Access'}</td>
                                            <td>{age && <span title={age.tooltip} style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', backgroundColor: age.bg, color: age.color, border: `1px solid ${age.border}`, cursor: 'default' }}>{age.label}</span>}</td>
                                            <td>
                                                {isClosed ? (
                                                    <span style={{ padding: '3px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', background: req.status === 'APPROVED' ? '#dcfce7' : '#fef2f2', color: req.status === 'APPROVED' ? '#166534' : '#dc2626' }}>{req.status === 'APPROVED' ? '✅ Approved' : '❌ Rejected'}</span>
                                                ) : (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '10px' }}>
                                                        {bindleStages.map((stage, idx) => (
                                                            <React.Fragment key={idx}>
                                                                {idx > 0 && <span style={{ color: '#cbd5e1', fontSize: '9px' }}>›</span>}
                                                                <span title={stage.done ? `${stage.name}: Approved by ${stage.approver}` : stage.active ? `${stage.name}: ${stage.name === 'Bindle POC' || stage.name === 'Leadership' ? 'Awaiting' : 'Your action'}` : stage.name}
                                                                    style={{ padding: '2px 5px', borderRadius: '3px', fontWeight: '600', whiteSpace: 'nowrap',
                                                                        backgroundColor: stage.done ? '#dcfce7' : stage.active ? (stage.name === 'Permissions' ? '#eef6ff' : '#fef3c7') : '#f1f5f9',
                                                                        color: stage.done ? '#166534' : stage.active ? (stage.name === 'Permissions' ? '#1e40af' : '#92400e') : '#94a3b8',
                                                                        border: `1px solid ${stage.done ? '#bbf7d0' : stage.active ? (stage.name === 'Permissions' ? '#bfdbfe' : '#fde68a') : '#e2e8f0'}` }}>
                                                                    {stage.done ? '✓' : stage.active ? '●' : '○'} {stage.name}
                                                                </span>
                                                            </React.Fragment>
                                                        ))}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                {isClosed ? (
                                                    <span style={{ fontSize: '12px', color: '#666' }}>{req.approvedBy || '—'}</span>
                                                ) : isYourTurn ? (
                                                    <div className="approver-action-btns">
                                                        <button className="approver-btn approver-btn-approve" onClick={() => openActionModal(req.requestId, 'approve')}>✅ Approve</button>
                                                        <button className="approver-btn approver-btn-reject" onClick={() => openActionModal(req.requestId, 'reject')}>❌ Reject</button>
                                                        <button className="approver-btn" style={{ background: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb' }} onClick={() => handleHoldRequest(req.requestId)}>⏸ Hold</button>
                                                    </div>
                                                ) : (
                                                    <button className="approver-btn" style={{ background: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb' }} onClick={() => handleHoldRequest(req.requestId)}>⏸ Hold</button>
                                                )}
                                            </td>
                                        </tr>
                                        {renderExpandedPanel(req.requestId)}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    };

    // ════════════════════════════════════════════
    // RENDER — RULESET REQUESTS TAB
    // ════════════════════════════════════════════
    const renderRulesetRequests = () => {
        return (
            <div className="approver-requests">
                <div className="approver-subtabs">
                    <button className={`approver-subtab ${rulesetSubTab === 'pending' ? 'approver-subtab-active' : ''}`} onClick={() => { setRulesetSubTab('pending'); }}>
                        📋 Pending
                    </button>
                    <button className={`approver-subtab ${rulesetSubTab === 'closed' ? 'approver-subtab-active' : ''}`} onClick={() => { setRulesetSubTab('closed'); }}>
                        🔒 Closed
                    </button>
                </div>

                {isLoadingRuleset ? (
                    <div className="approver-loading"><div className="spinner"></div><p>Loading ruleset requests...</p></div>
                ) : rulesetRequests.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                        <div style={{ fontSize: '32px', marginBottom: '12px' }}>📐</div>
                        <p>No ruleset requests found.</p>
                    </div>
                ) : (
                    <div className="approver-table-wrapper">
                        <table className="approver-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '140px' }}>Request ID</th>
                                    <th style={{ width: '80px' }}>Requester</th>
                                    <th style={{ width: '150px' }}>Permissions</th>
                                    <th style={{ width: '55px' }}>Age</th>
                                    <th style={{ width: '180px' }}>Stage</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rulesetRequests.map(req => {
                                    const isClosed = req.status === 'APPROVED' || req.status === 'REJECTED' || req.status === 'DENIED';
                                    const age = getAgeIndicator(req.createdAt);
                                    // Ruleset 2-stage: Leadership → Permissions Team (same as LRP/MRP)
                                    const leaderDone = !!(req.l8ApprovalStatus === 'APPROVED' || req.l8ApproverAlias);
                                    const currentStage = !leaderDone ? 1 : 2;
                                    const isYourTurn = currentStage === 2;

                                    const rulesetStages = [
                                        { name: 'Leadership', done: leaderDone, active: currentStage === 1, approver: req.l8ApproverAlias },
                                        { name: 'Permissions', done: false, active: currentStage === 2, approver: '' }
                                    ];

                                    return (
                                        <React.Fragment key={req.requestId}>
                                        <tr key={req.requestId}>
                                            <td style={{ fontWeight: '600', color: '#0073bb', whiteSpace: 'nowrap' }}>
                                                <button className="approver-expand-btn" onClick={() => handleExpandRequest(req.requestId)} title="View details">
                                                    {expandedRequest === req.requestId ? '▼' : '▶'}
                                                </button>
                                                <a href="#" className="approver-link" onClick={(e) => { e.preventDefault(); handleExpandRequest(req.requestId); }}>{req.requestId}</a>
                                            </td>
                                            <td>{req.requesterAlias || '—'}</td>
                                            <td style={{ fontSize: '12px' }}>
                                                {Array.isArray(req.selectedPermissionNames) ? req.selectedPermissionNames.join(', ') : req.useCase || req.functionDropdown || 'Ruleset Access'}
                                            </td>
                                            <td>{age && <span title={age.tooltip} style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', backgroundColor: age.bg, color: age.color, border: `1px solid ${age.border}`, cursor: 'default' }}>{age.label}</span>}</td>
                                            <td>
                                                {isClosed ? (
                                                    <span style={{ padding: '3px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', background: req.status === 'APPROVED' ? '#dcfce7' : '#fef2f2', color: req.status === 'APPROVED' ? '#166534' : '#dc2626' }}>{req.status === 'APPROVED' ? '✅ Approved' : '❌ Rejected'}</span>
                                                ) : (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '10px' }}>
                                                        {rulesetStages.map((stage, idx) => (
                                                            <React.Fragment key={idx}>
                                                                {idx > 0 && <span style={{ color: '#cbd5e1', fontSize: '9px' }}>›</span>}
                                                                <span title={stage.done ? `${stage.name}: Approved by ${stage.approver}` : stage.active ? `${stage.name}: ${stage.name === 'Leadership' ? 'Awaiting leader' : 'Your action'}` : stage.name}
                                                                    style={{ padding: '2px 5px', borderRadius: '3px', fontWeight: '600', whiteSpace: 'nowrap',
                                                                        backgroundColor: stage.done ? '#dcfce7' : stage.active ? (stage.name === 'Leadership' ? '#fef3c7' : '#eef6ff') : '#f1f5f9',
                                                                        color: stage.done ? '#166534' : stage.active ? (stage.name === 'Leadership' ? '#92400e' : '#1e40af') : '#94a3b8',
                                                                        border: `1px solid ${stage.done ? '#bbf7d0' : stage.active ? (stage.name === 'Leadership' ? '#fde68a' : '#bfdbfe') : '#e2e8f0'}` }}>
                                                                    {stage.done ? '✓' : stage.active ? '●' : '○'} {stage.name}
                                                                </span>
                                                            </React.Fragment>
                                                        ))}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                {isClosed ? (
                                                    <span style={{ fontSize: '12px', color: '#666' }}>{req.approvedBy || '—'}</span>
                                                ) : isYourTurn ? (
                                                    <div className="approver-action-btns">
                                                        <button className="approver-btn approver-btn-approve" onClick={() => openActionModal(req.requestId, 'approve')}>✅ Approve</button>
                                                        <button className="approver-btn approver-btn-reject" onClick={() => openActionModal(req.requestId, 'reject')}>❌ Reject</button>
                                                        <button className="approver-btn" style={{ background: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb' }} onClick={() => handleHoldRequest(req.requestId)}>⏸ Hold</button>
                                                    </div>
                                                ) : (
                                                    <button className="approver-btn" style={{ background: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb' }} onClick={() => handleHoldRequest(req.requestId)}>⏸ Hold</button>
                                                )}
                                            </td>
                                        </tr>
                                        {renderExpandedPanel(req.requestId)}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    };

    // ════════════════════════════════════════════
    // TENANT SWITCHING (Phase 2B)
    // ════════════════════════════════════════════
    const currentTenant = TENANT_REGISTRY[activeTenant] || TENANT_REGISTRY.T178;
    const currentTabs = currentTenant.tabs;

    const handleTenantChange = (tenantId) => {
        if (tenantId === activeTenant) return;
        setActiveTenant(tenantId);
        // Seamless switch: reset the sub-tab to Pending and CLEAR the current lists so the new
        // tenant's view never flashes the previous tenant's rows before the re-fetch lands.
        // (The activeTenant useEffect dep re-runs the tenant-scoped loaders right after.)
        setRequestsSubTab('pending');
        setPendingRequests([]);
        setOnHoldRequests([]);
        setClosedRequests([]);
        setBindleRequests([]);
        setRulesetRequests([]);
        setExpandedRequest(null);
        setExpandedData(null);
        // Zero the sub-tab/nav counts immediately on switch so a stale cross-tenant value can
        // NEVER be shown if a recount fetch is slow or fails (the recount effect refills them).
        // This is why On Hold could still show the other tenant's number: a failed on-hold
        // recount left the previous count in place. Resetting to 0 here makes it fail-safe.
        setAccessPendingCount(0);
        setAccessOnHoldCount(0);
        setAccessClosedCount(0);
        setBindlePendingCount(0);
        setRulesetPendingCount(0);
        // If the currently-open tab doesn't exist for the new tenant, fall back to
        // its first tab (keeps the view valid when e.g. switching off Bindle to T321).
        const nextTabs = (TENANT_REGISTRY[tenantId] || {}).tabs || [];
        if (!nextTabs.some(t => t.key === activeTab)) {
            setActiveTab(nextTabs[0] ? nextTabs[0].key : 'dashboard');
        }
    };

    // Per-tab pending badge count (only Access/Bindle/Ruleset have badges today).
    // All three are tenant-scoped (Phase 2C fix): they come from a tenant-filtered
    // PENDING list, NOT from the cross-tenant dashboard.pending total — otherwise the
    // Access badge would show all-tenant pending even on an empty tenant queue.
    const tabBadge = (key) => {
        if (key === 'requests') return accessPendingCount;
        if (key === 'bindle') return bindlePendingCount;
        if (key === 'ruleset') return rulesetPendingCount;
        return 0;
    };

    // ════════════════════════════════════════════
    // MAIN RENDER
    // ════════════════════════════════════════════
    return (
        <div className="approver-layout">
            <div className="approver-header">
                <div className="approver-header-content">
                    <div className="approver-header-left">
                        <span className="approver-header-icon">🛡️</span>
                        <div>
                            <h1>Permissions and Access Management Approver Portal</h1>
                            <p>Review and manage access requests</p>
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {/* Tenant switcher (Phase 2B) — scopes the tabs below to a program. Scales to ~7 tenants. */}
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#e5e7eb', fontWeight: 600 }}>
                            Tenant
                            <select
                                value={activeTenant}
                                onChange={(e) => handleTenantChange(e.target.value)}
                                title="Switch which tenant/program's requests and config you're viewing"
                                style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                            >
                                {TENANT_ORDER.filter(id => TENANT_REGISTRY[id]).map(id => (
                                    <option key={id} value={id}>{TENANT_REGISTRY[id].label}</option>
                                ))}
                            </select>
                        </label>
                        {lastSyncTime && (
                            <span style={{ fontSize: '11px', color: '#6b7280' }} title={`Last synced: ${new Date(lastSyncTime).toLocaleString()}`}>
                                Synced: {(() => {
                                    const diff = Math.floor((Date.now() - new Date(lastSyncTime).getTime()) / 60000);
                                    if (diff < 1) return 'just now';
                                    if (diff < 60) return `${diff}m ago`;
                                    return `${Math.floor(diff / 60)}h ago`;
                                })()}
                            </span>
                        )}
                        {syncError && <span style={{ fontSize: '11px', color: '#dc2626' }}>{syncError}</span>}
                        <button className="approver-btn approver-btn-refresh" onClick={handleSync} disabled={isSyncing}
                            style={{ opacity: isSyncing ? 0.7 : 1 }}>
                            {isSyncing ? '🔄 Syncing...' : '🔄 Sync'}
                        </button>
                    </div>
                </div>
            </div>

            <div className="approver-tabs">
                {/* Tabs render from the active tenant's registry entry (Phase 2B) */}
                {currentTabs.map(tab => {
                    const badge = tabBadge(tab.key);
                    return (
                        <button
                            key={tab.key}
                            className={`approver-tab ${activeTab === tab.key ? 'approver-tab-active' : ''}`}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            {tab.label}
                            {badge > 0 && <span className="approver-tab-badge">{badge}</span>}
                        </button>
                    );
                })}
            </div>

            <div className="approver-content">
                {error && (
                    <div className="approver-error">
                        <span>❌</span> {error}
                        <button onClick={loadDashboard} className="approver-btn approver-btn-secondary" style={{ marginLeft: '10px' }}>
                            Retry
                        </button>
                    </div>
                )}

                {isLoading && !dashboard ? (
                    <div className="approver-loading">
                        <div className="spinner"></div>
                        <p>Loading...</p>
                    </div>
                ) : (
                    <>
                        {activeTab === 'dashboard' && renderDashboard()}
                        {activeTab === 'requests' && renderRequests()}
                        {activeTab === 'bindle' && renderBindleRequests()}
                        {activeTab === 'ruleset' && renderRulesetRequests()}
                        {/* Config renders generically from the active tenant's registry entry (Phase 2B).
                            Each tenant declares its own config component (T178 -> ConfigPage, T321 -> T321ConfigPage). */}
                        {activeTab === 'config' && (() => {
                            const configTab = currentTabs.find(t => t.key === 'config');
                            const ConfigComponent = configTab && configTab.component;
                            return ConfigComponent ? <ConfigComponent userAlias={userAlias} /> : null;
                        })()}
                    </>
                )}
            </div>

            {renderActionModal()}
            {renderBulkActionModal()}

            {/* Provisioning Details Modal — shown after approval */}
            {provisioningData && (
                <div className="approver-modal-overlay" onClick={() => setProvisioningData(null)}>
                    <div className="approver-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '550px' }}>
                        <div className="approver-modal-header modal-approve">
                            <h3>✅ Approved — Provisioning Details</h3>
                            <button className="approver-modal-close" onClick={() => setProvisioningData(null)}>✕</button>
                        </div>
                        <div className="approver-modal-body" style={{ padding: '20px' }}>
                            <p style={{ fontSize: '13px', color: '#555', marginBottom: '16px' }}>
                                Request <strong>{provisioningData.requestId}</strong> approved. Copy details below to add users on permissions.amazon.com
                            </p>

                            {/* Users */}
                            <div style={{ marginBottom: '12px', padding: '10px 14px', borderRadius: '8px', background: '#f8f9fa', border: '1px solid #e2e6ea' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                    <span style={{ fontSize: '12px', fontWeight: '600', color: '#666' }}>Users</span>
                                    <button onClick={() => navigator.clipboard.writeText(provisioningData.users.replace(/,/g, '\n'))}
                                        style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '4px', border: '1px solid #b8d4f0', background: '#f0f8ff', color: '#0073bb', cursor: 'pointer', fontWeight: '600' }}>
                                        📋 Copy
                                    </button>
                                </div>
                                <div style={{ fontSize: '13px', color: '#333' }}>{provisioningData.users}</div>
                            </div>

                            {/* Dates */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                                <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#f8f9fa', border: '1px solid #e2e6ea' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                        <span style={{ fontSize: '12px', fontWeight: '600', color: '#666' }}>Start Date</span>
                                        <button onClick={() => { const d = new Date(); navigator.clipboard.writeText(`${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')}/${d.getFullYear()}`); }}
                                            style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '4px', border: '1px solid #b8d4f0', background: '#f0f8ff', color: '#0073bb', cursor: 'pointer', fontWeight: '600' }}>
                                            📋 Copy
                                        </button>
                                    </div>
                                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                                        {(() => { const d = new Date(); return `${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')}/${d.getFullYear()}`; })()}
                                    </div>
                                </div>
                                <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#f8f9fa', border: '1px solid #e2e6ea' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                        <span style={{ fontSize: '12px', fontWeight: '600', color: '#666' }}>End Date</span>
                                        <button onClick={() => { const days = provisioningData.riskTypeGroup === 'CRP' || provisioningData.riskTypeGroup === 'HRP' ? 120 : 180; const end = new Date(); end.setDate(end.getDate() + days); navigator.clipboard.writeText(`${String(end.getMonth()+1).padStart(2,'0')}/${String(end.getDate()).padStart(2,'0')}/${end.getFullYear()}`); }}
                                            style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '4px', border: '1px solid #b8d4f0', background: '#f0f8ff', color: '#0073bb', cursor: 'pointer', fontWeight: '600' }}>
                                            📋 Copy
                                        </button>
                                    </div>
                                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                                        {(() => { const days = provisioningData.riskTypeGroup === 'CRP' || provisioningData.riskTypeGroup === 'HRP' ? 120 : 180; const end = new Date(); end.setDate(end.getDate() + days); return `${String(end.getMonth()+1).padStart(2,'0')}/${String(end.getDate()).padStart(2,'0')}/${end.getFullYear()} (${days} days)`; })()}
                                    </div>
                                </div>
                            </div>

                            {/* Reason */}
                            <div style={{ marginBottom: '12px', padding: '10px 14px', borderRadius: '8px', background: '#f8f9fa', border: '1px solid #e2e6ea' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                    <span style={{ fontSize: '12px', fontWeight: '600', color: '#666' }}>Reason</span>
                                    <button onClick={() => navigator.clipboard.writeText(provisioningData.requestId)}
                                        style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '4px', border: '1px solid #b8d4f0', background: '#f0f8ff', color: '#0073bb', cursor: 'pointer', fontWeight: '600' }}>
                                        📋 Copy
                                    </button>
                                </div>
                                <div style={{ fontSize: '13px', color: '#333' }}>{provisioningData.requestId}</div>
                            </div>

                            {/* Permission Links */}
                            {provisioningData.permissionLinks && provisioningData.permissionLinks.length > 0 && (
                                <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                                    <span style={{ fontSize: '12px', fontWeight: '600', color: '#166534', display: 'block', marginBottom: '6px' }}>Permission Group(s) — click to open</span>
                                    {provisioningData.permissionLinks.map((pl, i) => (
                                        <div key={i} style={{ marginBottom: '4px' }}>
                                            <a href={pl.link} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', color: '#0073bb', fontWeight: '600', textDecoration: 'underline' }}>
                                                {pl.name} ↗
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="approver-modal-footer">
                            <button className="approver-btn approver-btn-secondary" onClick={() => setProvisioningData(null)}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Helper component: 3-stage approval row for HRP/CRP
const ApproverStageRow = ({ stageNum, label, sublabel, status }) => {
    // status: 'completed' | 'active' | 'waiting' | 'rejected'
    const styles = {
        completed: { icon: '✅', bg: '#dcfce7', border: '#bbf7d0', color: '#166534', numBg: '#22c55e' },
        active: { icon: '🔄', bg: '#fff3cd', border: '#fde68a', color: '#92400e', numBg: '#fbbf24' },
        waiting: { icon: '⏸️', bg: '#f3f4f6', border: '#e5e7eb', color: '#6b7280', numBg: '#d1d5db' },
        rejected: { icon: '❌', bg: '#ffeef0', border: '#ffc1c8', color: '#d13212', numBg: '#ef4444' },
    };
    const s = styles[status] || styles.waiting;

    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '10px 14px',
            borderRadius: '8px',
            background: s.bg,
            border: `1px solid ${s.border}`,
        }}>
            <div style={{
                width: '26px', height: '26px', borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: s.numBg,
                color: '#fff', fontSize: '12px', fontWeight: '700', flexShrink: 0,
            }}>
                {stageNum}
            </div>
            <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: s.color }}>{label}</div>
                <div style={{ fontSize: '12px', color: s.color, opacity: 0.8, marginTop: '1px' }}>{sublabel}</div>
            </div>
            <span style={{ fontSize: '16px' }}>{s.icon}</span>
        </div>
    );
};

export default ApproverPage;